// @vowric/shared — types shared across packages (D5). No runtime logic here.

/** Fixed role set per tenant membership (D8). Core hardening 2026-07-29,
 * Item 10: 'client' is a NEW, genuinely restrictive tier, ranked below
 * 'viewer' (packages/core/src/lib/roles.ts's ROLE_RANK) — deliberately NOT
 * accepted by record_type_definitions.min_role_read/min_role_write's own
 * CHECK (migration 0005), so a 'client'-role caller is already denied by
 * every existing role gate in the general records engine; it only ever
 * reaches the dedicated, narrowly-scoped client-portal read surface
 * (packages/core/src/parties/clientPortalReads.ts). See migration
 * 0029_client_role.sql's own header for the full reasoning.
 *
 * R2 2026-08-07 (decision 6ae2703d): 'manager' is a NEW rung BETWEEN 'staff'
 * and 'admin' — the seat for someone who runs the DAY-TO-DAY without
 * administering the company (Rexx's dispatcher is the motivating case).
 * Unlike 'client' it IS accepted by record_type_definitions.min_role_read/
 * min_role_write's CHECK (migration 0037), because gating a lifecycle edge at
 * "management" is exactly what the rung is for. See migration
 * 0037_manager_role.sql's own header. */
export type Role = 'owner' | 'admin' | 'manager' | 'staff' | 'member' | 'viewer' | 'client';

/** Membership lifecycle (spec §3). */
export type MembershipStatus = 'invited' | 'active' | 'suspended';

/** Access-capabilities plan, Item 1 (2026-07-30): the second, orthogonal
 * authorization axis alongside role rank — see migration 0030_capabilities
 * .sql's own header for the full two-postures reasoning.
 *   'open'   (default, every membership today): reaches any record type
 *            its role rank clears, UNLESS that type declares
 *            requiredCapability and this membership's capabilities
 *            doesn't include it. Untagged types unaffected.
 *   'scoped' (delegate invites only): the inverse default — denied on
 *            every type UNLESS it declares requiredCapability AND this
 *            membership's capabilities includes it. Untagged types are
 *            ALSO denied — this is what makes a delegate safe-by-default. */
export type AccessMode = 'open' | 'scoped';

/** Audit actor classes (D22). */
export type ActorType = 'user' | 'agent' | 'system' | 'external';

export interface Tenant {
  id: string;
  name: string;
  vertical: string;
  plan: string;
  stripeCustomerId: string | null;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Membership {
  id: string;
  tenantId: string;
  userId: string;
  role: Role;
  status: MembershipStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * The scope `withEnforcement` (P1.2) hands to every handler — resolved
 * per-request from the caller's membership + `membership_parties` (D23);
 * never cached cross-request (a revoked membership must take effect
 * immediately, per the P1.2 brief). `role`/`userId` are null only for the
 * system-actor path (background jobs via `forEachTenant`, D3 req 6): there
 * is no calling user/membership to resolve for those.
 */
export interface EnforcementScope {
  tenantId: string;
  userId: string | null;
  role: Role | null;
  /** RECORD-bound party bindings: `membership_parties.party_record_id`, rows in
   *  `records`. This is what `authority/rowScope.ts` seeds its record-graph walk
   *  from ("the actor's party records, and nothing else"). A cockpit client has
   *  NONE of these — see `partyRootIds`. */
  partyIds: string[];
  /** ROOT-bound party bindings: `membership_parties.party_id`, rows in `parties`
   *  — the CRM root (migration 0080).
   *
   *  ⚑ SEPARATE FROM `partyIds` ON PURPOSE (gap 49, 2026-09-18). A membership
   *  binds to a record OR a root, never both and never neither (0080's
   *  `num_nonnulls(...) = 1` CHECK) — they are alternatives for the SAME
   *  relationship. But they reference DIFFERENT TABLES, so merging them into one
   *  array would put `records.id` and `parties.id` values in a single untyped
   *  field: precisely the silent-corruption shape 0080 added a second column to
   *  refuse ("a second column, with a real FK, says which kind of thing it is by
   *  construction"). Collapsing them in the scope would undo that by hand.
   *
   *  Each consumer takes the one it means. Anything walking the record graph
   *  wants `partyIds`; anything asking "is this caller that party?" about a
   *  cockpit client — which has a party root and no record at all, because the
   *  personal pack declares no client record types — wants this. */
  partyRootIds: string[];
  /** Access-capabilities plan, Item 1: empty array + accessMode:'open' is
   * the default for every membership today (unrestricted at role rank,
   * same as before this axis existed) — see AccessMode's own doc. */
  capabilities: string[];
  accessMode: AccessMode;
  /** memberships.job_roles (migration 0028) — the pack's own workforce
   * vocabulary ('tech', 'dispatcher', …). Empty for a system-actor call and
   * for every membership that was never given one.
   *
   * C5a put this on the scope because `readScope` narrows reads BY JOB ROLE,
   * so the chokepoint needs it. Note that this is the third thing job roles
   * now decide (nav-role routing, `seating`'s tier derivation, and now read
   * visibility) — they are no longer the purely descriptive labels 0028's
   * header described, though they are still not a capability axis: the gate
   * binds to minted capabilities (migration 0030) and nothing here changes
   * that. */
  jobRoles: string[];
  /** G28 — the stable machine name a system-actor caller gave itself
   * (`EnforcementContext.systemActor`), carried onto the scope by
   * `resolveScope` exactly the way `role`/`jobRoles` are: read wherever the
   * role floor would have decided, by the SAME callers (records.ts's
   * assertRoleAtLeast and friends). Null for every user/agent/external
   * actor and for a system call that named no actor — the null case is
   * BY CONSTRUCTION today's behaviour (role null, refused on every gated
   * type), never a call site remembering to check something new. */
  systemActor: string | null;
}

// ============================================================
// Records layer (spec §5, §11, D11, D12) — the record type definition
// shape and the vertical pack format. Live here (not in @vowric/core)
// specifically so that packages/verticals/<name> packs (D5: verticals are
// packages) can depend on ONLY @vowric/shared, never @vowric/core —
// depending the other way (a vertical pack importing @vowric/core, which
// in turn depends on the vertical pack for its own tests) would be a
// cyclic workspace dependency. @vowric/core's definitions loader imports
// these same types from here.
// ============================================================

/** spec §5's field type list — deliberately duration|file, no 'reference'
 * (relationships are `links` rows by rule 1, never a JSONB field).
 *
 * 'json' (C6b, 2026-08-09): STRUCTURED data — an array or plain object —
 * for the one shape the vocabulary could not carry: a small owned LIST
 * living on a record (the Rexx tech profile's tickets). The established
 * array answer is a child record type (actor_media's pattern), but on a
 * manifest-declaring pack a new record type is an authority-manifest
 * amendment (validateManifestCoverage refuses the boot for an uncovered
 * type), which turns "the profile gains a field" into a version bump —
 * disproportionate for data one screen owns end to end. Constraints that
 * keep this from becoming a JSONB dumping ground: checkFieldType accepts
 * only arrays/plain objects (scalars have their own types), the indexer
 * already skips object values (indexing.ts's defensive typeof check), the
 * Knot's field extraction never offers it to a model
 * (fieldExtraction.ts), and the SHAPE inside is the declaring pack's own
 * write path's contract, not the engine's. */
export type FieldType =
  | 'text'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'picklist'
  | 'boolean'
  | 'duration'
  | 'file'
  | 'json';

export interface FieldDefinition {
  name: string;
  type: FieldType;
  required?: boolean;
  options?: string[]; // meaningful for type:'picklist'
  default?: unknown;
  /** What this field MEANS, in the words the source document uses.
   *
   * ⚑ THE MODEL READS THIS. The capture engine renders a type's schema into
   * the extraction prompt (actorCapture.ts `schemaBlockFor`), and a field name
   * alone is often not enough to place a fact: a real call sheet says "TALENT
   * CALL", never "on_set_at". Walked on production 2026-08-29 — hair/makeup
   * landed in `hair_makeup_at` because the sheet said "HAIR & MAKEUP", while
   * the TALENT CALL, the single fact a parent opens the sheet for, fell into
   * `notes` as prose.
   *
   * A pack DECLARES the meaning; the engine passes it along. Optional, so
   * every existing definition is unchanged. */
  description?: string;
  /** G6 -- FIELD-LEVEL READ SCOPE, the boot refusal (decision, 2026-09-12
   * revision). resolveFieldAccess treats a field with no manifest rule as
   * READABLE -- "field rules narrow, they never widen" -- which is right for
   * an ordinary field and wrong for one the type's own author has marked
   * sensitive (pay, medical, custody, pickup): a pack whose manifest is
   * silent on one would hand it to every actor who can read the type at
   * all, and the leak would arrive as a rule nobody wrote, not as a bug
   * that goes red.
   *
   * DELIBERATELY PER-FIELD, not a separate list on RecordTypeDefinition:
   * `fields` is what record_type_definitions.fields (JSONB) actually
   * persists -- a top-level array would have been visible to the pack's OWN
   * in-memory declaration (boot-time validation) but invisible to
   * loadDefinition's read at request time, since nothing else on that row
   * carries it. A flag ON the field rides the SAME column that already
   * round-trips through the database with no schema change, so the fact
   * "this field is sensitive" is available exactly where it has to be: at
   * the read/write chokepoint, not only at boot. See sensitiveFieldNames
   * below -- the one place that reads this flag. */
  sensitive?: boolean;
  /** V14 -- a DECLARATION, not a new field type (FieldType stays exactly
   * as `checkFieldType` above documents it -- no 'reference' kind; a
   * commercial reference is still a bare `type: 'text'` field, the way
   * ENGAGEMENT_RECORD_TYPE/OUTCOME_RECORD_TYPE/PLAN_RECORD_TYPE already
   * declare `account_id`/`engagement_id`). This hint tells
   * `packages/core/src/records/references.ts::assertReferences` what the
   * text value NAMES: `{ table: 'engagements' }` for B9's dedicated
   * Account table (aliased -- the generic `links` mechanism cannot target
   * it, same reasoning as those three types' own header comments), or
   * `{ record_type: string }` for a bounded record on the generic
   * substrate. Rides the SAME `fields` JSONB column `sensitive` already
   * round-trips through (see that flag's own comment) -- no migration.
   * Seed-time validation (records/references.ts's validateReferenceFields,
   * called from definitions.ts's seedRecordTypeLocked, G31's family of
   * seed-time refusals) accepts this ONLY on `type: 'text'` fields.
   *
   * V15 -- `{ table: 'agreements' }` extends the union for
   * PLAN_RECORD_TYPE.governing_agreement_id: a plan may name the ONE
   * agreement it considers its governing terms. This hint checks only
   * EXISTENCE + tenant match (references.ts's own assertAgreementReference
   * -- the same bar assertAccountReference already holds `{ table:
   * 'engagements' }` to); whether the named agreement is CURRENTLY
   * accepted-and-applicable (the governing gate proper) is
   * approvePlan/overridePlan's own separate check
   * (plan/authorize.ts), against governingAgreements, not this one. */
  references?: { table: 'engagements' } | { table: 'agreements' } | { record_type: string };
  /** S4-A — `type: 'file'` only: the MIME types this field takes, exact
   * (`video/mp4`) or by family (`video/*`). Held against the file's own
   * first bytes (memory/mimeSniff.ts), never the Content-Type a client
   * sends. Enforced on the client media door
   * (`POST /api/client-portal/records/:id/files/:field`); absent = any type.
   * Rides the `fields` JSONB like `sensitive`; seed-checked. */
  accept?: string[];
  /** S4-A — `type: 'file'` only: the lever (an `integer` lever, Core's or
   * the pack's own `<pack>.*`, G-S13-1) holding the largest upload this
   * field takes, in bytes — an owner-facing number, so a lever, never a
   * constant. Enforced on the client media door; absent = the server's
   * upload ceiling (core maxUploadBytes(), 1 GiB). Seed-checked: the lever must exist. */
  maxBytesLever?: string;
  /** CTW v0.7 — A FIELD A MODEL MAY NEVER SET. On a type in a pack's
   * `captureModelTypes`, every field's description is rendered into the
   * extraction prompt, so "NEVER EXTRACT THIS" used to be only a sentence the
   * model was asked to obey. With this flag it is a rule: the capture engine
   * (actorCapture.ts `validateRecord`) drops the field from every
   * model-proposed record before anything is staged — the field only, never
   * the whole record — and says so in the log.
   *
   * ⚠ A MODEL'S PROPOSAL ONLY. A person's own write (the record routes, and a
   * person's edits on an approved capture) is untouched: the flag is about
   * who may not GUESS the value, not who may not hold it.
   *
   * Per-field for the reason `sensitive` is: it rides the `fields` JSONB that
   * already round-trips to the request-time `loadDefinition` read, with no
   * migration, and the capture's catch-up job (registered once per process)
   * sees it without any new parameter threaded through it. */
  neverExtract?: boolean;
  /** CTW v0.7 — FIELDS A WRITE OF THIS FIELD THROUGH THE GENERIC RECORD
   * ROUTES CLEARS IN THE SAME WRITE. `aa_project.poster_ref` names its four
   * provenance fields here: a person's own photo must never sit under a
   * looked-up poster's credit line, and the client sends `poster_ref` alone.
   *
   * ⚠ THE RECORD ROUTES ONLY (POST/PATCH /records, a transition's fields).
   * A pack's own plugin that writes the field and its companions together
   * (the production lookup's apply route) calls `updateRecord` directly and
   * is not affected. Names must be other fields of the same type
   * (seed-checked). */
  writeClears?: string[];
  /** CTW Your Page S1 review (decision 4691c16d) — ONCE THIS FIELD HOLDS A
   * VALUE, NO UPDATE MAY CLEAR IT (null, undefined or an empty/blank string);
   * it may only be changed to another value. The string is the refusal
   * sentence a person reads (a ValidationError → 422 `validation_error` on the
   * record routes, the same convention as "required field cannot be
   * cleared"). Enforced in core's update chokepoint and a transition's own
   * fields (records.ts, `assertKeptFieldsNotCleared`), so every door that
   * writes the field through core is held to it, not one route.
   * `aa_subject.birth_month_year` carries it: clearing a child's month and
   * year would hand their page's age to the holder's own facts
   * (core publicPage/performerAge.ts). */
  keepOnceSet?: string;
}

/** Every field a type's own author has flagged sensitive -- the boot
 * validator's (validateSensitiveFields, packages/core) and the read/write
 * chokepoint's (resolveAuthorityManifest) shared source of truth, so the
 * two can never see a different answer. Reads FieldDefinition.sensitive,
 * never a separate list -- see that flag's own doc comment for why a
 * per-field marker is what has to survive from a pack's in-memory
 * declaration through record_type_definitions.fields (JSONB) to a
 * request-time loadDefinition read. */
export function sensitiveFieldNames(def: Pick<RecordTypeDefinition, 'fields'>): string[] {
  return def.fields.filter((f) => f.sensitive === true).map((f) => f.name);
}

export interface RelationshipDefinition {
  key: string;
  target_type: string;
  cardinality: 'one' | 'many';
  kind: 'lookup' | 'master_detail';
  label: string;
  /** CTW v0.7 — WHICH TARGETS THIS RELATIONSHIP TAKES, beyond their type: a
   * picklist field on the target type and the values it must hold. A link to
   * a target outside them is refused by `linkRecordsMechanics` (every link
   * door: the record routes and the cascade executor alike) with `refusal`,
   * the pack's own sentence, verbatim, and nothing is written.
   * Seed-checked: `field` must be a picklist on `target_type` and every value
   * one of its options. Absent = any record of `target_type`, as before. */
  target_must?: { field: string; oneOf: string[]; refusal: string };
}

// ------------------------------------------------------------------------
// Side effects (P2.2 — spec §5 rule 5's "named-effects registry in Core" +
// the P2.2 brief's declarative boundary: a pack's lifecycle.transitions[]
// may ONLY declare one of these three shapes; there is no way for a pack
// to run arbitrary code from a transition. Unknown shapes / bad field refs
// are a seed-time DefinitionRegressionError-class failure (see
// packages/core/src/records/effects.ts's validateSideEffects, called from
// the definitions loader's seed path — never a runtime surprise).
// ------------------------------------------------------------------------

/** Sets one field ON THE RECORD UNDERGOING THE TRANSITION. `field` must
 * name a field declared on that record's OWN type; `value` is a literal
 * (validated against the field's declared type, both at seed time and
 * again at effect-run time as defense-in-depth) — WITH ONE EXCEPTION: on a
 * `datetime` field, the exact literal string `'now'` is the one dynamic
 * value this effect knows, resolved to the moment the transition actually
 * runs (records/effects.ts's `applySetField`, `SET_FIELD_NOW`), never at
 * seed time. Added S2R (Huan's ruling, decision 6a6b7cb3, 2026-09-12) so a
 * lifecycle state can honestly STAMP the act its own name promises —
 * closing the gap `authority/stateNaming.ts`'s validateStateNamingHonesty
 * named: a bare literal could never carry a real timestamp, only an
 * emit_event could (see that file's own STATE_NAMING_ALLOWLIST, now empty
 * of the case this exception exists for). Every other field type keeps the
 * original literal-only contract, unchanged. */
export interface SetFieldEffect {
  type: 'set_field';
  field: string;
  value: unknown;
}

/** Creates a child/related record from a template of field mappings.
 * `target_type` names another record type (must already be a seeded
 * definition at the time THIS type is seeded — pack authors list the
 * target type earlier in the pack, or seed it in an earlier pack).
 * `field_map` keys are field names on `target_type`; each value is a
 * template string evaluated against the transitioning record's OWN
 * fields — a value that is EXACTLY one `{token}` (nothing else) passes the
 * source field's raw (typed) value through unchanged (so a `currency`/
 * `number`/`boolean` source field maps cleanly onto a same-typed target
 * field); a value containing other text alongside `{token}`s renders as a
 * string, `{id}` referring to the transitioning record's own id. A value
 * with no `{...}` at all is a plain literal. `relationship_key`, if given,
 * must name a relationship declared ON `target_type` whose `target_type`
 * points back at the OWNER type — the newly created record is linked to
 * the transitioning record via that relationship (src = new record,
 * dst = the transitioning record), and — per the P2.1 sensitivity ruling —
 * if that relationship's `kind` is `master_detail`, the new child's
 * sensitivity is set to match the owner's (never lowers anything, since
 * the child is freshly created at `normal` otherwise). */
export interface CreateRecordEffect {
  type: 'create_record';
  target_type: string;
  field_map: Record<string, string>;
  relationship_key?: string;
}

/** Emits an internal event — a seam the Attention layer (P5) will consume
 * from later (kept as inert as `records/indexing.ts`'s `indexRecord` stub
 * is today: wired at the right call site, writes nothing yet).
 * `payload_fields`, if given, must name fields on the OWNER type. */
export interface EmitEventEffect {
  type: 'emit_event';
  event_name: string;
  payload_fields?: string[];
}

/** P6.5c (spec §16 G3 + the two-class rule's "packs CONFIGURE primitives;
 * they never create them"): "Work Order completion fires a G3 consume
 * move" is, structurally, EXACTLY what the existing side_effects mechanism
 * already models ("this transition fires this effect") — so G3's
 * consume-on-transition hook is the fourth SideEffectDeclaration member
 * rather than a separate declarative surface. `sku` is a pack-authored
 * LITERAL (packs know their own SKU vocabulary; stock_items are
 * tenant-scoped data, so unlike create_record's target_type this cannot be
 * resolved/validated at GLOBAL seed time — a bad/nonexistent sku is a
 * genuinely runtime-only failure, StockItemNotFoundError, same class as
 * create_record's "required target field with no default" case).
 * `quantity` reuses CreateRecordEffect.field_map's exact template semantics
 * (a bare number is a literal; a whole-token `{field}` string passes a
 * same-transitioning-record NUMBER/CURRENCY field's value through typed) —
 * "Work Order completion consumes however many units the WO's own quantity
 * field says." `location` defaults to inventory.ts's own DEFAULT_LOCATION
 * when omitted, identical to every other G3 op. */
export interface StockConsumeEffect {
  type: 'stock_consume';
  sku: string;
  quantity: number | string;
  location?: string;
}

/** G30 (g30-documents-and-deliveries.md) — the fifth side effect: render a
 * document from the transitioning record's data and deliver it. Unlike the
 * first four, this one does NOT run inside the transition's own
 * transaction — see records/effects.ts's `applyEffects` doc for why
 * (a delivery is a network call, and "a delivery failure does NOT roll
 * back the transition" is the spec's own rule) — it runs AFTER the
 * transition commits, and a failure raises an attention item instead of
 * throwing back through the transition.
 *
 * `to` names WHICH FIELD supplies the recipient and HOW to read it:
 *   - 'party': `to_field` is a whole-token reference (`{field}`) to a field
 *     on the OWNER record whose value is a party id — the recipient's
 *     email is resolved from that party.
 *   - 'address': `to_field` is a whole-token reference to a field on the
 *     owner record that already holds a plain address/email string.
 * `to_field` reuses `CreateRecordEffect.field_map`'s exact template
 * semantics — a bare literal (no `{}` at all) is also valid, for a
 * channel that pages nobody (`download`, `portal`) and so has nothing
 * dynamic to resolve.
 * (BUILD FINDING, same class as C4's `supersedesId`: the spec's own shape —
 * `{type, template_key, channel, to, inputs_from}` — names `to`'s TWO
 * cases but not which field carries the value; `to_field` is the smallest
 * addition that makes the case resolvable. A SECOND FINDING, discovered
 * wiring Rexx's own `hvac_invoice.send` against this guard: neither
 * `to_field` NOR `inputs_from`'s templates can reach a field on a RELATED
 * record — only the owner's own fields (same limit `CreateRecordEffect.
 * field_map` already has). A record whose recipient lives behind a
 * relationship (Rexx's invoice → work order → client → email, two hops)
 * cannot express it here; filed as a follow-up (a multi-hop resolver,
 * mirroring `AuthorityRelationPath`'s hop-walk), not silently worked
 * around.)
 *
 * `inputs_from` reuses `CreateRecordEffect.field_map`'s exact template
 * semantics: keys are the template's own input names (validated at seed
 * time against the named template's `requiredInputs`), values are
 * template strings evaluated against the transitioning record's own
 * fields. */
// ⚑ Ported 2026-09-26 (decision 2da5b9e8, "Use Rex's so we can merge") —
// this SHAPE is the rexx-tier1 fork's, replacing trunk's original
// Lane B shape ({ template_key, channel: DeliveryChannel, to: 'party'|
// 'address', to_field, inputs_from }). Every caller (records/effects.ts,
// verticals/rexx/src/recordTypes.ts) is ported to this shape in the same
// change; see the port's report for the conflict list.
export interface DeliverDocumentEffect {
  type: 'deliver_document';
  /** The template file's key (core/src/documents/templates/<template>.html). */
  template: string;
  /** The reach role to address it to. */
  to: string;
  /** #83 (0918 rulings) — tried when `to` resolves to nothing, on the SAME
   * record (Core's `resolveReach` own `fallbackRole`). "The invoice defaults
   * to the site, falling back to the client where the site has no contact
   * on file" is this field, declared once, read here and nowhere else.
   * Restored 2026-09-26 (atlas-2 review of decision 2da5b9e8) — dropped by
   * mistake in the first pass of this port; deliverEffect.ts, reach.ts and
   * customerNotices.ts all needed it. */
  fallbackTo?: string;
  /** Only 'email' has an implementation today; a channel with none is a
   * seed-time refusal, never a silent no-send. */
  channel?: 'email';
  /** A `datetime` field on the transitioning record's own type, stamped on
   * success. */
  stampField?: string;
  /**
   * ISSUES #82 / #75 — THE MOVE IS THE DELIVERY, NOT THE BUTTON PRESS. With
   * this set, a delivery that cannot even be ATTEMPTED (no address for the
   * reach role, no channel that can carry it) REFUSES the transition: the
   * record stays where it was, the office is told in its own sentence which
   * record and which address is missing, and an attention item is raised
   * that names the record. A send that is attempted and later bounces still
   * leaves the state moved. Absent = the old behaviour (the edge stands
   * whatever happened), right for a document whose delivery is a courtesy
   * rather than the point of the move.
   */
  requireDelivery?: boolean;
}

/** D2 (d2-prepaid-entitlement.md) — the sixth SideEffectDeclaration member,
 * same precedent StockConsumeEffect's own header sets: "a lifecycle
 * transition fires this effect" already models "attendance -> present
 * draws a class." `unit` and `quantity` are pack-authored literals for the
 * SAME reason `sku` is (prepaid_entitlements is tenant-scoped data, seeded
 * at runtime, not at global seed time); `quantity` reuses the identical
 * template semantics (`resolveFieldMapValue`'s whole-token passthrough) as
 * `StockConsumeEffect.quantity`. `beneficiaryField` names a field ON THE
 * TRANSITIONING RECORD holding the drawing party's id — there is no
 * dedicated field TYPE for a party reference (FieldType has no 'party'
 * member), so this is validated as a plain `text` field, the same
 * scope-cut posture G1ResourceBinding's `resourceType` already takes
 * ("free vocabulary... deliberately stopping short of magic auto-wiring").
 * `reason` is a fixed literal ('record' is the only value built): the
 * draw's reason is always the transitioning record itself
 * (reason_type/reason_id = the owner record's own type/id) — see
 * entitlements/prepaid.ts's applyEntitlementDraw for how the SAME
 * declaration, attached to a transition's reverse direction (e.g.
 * `present -> absent`), reverses the original draw by finding it via that
 * reason rather than drawing again. */
export interface EntitlementDrawEffect {
  type: 'entitlement_draw';
  unit: 'class' | 'hour' | 'visit' | 'session' | 'unit';
  quantity: number | string;
  beneficiaryField: string;
  reason: 'record';
  /** V16 (v16-prepaid-validity-and-idempotency.md, INV-ENT-2): which
   * operation this declaration fires — 'forward' draws (idempotent on
   * `intent`, below); 'reverse' undoes a specific forward draw. Replaces
   * the pre-V16 behavior where entitlements/prepaid.ts's
   * applyEntitlementDrawWithClient GUESSED direction by probing for a live
   * draw under the same reason — a replayed forward effect could reverse
   * instead of no-op. REQUIRED: the seed-time validator
   * (records/effects.ts's validateSideEffects) refuses a declaration
   * without one. "Transition back reverses" is now something the pack
   * author states on the backward edge's own effect, never inferred from
   * which draws happen to already exist. */
  direction: 'forward' | 'reverse';
  /** V16: identifies WHICH draw this is (forward) or undoes (reverse) —
   * `entitlement_draws.intent_key`, resolved through the SAME field_map
   * template semantics as `beneficiaryField`/`to_field` above (a bare
   * literal, or a whole-token `{field}`/`{id}` reference on the owner
   * record). Two DIFFERENT transitions (e.g. a forward edge and its
   * backward edge) that should correlate — a reverse undoing a specific
   * forward draw — must declare the SAME `intent` literal on both;
   * nothing here infers that pairing from the transitions' own shape.
   * Omit for the default `record:{id}` (this record, by id alone) — see
   * records/effects.ts's applyEntitlementDraw for why the default cannot
   * also fold in a transition name (no transition identity reaches that
   * executor; a BUILD FINDING, not a silent gap, since the spec's own text
   * names a `record:<id>:<transition>` default this chunk could not wire
   * without editing records.ts, out of this chunk's scope). */
  intent?: string;
  /** V16: the consumption_rule the FIRING transition represents —
   * 'scheduled' | 'attended' | 'manual' — checked against the target
   * prepaid_entitlements row's own `consumption_rule` (0111) by
   * consumeEntitlement; a mismatch refuses ("this pack is drawn on
   * attendance, not on scheduling"). Omitted defaults to 'manual', which
   * consumeEntitlement treats as an always-allowed override (an operator's
   * explicit draw, not an automated one) — the pre-V16 behavior, which
   * never checked consumption_rule at all (this module's own former
   * header called that out as a BUILD FINDING; V16 closes it for packs
   * that opt in by declaring a real action here). */
  action?: 'scheduled' | 'attended' | 'manual';
}

export type SideEffectDeclaration =
  | SetFieldEffect
  | CreateRecordEffect
  | EmitEventEffect
  | StockConsumeEffect
  | DeliverDocumentEffect
  | EntitlementDrawEffect;

// ------------------------------------------------------------------------
// Primitives (P6.5c — spec §16's pack-interface paragraph: "a pack declares
// 'uses G1 resource for Class records with buffer=15m/notice=24h'... —
// configuration, never table creation. The definitions loader wires record
// types to primitives through this block."). Declared PER RECORD TYPE
// (same level as fields/relationships/lifecycle/converts_to/rollups
// already live) since §16's own example names a record type ("Class
// records") as the thing doing the declaring. Seed-time validated
// (records/primitives.ts's validatePrimitives, the same D12-class
// seed-time-error discipline as validateSideEffects/validateConverts/
// validateRollups) and stored in record_type_definitions.primitives — this
// makes it a QUERYABLE registry (loadDefinition(typeKey).primitives) with
// zero new tables, deliberately STOPPING SHORT of any auto-creation/
// auto-binding magic (a record-type->G1-resource declaration here does NOT
// itself create/bind a `resources` row for every record of that type —
// FLAGGED per the P6.5c brief's own instruction: validation + a queryable
// registry only). G3's own consume-on-transition hook is NOT part of this
// block — see StockConsumeEffect above for why that one rides the existing
// side_effects mechanism instead. */
export interface G1ResourceBinding {
  /** Free vocabulary passed straight to resources.resource_type at
   * whatever future point a real binding mechanism creates/matches a
   * resource — open, same posture as resources.resource_type itself
   * (migration 0016's own header). */
  resourceType?: string;
  bufferMinutes?: number;
  noticeHours?: number;
  capacity?: number;
}

/** §16 G5 (b), literal text: "A RECORD TYPE'S LIFECYCLE TRANSITION/action
 * may declare `requires: [{credential_type, holder_via}]`" — scoped to a
 * SPECIFIC transition (fromState/toState must name a transition already
 * declared in this same type's `lifecycle.transitions`, seed-time checked,
 * records/primitives.ts), not a whole-type-wide rule. `holderVia` resolves
 * WHO the holder is, starting from the record undergoing that transition:
 * `'self'` means the record itself is the holder (the record type IS a
 * Person/holder-shaped type); any other string must name a
 * relationship_key declared ON THIS record type (seed-time checked) whose
 * target is the record that actually holds the credential (e.g. a Work
 * Order's `technician` relationship). Stored + validated in P6.5c (a
 * queryable registry, per that brief's own "stop short of magic
 * auto-wiring" instruction); ops/bookings.ts's createBooking was, at that
 * point, the one concrete, LIVE-wired G5 gate (via resources.
 * required_credential_types), per §12's own "G1 booking create at
 * minimum" framing.
 *
 * G33 (2026-09-12) closes the gap that comment used to flag: records.ts's
 * transitionRecordMechanics now DOES call assertCredentialWithClient for
 * every g5Requires entry matching the transition being performed
 * (fromState/toState equal to the current move), resolving holderVia
 * exactly as described above (a `links` row lookup for anything but
 * 'self'), and refuses with a sentence naming the holder and the
 * credential before any write happens. See transitionRecordMechanics'
 * own comment for the refusal shape and the convert-to-ask gap it flags. */
export interface G5RequiresDeclaration {
  fromState: string;
  toState: string;
  credentialType: string;
  holderVia: string;
}

export interface PrimitivesDeclaration {
  g1Resource?: G1ResourceBinding;
  g5Requires?: G5RequiresDeclaration[];
}

// ------------------------------------------------------------------------
// Converts (P2.3 — spec §5 rule 6: "Converts (record_convert) create the
// target from field_map, write a lineage link (converted_from), never
// copy-delete the source." Rule 6 is the ENTIRE spec text on converts —
// it names the mechanism (field_map) and the lineage link_type
// (converted_from) but says nothing else about the declaration's shape.
// This shape is deliberately modeled on CreateRecordEffect's already-
// accepted field_map (P2.2): same template semantics (resolveFieldMapValue
// in records/effects.ts, reused verbatim by records/convert.ts), same
// seed-time-validated-like-side_effects discipline the P2.3 brief asks
// for. `converts_to` is an ARRAY (not a single declaration) because
// convertRecord's signature takes an explicit targetTypeKey — a source
// type may declare more than one legal conversion target (e.g. a lead
// converting to either 'opportunity' or 'disqualified'), and the caller
// picks which. Each array entry's `target_type` must be unique within the
// array (seed-time checked) so `convertRecord` can look one up
// unambiguously by targetTypeKey.
// ------------------------------------------------------------------------

/** One legal conversion target for the record type that declares it (spec
 * §5 rule 6). `field_map` keys are field names on `target_type`; values are
 * template strings evaluated against the SOURCE record's own fields —
 * identical template semantics to `CreateRecordEffect.field_map` (whole-
 * token passthrough preserves typed values; `{id}` refers to the source
 * record's own id). */
export interface ConvertDeclaration {
  target_type: string;
  field_map: Record<string, string>;
}

// ------------------------------------------------------------------------
// Transition GUARDS (P6 — partial payments).
//
// `required_fields` answers "is the field there". Nothing until now answered
// "is the NUMBER the field carries consistent with the move being made", and
// a money lifecycle cannot be modelled without that: `partially_received` is
// only meaningful while `received_amount` is BELOW `expected_amount`, and a
// running total that goes down is money the family silently loses off the
// screen.
//
// Same posture as `side_effects` / `converts_to` / `rollups` / `primitives`
// before it, for the same reason: A PACK MAY ONLY DECLARE, NEVER SUPPLY
// CODE. One declarative shape, validated at DEFINITION-SEED time
// (records/guards.ts's `validateTransitionGuards`, wired into the same seed
// path) and enforced inside the transition's own transaction
// (`assertTransitionGuards`, called from records.ts's
// transitionRecordMechanics).
//
// ⚠ THE REFUSAL SENTENCE IS THE PACK'S, NOT THE ENGINE'S. A guard that
// refused with "guard field_compare failed" would be technically loud and
// practically silent — the household reading it is a parent chasing a
// session fee, and only the pack knows what the two numbers MEAN. So the
// declaration carries the sentence and `TransitionGuardError` quotes it
// verbatim.
// ------------------------------------------------------------------------

/** What the guarded field's proposed value is measured against.
 *
 *   { field: 'expected_amount' }  another field on the SAME record, taking
 *                                 its post-merge (proposed) value — "less
 *                                 than what was expected".
 *   { prior: true }               THIS field's value BEFORE the transition —
 *                                 "more than the running total already
 *                                 recorded". The only way to express
 *                                 monotonicity, and the reason a guard reads
 *                                 the stored row rather than only the patch.
 */
export type GuardComparand = { field: string; prior?: never } | { prior: true; field?: never };

/** One numeric consistency rule on a lifecycle transition. */
export interface FieldComparisonGuard {
  type: 'field_compare';
  /** A `number`/`currency` field on the type declaring this (seed-checked). */
  field: string;
  op: 'lt' | 'lte' | 'gt' | 'gte';
  against: GuardComparand;
  /** The sentence a person reads when refused. Pack-authored, required —
   * see this section's header. */
  refusal: string;
}

export type TransitionGuardDeclaration = FieldComparisonGuard;

export interface LifecycleTransitionDefinition {
  from: string;
  to: string;
  required_fields?: string[];
  min_role?: Role;
  side_effects?: SideEffectDeclaration[];
  /** P6: numeric consistency rules checked AFTER required_fields and BEFORE
   * any write — see the TransitionGuardDeclaration section above. Absent on
   * every transition that had none, which is every transition that existed
   * before this field did. */
  guards?: TransitionGuardDeclaration[];
}

export interface LifecycleDefinition {
  states: string[];
  transitions: LifecycleTransitionDefinition[];
}

/** K2 (console kernel, plan v3) — a record type whose lifecycle is not ONE
 * shape but SEVERAL, selected per record by the value of one of its own
 * fields. `key_field` must name a declared `picklist` field on the same
 * type (seed-time checked, packages/core/src/records/definitions.ts); every
 * one of that field's `options` must have a `variants` entry, and every
 * entry passes the exact checks a single `lifecycle` passes today (states/
 * transitions well-formed, side_effects/converts/rollups/g5Requires resolve)
 * — WITHIN that variant, seed-time. Mutually exclusive with `lifecycle` on
 * the same RecordTypeDefinition (declaring both is a seed-time error).
 *
 * The engine never reads `variants[...]` directly at a record-level call
 * site — `effectiveLifecycle(definition, record)` (records/lifecycle.ts) is
 * the ONE place that resolves which variant governs a given record, so a
 * variant type behaves exactly like a single-lifecycle type everywhere
 * except at the one place `key_field` is read. */
export interface LifecycleVariantsDefinition {
  key_field: string;
  variants: Record<string, LifecycleDefinition>;
}

// ------------------------------------------------------------------------
// Rollups (P2.4 — spec §5 rule 8: "Rollups recompute in the child's write
// transaction (sum/count/min/max over a master_detail relationship into a
// parent field)." Declared on the PARENT type (the type whose
// `record_type_definitions.rollups` array this lives in) — same
// "declared-from-the-owner's-perspective, relationship_key resolves on the
// CHILD side pointing back" convention CreateRecordEffect (P2.2) already
// established: `source_type` names the master_detail CHILD record type;
// `relationship_key` must be a `kind: 'master_detail'` relationship
// declared ON `source_type` whose `target_type` points back at the type
// declaring this rollup (seed-time checked — records/rollups.ts's
// validateRollups, wired into definitions.ts's seed path exactly like
// validateSideEffects/validateConverts). Stored (not computed-at-read):
// rule 8's "recompute... into a parent field" names the mechanism —
// `field` is written on every child write that changes membership or the
// aggregated value (see records/rollups.ts's module header for the full
// enumerated trigger list, hooked across every records.ts write path).
// ------------------------------------------------------------------------

export interface RollupDeclaration {
  /** Field on THIS (parent) type that receives the aggregate value. Must
   * be a seed-time-validated 'number' or 'currency' field on this type. */
  field: string;
  /** The master_detail CHILD record type being aggregated. */
  source_type: string;
  /** The `kind: 'master_detail'` relationship, declared ON `source_type`,
   * whose `target_type` points back at the type declaring this rollup. */
  relationship_key: string;
  aggregate: 'count' | 'sum' | 'min' | 'max';
  /** The child field to aggregate — REQUIRED for sum/min/max (must be a
   * 'number' or 'currency' field on `source_type`); must be OMITTED for
   * count (nothing to aggregate — count is membership-only). */
  source_field?: string;
}

/** G14 (Core strengthening, 2026-09-12) — one calendar projection a record
 * type declares: a place on its OWN fields that names a date/datetime the
 * record wants to appear on somebody's calendar, and who that somebody is.
 *
 * ⚑ NEVER PERSISTED TO `record_type_definitions` — no column exists for it,
 * and none is added (no migration rides with this declaration). A Vowric
 * process runs exactly one active vertical pack per fork (server/app.ts's
 * own "packs[0]" convention — see its header), so the declaration is read
 * straight off the pack's IN-MEMORY RecordTypeDefinition
 * (calendar/runtime.ts's setCalendarRuntime, called once at boot, mirroring
 * authority/runtime.ts's setAuthorityRuntime) rather than round-tripped
 * through the database the way fields/relationships/lifecycle/primitives
 * are. See calendar/projections.ts (packages/core) for the seed-time
 * validation this feeds and calendar/icsFeed.ts for the renderer.
 *
 * One record type may declare SEVERAL (a shoot day: `pickup_at`,
 * `on_set_at`) — each with its own `key`, half of the VEVENT's UID
 * (`<record id>:<projection key>`) so two projections on one record never
 * collide and a projection survives a re-seed with a stable identity. */
export interface CalendarProjectionDeclaration {
  /** Unique among this type's OWN `calendar` array (seed-time checked).
   * Renaming this is the same "a subscribed calendar sees a NEW event"
   * class of change as renaming any other UID-bearing field. */
  key: string;
  /** Names a `fields[]` entry of type 'date' or 'datetime' (seed-time
   * checked: must exist, must be one of those two types). */
  start: string;
  /** Names a `fields[]` entry of type 'date' or 'datetime' (seed-time
   * checked) if given. An end before its own start is a RUNTIME check —
   * per-record data, not knowable at seed time — and that one record's
   * projection is dropped from the feed rather than rendered inverted. */
  end?: string;
  /** Forces an all-day rendering even for a 'datetime' field (the wall-
   * clock time is discarded — only the date renders). Redundant, and
   * seed-time refused, on a 'date' field, which is already all-day by
   * construction. */
  allDay?: boolean;
  /** A name_template-style string (recordWrite.ts's renderNameTemplate —
   * the SAME renderer `display_name` itself uses), evaluated against the
   * record's OWN, field-scoped (G6) fields. */
  title: string;
  /** Who sees this projection on their feed:
   *   'tenant'   — every member (the existing tenant-wide posture
   *                bookings/obligations/mailed events already have).
   *   'assignee' — only the membership `audienceField` names — a plain
   *                field on this SAME record holding a membership id.
   *                Mirrors commitments.assignee_membership_id's own
   *                "unassigned is a value, not a gap" rule: a null/absent
   *                field renders for EVERY member, never for nobody.
   *   'party'    — only the client-portal party (and its reachable
   *                household) `audienceField` names a path to — a
   *                relationship key OR a field holding a record id,
   *                walked the same direction resolvePartyReachableIds
   *                already walks. Rendered ONLY on the portal feed (`GET
   *                /api/portal/ics/:token`) — a member session has no
   *                "own party", so a 'party' projection never appears on
   *                the member feed (`GET /ics/:token`).
   */
  audience: 'assignee' | 'party' | 'tenant';
  /** Required for 'assignee'/'party' (seed-time refused if absent);
   * seed-time refused if GIVEN alongside 'tenant' (nothing to scope by —
   * every member already sees a tenant-wide projection). Names either a
   * field in this type's OWN `fields[]` (a plain id/membership-id value)
   * or a relationship key in this type's OWN `relationships[]`.
   * 'assignee' accepts a FIELD name only: there is no declared path from
   * a relationship's target record to "is this the caller's own
   * membership", so a relationship name here is seed-time refused for
   * 'assignee' — flagged as a spec gap left open (see G14's own "Built"
   * note at the top of the spec file). */
  audienceField?: string;
  /** Lifecycle states this projection renders for. Default: every
   * NON-TERMINAL state (a state with at least one declared outgoing
   * transition) — a completed/withdrawn/lost thing drops off the
   * calendar the moment it stops being live, with no author action
   * needed. Seed-time checked against every state the type could ever be
   * in (its own `lifecycle`, or the UNION of every `lifecycle_variants`
   * variant's states — see calendar/projections.ts's own comment for why
   * a union, not a per-record resolution, is the right seed-time check).
   * Meaningless, and seed-time refused, on a type with no lifecycle at
   * all. */
  states?: string[];
}

/** One record type's definition, as authored by a vertical pack (spec §11)
 * and (once seeded) as stored in `record_type_definitions` (spec §5). */
export interface RecordTypeDefinition {
  type_key: string;
  vertical: string;
  label_singular: string;
  label_plural: string;
  name_template: string;
  fields: FieldDefinition[];
  relationships: RelationshipDefinition[];
  lifecycle?: LifecycleDefinition | null;
  /** K2 — mutually exclusive with `lifecycle`. See LifecycleVariantsDefinition. */
  lifecycle_variants?: LifecycleVariantsDefinition | null;
  converts_to?: ConvertDeclaration[] | null;
  rollups?: RollupDeclaration[] | null;
  /** P6.5c: §16's pack `primitives:` block (G1 resource bindings + G5
   * requires: declarations — see PrimitivesDeclaration's own doc comment
   * for why G3's consume-on-transition hook lives on side_effects instead). */
  primitives?: PrimitivesDeclaration | null;
  min_role_read?: Role;
  min_role_write?: Role;
  /** Access-capabilities plan, Item 1: nullable/absent by default — a type
   * opts IN to being capability-gated. See AccessMode's own doc for the
   * two-postures check this feeds (assertCapability, records.ts).
   *
   * PITFALL (found designing Item 5, before it shipped): tagging a type
   * that's ALREADY in live use on an ALREADY-DEPLOYED tenant is a live
   * regression, not a safe additive change — every existing 'open'-mode
   * membership has capabilities:'{}' (the migration's own default), so
   * the moment a type they currently rely on gets tagged, `open` mode's
   * own carve-out rule denies them (assertCapability throws) until
   * someone explicitly grants the new capability to their membership.
   * required_capability is safe to add to a BRAND NEW type immediately;
   * retrofitting it onto an existing, already-used type requires a
   * coordinated capabilities backfill (grant the relevant existing
   * memberships the new capability, in the SAME deploy) — a product/ops
   * decision, not something to do unilaterally in a pack definition. */
  required_capability?: string | null;
  /** G6 — FIELD-LEVEL READ SCOPE, the boot refusal (2026-09-12 revision:
   * moved from a top-level list here to a per-field flag —
   * FieldDefinition.sensitive, read by the shared `sensitiveFieldNames`
   * helper above. A top-level array here would be visible to a pack's
   * in-memory declaration (boot-time validation) but invisible to
   * `loadDefinition`'s read at REQUEST time, since nothing else on the
   * `record_type_definitions` row carries it — the flag has to live
   * somewhere that survives the trip through the database, and `fields`
   * (JSONB) is that somewhere). See FieldDefinition.sensitive's own doc
   * comment for the full reasoning; this comment stays as the pointer a
   * reader of RecordTypeDefinition lands on first. */
  /** G14 — see CalendarProjectionDeclaration's own header, right above
   * this interface, for why this NEVER reaches `record_type_definitions`
   * (no column, no migration): read straight off the in-memory pack via
   * calendar/runtime.ts's setCalendarRuntime/getCalendarRuntime. */
  calendar?: CalendarProjectionDeclaration[] | null;
  /** D1 — this type's own attachment(s) to a consent-gated act. Same
   * "never reaches record_type_definitions" posture as `calendar` right
   * above: read off the in-memory pack (consent/runtime.ts's
   * setConsentRuntime/getConsentRuntime), validated at seed time
   * (consent/gates.ts) rather than persisted. See ConsentGateDeclaration's
   * own header for the shape and D1's spec
   * (docs/console/d1-consent-on-a-person.md) for the full design. */
  consentGates?: ConsentGateDeclaration[] | null;
  /** V14 -- cross-field reference consistency (INV-REF-1's "consistent
   * with the row's other references"), same "declared, never persisted"
   * posture as `calendar`/`consentGates` right above (no column on
   * `record_type_definitions`; a Vowric process runs exactly one active
   * pack per fork, so a plain in-memory install at boot is the right
   * shape -- see references/runtime.ts, mirroring calendar/runtime.ts).
   * Each rule reads `must_belong_to`'s value from THIS record's own
   * fields and compares it to the record `field` (already resolved via
   * its own `references` hint) names its OWN `account_id` -- e.g.
   * OUTCOME_RECORD_TYPE declares `{ field: 'engagement_id',
   * must_belong_to: 'account_id' }`: the outcome's engagement must belong
   * to the SAME account the outcome itself names. */
  consistency?: Array<{ field: string; must_belong_to: string }> | null;
  /** V11 (INV-CMD-1/2/4) — the command boundary beneath the routes
   * (docs/console/v11-command-boundary.md). A record type may name a
   * lifecycle state, a transition edge, or a field as owned by ONE named
   * command — the generic doors (create/update/transition, the cascade
   * executor, client-portal submissions) refuse a caller that does not
   * name that exact command via `RecordMechanicsOptions.command`
   * (records.ts's createRecordMechanics/updateRecordMechanics/
   * transitionRecordMechanics — see their own headers for the check).
   * `command` names must be registered (`records/commands.ts`'s
   * registerCommand) — a manifest naming an unregistered one refuses to
   * seed (G31, definitions.ts's validateCommandOwnership).
   *
   * ⚑ NEVER PERSISTED TO `record_type_definitions` — no migration rides
   * with this (V11's spec says so explicitly), same "declared, read from
   * the pack's own in-memory install at boot" posture as `calendar`/
   * `consentGates`/`consistency` right above (records/commands.ts's own
   * runtime slot, modelled directly on references.ts's). */
  command_owned?: {
    /** Entering this lifecycle state (at CREATE or via TRANSITION) is
     * owned by `command` — refused generically unless the caller names
     * it. */
    states?: Array<{ state: string; command: string }>;
    /** This exact (from, to) edge is owned by `command` — refused
     * generically (no fallback dispatch: "a command needs inputs the
     * generic door does not have," the spec's own words) unless the
     * caller names it. */
    transitions?: Array<{ from: string; to: string; command: string }>;
    /** Writing this field (at CREATE with a value, or at UPDATE) is owned
     * by `command` — or, V15 addition, by any ONE of several commands
     * (`string[]`): a field two different dedicated commands may
     * legitimately write at different moments (e.g. `revision_status`,
     * written 'proposed' by `plan.revise` and 'approved' by
     * `plan.accept_revision`) names all of them; a generic-door write
     * still refuses unless the caller's own `opts.command` is ONE of the
     * named owners. */
    fields?: Array<{ field: string; command: string | string[] }>;
    /** While the record sits in one of these states, NO field may be
     * written through the generic update door — an explicit amendment
     * command is the only way in (V15 owns the first one; V11 itself
     * registers none, so this is an unconditional freeze today). */
    frozen_in_states?: string[];
  } | null;
  /** V11 (INV-CMD-3) — a state in this list never has an outgoing
   * transition: `transitionRecordMechanics` refuses UNCONDITIONALLY (no
   * `opts.command` bypasses it — nothing may reopen a terminal record),
   * on every caller including the cascade executor and client-portal
   * submissions, with the same sentence regardless of entry path. Same
   * never-persisted posture as `command_owned` — read from the runtime
   * install, never from `record_type_definitions`. For a
   * `lifecycle_variants` type this one flat list covers every variant
   * (ENGAGEMENT_RECORD_TYPE's own terminal names are globally unique
   * across its five templates — see its header). */
  terminal_states?: string[] | null;
}

/** D1 — a record type's own attachment to a CONSENT-gated act. Declared by
 * the pack; enforced at the chokepoint (records.ts, beside G6's field
 * check) via Core's `packages/core/src/consent/persons.ts` requireConsent.
 * `kind` must be one the type's OWN pack declares in
 * `VerticalPack.consentKinds` — an undeclared kind refuses boot (see
 * consent/gates.ts's seed-time validation: the same "declare the
 * vocabulary or refuse" discipline G6's sensitive fields and CR-age's
 * memberFields.age already apply, one primitive over). */
export interface ConsentGateDeclaration {
  kind: string;
  on: 'create' | 'update' | 'transition' | 'read';
  /** Required, and only meaningful, for `on: 'transition'` — the
   * destination state(s) this gate covers (e.g. a `media` record's
   * transition into `published`). */
  transitions?: string[];
  /** Required, and only meaningful, for `on: 'read'` — field names this
   * gate governs. Missing consent hides exactly these fields (G6's own
   * "hidden = absent" — the read chokepoint runs this gate beside
   * applyFieldAccess, never instead of it), never the whole record. May
   * also narrow an `on: 'update'` gate to fire only when a patch touches
   * one of these fields, rather than on every edit. */
  fields?: string[];
  /** The field on THIS record naming the person the act is about — a
   * party id (e.g. `student`, `subject_party_id`, or a `pickup_log`'s
   * `picked_up_by`'s OWNER — see scopeFrom for the picked-up-by value
   * itself). */
  subjectField: string;
  /** A field on this record whose value feeds the consent's `scope` check
   * — e.g. a `pickup_log` create gate names `scopeFrom: 'picked_up_by'` so
   * `requireConsent` can refuse a pickup by an adult the grant did not
   * name. Omitted = no scope check beyond the bare kind/subject match. */
  scopeFrom?: string;
}

/** D1 — one consent vocabulary entry a pack declares on
 * `VerticalPack.consentKinds`. Validated at seed: a `consentGates` entry
 * (on any of the pack's record types) naming a kind absent here refuses
 * boot — same discipline as `memberFields.age`'s own seed-time guard one
 * primitive over (authority/resolve.ts's validateMemberFieldRules). */
export interface ConsentKindDeclaration {
  label: string;
  /** Free-form documentation of what a grant's `scope` should carry for
   * this kind (e.g. pickup's named adults) — advisory only. Core does not
   * validate a grant's `scope` object against this at runtime; D1's spec
   * calls it "free per kind, validated by the pack's declaration", which
   * this chunk treats as pack-authoring documentation, not a schema this
   * chunk enforces. */
  scopeSchema?: Record<string, unknown>;
  /** Who may grant this kind: the subject themselves (refused unless of
   * majority age — see consent/persons.ts's grantConsent), a guardian, or
   * either. */
  grantors: 'subject' | 'guardian' | 'either';
  /** Months after `granted_at` this kind's grants expire, or absent/null
   * for a grant that never expires on its own (still revocable). Not
   * enforced by a stored `expires_on` default here — the grant call reads
   * this at GRANT time and computes `expires_on` from it, same "derive
   * once, at the moment it is knowable" posture CASL's own
   * computeConsentStatus follows for its 2-year/6-month windows. */
  expiresAfterMonths?: number;
}

// ------------------------------------------------------------------------
// Knot intent (Core hardening 2026-07-29, Item 7): the "never assume"
// voice-create classification/resolution pattern, generalized out of the
// Rexx fork's HVAC-hardcoded knotIntentRoute.ts. Declarative config only —
// the engine (packages/core/src/knot/*) stays "dumb": it executes whatever
// a pack declares here, it never infers hierarchy or invents a general
// N-level chain-walk. Optional and additive on VerticalPack; a pack that
// declares none of this (e.g. Actor's) gets no knot-intent routes at all
// (packages/server/src/plugins/knotIntent.ts registers only when a config
// is passed) — zero behavior change for any pack that doesn't opt in.

/** One screen/record-type the Knot can be pressed from. */
export interface KnotIntentContextDef {
  /** The record_type_definitions key this context represents, e.g.
   * 'hvac_work_order'. */
  typeKey: string;
  label: string;
  /** Domain-language hint spliced into the classifier prompt when the Knot
   * is pressed from this screen — vertical-authored copy describing what
   * phrases like "add a location" mean here. */
  hint: string;
}

/** One voice-creatable record type. */
export interface KnotIntentTargetDef {
  /** Short classifier key, e.g. 'work_order' — NOT necessarily the same as
   * typeKey; this is what the model is asked to name, and what the create
   * endpoint's request body references. */
  key: string;
  typeKey: string;
  label: string;
  /** A KnotIntentContextDef.typeKey this target requires as a parent, or
   * null for a top-level create (no parent needed, always 'ready'). */
  parentType: string | null;
  /** The relationship key on the child record pointing at its parent
   * (linkRecords' relationshipKey) — required whenever parentType is set. */
  parentLinkKey: string | null;
  /** Field name whose extracted/fallback value becomes the confirm-dialog
   * and create-response item label. Falls back to the target's own label
   * when absent or when that field has no value. */
  nameField?: string;
  /** Field -> fallback-value function, applied when the field is required
   * and the model didn't extract one — a voice capture must never fail
   * creation over a missed required field. Mirrors the Rexx fork's
   * applyRequiredFallbacks per-target defaults, lifted into config. */
  requiredFieldFallbacks?: Record<string, (text: string) => unknown>;
}

/** The one-hop "context is an ancestor, not the direct parent — offer its
 * children as disambiguation candidates" escape hatch (the Rexx fork's own
 * example: on a client screen, "add a work order" offers that client's own
 * sites). Declared per case, not inferred — see this section's own header
 * on why the engine doesn't walk an arbitrary chain. */
export interface KnotIntentAncestorDisambiguation {
  contextTypeKey: string;
  targetKey: string;
  viaChildTypeKey: string;
  viaLinkKey: string;
}

export interface KnotIntentPackConfig {
  /** Natural-language domain description spliced into the classifier's
   * system prompt, e.g. "a commercial HVAC/refrigeration company". */
  domainLabel: string;
  contextTypes: KnotIntentContextDef[];
  targets: Record<string, KnotIntentTargetDef>;
  /** The deepest/leaf context type's typeKey (e.g. 'hvac_work_order') —
   * when the Knot is pressed from here, candidate targets are every target
   * whose parentType equals this; from anywhere else (or no context),
   * candidates are primaryChainKeys. Binary, not a general depth rule —
   * matches the one depth the Rexx fork's object model has ever exercised. */
  leafContextType: string;
  primaryChainKeys: string[];
  /** When set, classification on the leaf context never returns null — an
   * unclear/ambiguous utterance falls back to this target key (the Rexx
   * fork's own "note" catch-all: on a work-order screen, anything said is
   * at minimum a note about that job). Omit to allow 'unclear' even on the
   * leaf context, same as everywhere else — declared per pack, not assumed. */
  leafFallbackTargetKey?: string;
  /** Fields the model must never author for ANY target here (rollup
   * targets, sync keys) — vertical-specific names, generic mechanism. */
  excludedFields?: string[];
  ancestorDisambiguations?: KnotIntentAncestorDisambiguation[];
}

// ------------------------------------------------------------------------
// Domain (nav) roles (Core hardening 2026-07-29, Item 8): promotes a
// pattern found in the Rexx fork to Core — team members carry DOMAIN job
// roles (memberships.job_roles, migration 0028) alongside the engine's
// single authorization axis (memberships.role, Role above, unchanged).
// Job roles drive which tab set a pack-declared UI shows; they are never
// an authz mechanism. Same "declarative config, dumb engine" philosophy as
// KnotIntentPackConfig above — see that section's header for why. Optional
// and additive on VerticalPack; a pack that declares none of this (e.g.
// Actor's) gets no change at all (packages/server/src/plugins/team.ts's
// `navTabs` field is simply absent from its response).

/** One tab a pack-declared nav role can show. `tab` is a fork's own
 * TopTab-equivalent key — opaque to Core, since Core can't see (and
 * shouldn't validate against) a fork-specific route union. */
export interface NavTabDef {
  tab: string;
  label: string;
  /** Space-tight variant for a phone bottom bar; falls back to `label`. */
  short?: string;
  /** P6 — THE TIER THIS DESTINATION BELONGS TO, declared by the pack and
   * narrowed by the engine (server/plugins/team.ts asks `hasFeature` and
   * drops the tab from the answer). The same DECLARED-not-called mechanism
   * `captureModelTypeFeatures` and `recordWriteFeatures` already use, and
   * for the same reason: which tier owns a surface is the PACK's product
   * model, and a copy of it in the web bundle is a second source of truth
   * nothing checks.
   *
   * ⚠ NOT AN AUTHORIZATION BOUNDARY, and it must not be mistaken for one.
   * Dropping a tab stops the product OFFERING a screen the tier does not
   * include; the RECORDS behind it stay readable through Registry and the
   * generic API, because the data is the household's and only the SCREEN is
   * the product. The gates that actually protect anything are
   * `requireFeature` at the write and the screen's own `planHas`.
   *
   * Absent = ungated, which is every tab on every pack that declares
   * nothing — those responses stay byte-identical. */
  feature?: string;
}

export interface NavRoleDef {
  key: string;
  /** For a future team-admin UI; not read by nav rendering itself. */
  label: string;
  /** job_roles values that resolve TO this nav role, checked in array
   * order — first match wins (a membership can carry multiple job_roles;
   * this declared priority order decides which wins when several match). */
  matchJobRoles: string[];
  tabs: NavTabDef[];
  /** S1 (wf02 §01b, blessed 2026-08-06): nav roles whose tab GROUPS a
   * membership resolving to THIS role also shows, after its own, in the
   * listed order. This is how a pack declares "the owner holds all of it,
   * grouped, in one rail, with no mode to be in" — authority is a union,
   * and navigation never hides authority you actually hold. Unknown keys
   * are skipped (never throw), same discipline as navTabsForRole. Read by
   * navGroupsForMembership (@vowric/core) only; resolveNavRole's
   * first-match-wins landing behaviour is untouched. */
  includesNavRoles?: string[];
  /** THE PHONE'S OWN SET (blessed by Huan 2026-08-25, after walking the
   * owner rail on a real phone).
   *
   * `tabs` + `includesNavRoles` produce the union a rail is built for —
   * "the owner holds all of it, grouped, in one rail, with no mode to be
   * in". On a DESKTOP side rail that reads beautifully. On a phone bottom
   * bar the same union was eleven destinations: it ran off both edges and
   * pushed the centred Knot out of centre, which hides authority far more
   * completely than any menu — illegibly, and without saying so.
   *
   * So a role may name the few destinations its bottom bar carries. This
   * is NOT a mode and NOT a smaller set of permissions: everything else
   * stays reachable through Find, which is why declaring this hides
   * nothing. Absent (and on every desktop rail) the union is unchanged. */
  phoneTabs?: NavTabDef[];
}

export interface NavRolesPackConfig {
  /** memberships.role values that always resolve to a fixed nav role
   * regardless of job_roles, checked BEFORE matchJobRoles (e.g. "owner
   * always wins"). */
  engineRoleOverrides?: Record<string, string>;
  navRoles: NavRoleDef[];
  /** Fallback when nothing above matches. */
  defaultNavRoleKey: string;
}

// ------------------------------------------------------------------------
// Client portal (Core hardening 2026-07-29, Item 10): config for the
// dedicated, narrow read surface a 'client'-role identity uses
// (packages/core/src/parties/*, packages/server/src/plugins/
// clientPortal.ts). Declares which link_type(s) count as "this party's own
// data" for the bounded-depth reachability walk
// (resolvePartyReachableIds) — deliberately declarative config the engine
// executes, not a general graph-traversal/inference engine (same
// "dumb engine" philosophy as KnotIntentPackConfig/NavRolesPackConfig
// above — see enforcement.ts's own comments on why the general records
// engine stays party-unaware and this dedicated surface exists instead).
/** Access-capabilities plan, Item 3 (2026-07-30): a record TYPE opts into
 * being client-portal direct-writable (own profile/contact/billing-shaped
 * fields — no lifecycle involvement, see clientPortalWrites.ts's own
 * header). Not necessarily the party's own root type — a pack MAY declare
 * a reachable descendant type (e.g. a billing_profile child) writable too;
 * reachability still gates WHICH record of that type, this only gates
 * WHICH fields on it. */
export interface ClientPortalDirectWriteConfig {
  typeKey: string;
  fields: string[];
}

/** Access-capabilities plan, Item 4 (2026-07-30): a record TYPE opts into
 * being client-portal SUBMITTABLE — a lifecycle-aware create/amend, unlike
 * ClientPortalDirectWriteConfig's plain field merge (see
 * clientPortalSubmissions.ts's own header for the full design). A client
 * submission is always pushed to clientTransitionTo's lifecycle state —
 * the pack decides where that leads (an approval-queue state, or straight
 * through if that process needs no approval); the engine never special-
 * cases which. */
export interface ClientPortalSubmissionConfig {
  typeKey: string;
  createAllowed: boolean;
  updateAllowed: boolean;
  /** The lifecycle_state a client-initiated create/amend always lands in
   * — createRecordMechanics' own lifecycleState override for create,
   * transitionRecordMechanics' own transition lookup (matched against the
   * record's CURRENT state) for amend. */
  clientTransitionTo: string;
  allowedFields: string[];
  allowFileAttachment?: boolean;
  /** Required iff allowFileAttachment — which of typeKey's own declared
   * fields (type: 'file') the stored fileId is written onto. */
  fileField?: string;
  /** Required iff createAllowed — the relationship key (declared on
   * typeKey's own relationships[]) used to link a newly created submission
   * back to its caller-supplied parent, which must be within the caller's
   * own reachable set. */
  parentRelationshipKey?: string;
}

/** S4-A — a `file` field a CLIENT seat may upload onto, on a record it
 * reaches (`POST /api/client-portal/records/:id/files/:field`). Opt-in per
 * (type, field): a field not named here is never client-writable, whatever
 * else the type declares. The field's own `accept` / `maxBytesLever`
 * (FieldDefinition) and any `consentGates` naming it apply. */
export interface ClientPortalFileUploadConfig {
  typeKey: string;
  field: string;
}

export interface ClientPortalPackConfig {
  /** link_type keys counted as "descendant of this party's own record" at
   * EVERY hop of the walk (not per-level — this v1 doesn't distinguish
   * which link types apply at which depth, matching the single depth the
   * only real-world case exercises so far). */
  descendantLinkTypes: string[];
  /** Bounds the walk. Default 4 if omitted. */
  maxDepth?: number;
  directWrites?: ClientPortalDirectWriteConfig[];
  submissions?: ClientPortalSubmissionConfig[];
  /** S4-A — see ClientPortalFileUploadConfig. Absent = no client uploads. */
  fileUploads?: ClientPortalFileUploadConfig[];
}

/* ------------------------------------------------------------------ *
 * Erasure (GAP 67) — docs/specs/core-gap-67-68-erasure.md §3. Core's own
 * defaults (§2) apply to anything a pack leaves undeclared; this is ONLY
 * the per-pack override surface, same "declarative config, dumb engine"
 * posture as ClientPortalPackConfig right above it.
 * ------------------------------------------------------------------ */

/** One record type's erasure treatment, overriding Core's default
 * (erase, for anything reachable through clientPortal.descendantLinkTypes
 * / erasure.extraLinkTypes). */
export type ErasureRecordTypeRule =
  | { action: 'erase' }
  | { action: 'anonymise'; keepFields: string[] }
  | { action: 'hold'; years: number; reason: string };

export interface ErasurePackConfig {
  /** Per record type (by `type_key`): what happens to records of this
   * type reached from the erased party. Absent for a type = Core's
   * default for that position (erase, if reachable at all). */
  recordTypes?: Record<string, ErasureRecordTypeRule>;
  /** Extra link_type keys (beyond clientPortal.descendantLinkTypes) that
   * bring a record into the erasure set at every hop of the walk. */
  extraLinkTypes?: string[];
  /** Link types whose far end is NEVER erased, only unlinked — e.g. an
   * event a family submitted stays with the business; only the link
   * (the family's claim on it) goes. */
  unlinkOnly?: string[];
  /** Suppression hash lifetime in years; null = never expires. Default =
   * erasure.consent_hold_years. FLOOR = that lever — a pack may lengthen
   * this (including to never) but never shorten it below the lever's
   * value (a46b8821: "legal retention duties win"). */
  suppressionYears?: number | null;
  /** Whether a client may start their own erasure through
   * GET/POST /api/client/erasure. Default false — a pack that declares
   * nothing gets no self-serve erasure route at all. */
  selfServe?: boolean;
  /** D1: fields on an erased or anonymised record that hold the id of a
   * `parties` row for a person this client brought in (a child, an adult
   * on a pickup list). Each named party is erased WITH the client unless
   * it has standing of its own (an account, another live guardian, wards
   * of its own) — see erasure/plan.ts's dependent walk. Keyed by
   * type_key; values are field names on that type. Live `party_guardians`
   * rows from the client are followed whether or not a pack declares
   * anything here. */
  dependentPartyFields?: Record<string, string[]>;
}

/* ------------------------------------------------------------------ *
 * Entry (C6a) — the pack-declared voice of the FRONT DOOR: the sign-in
 * screen's copy, per the blessed wf05 wireframe ("one field, then the
 * best door for that person"). Same "dumb engine executes declarative
 * pack config" pattern as every sibling config here: the web renders
 * whatever the deployment's pack declares (served public via GET
 * /auth/entry — sign-in happens before any tenant is known, and a fork
 * runs a single pack, packs[0]); a pack that declares nothing gets the
 * web's own neutral defaults. Only `welcome` is required — the trouble
 * line, the lockout reassurance and the record-honesty sentence render
 * ONLY when a pack actually declares them, so a neutral deployment never
 * shows a promise nobody made. No instructional captions belong here:
 * the recovery path is on screen before anything goes wrong, as copy a
 * human wrote, not as UI teaching UI.
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------
 * THE ACCOUNT FRONT DOOR (spec 2026-10-12, ACCOUNT-FRONT-DOOR-SPEC.md) —
 * one sign-up / sign-in / settings flow for every product. The flow, its
 * rules and its emails live in Core once; a pack supplies only its LOOK
 * (the brand below) and what happens after the address is proven
 * (`onboarding`). Declaring this registers /auth/account/* on the
 * deployment; a pack that declares nothing keeps exactly the doors it had.
 * ------------------------------------------------------------------ */

/** The product's look, as the account emails wear it. Email clients take
 * no CSS variables and fetch no web fonts reliably, so these are the
 * literal values from the product's own light theme, plus font stacks
 * whose fallbacks carry the look when the named face is missing. */
export interface AccountDoorBrand {
  productName: string;
  /** Page behind the card. */
  paper: string;
  /** The card itself. */
  surface: string;
  ink: string;
  muted: string;
  line: string;
  /** The one button's colour, and the brand mark's. */
  accent: string;
  accentInk: string;
  displayFont: string;
  bodyFont: string;
  /** Absolute path on the product's own origin, e.g. '/privacy'. */
  privacyPath: string;
  /** The product's square mark, an absolute path on its own origin (e.g.
   * '/apple-touch-icon.png'). Drawn on an accent-coloured cell, so a mail
   * client that blocks images still shows the brand's colour block. */
  markPath: string;
}

export interface AccountDoorPackConfig {
  brand: AccountDoorBrand;
  /** What a verified, signed-in person with no membership does next.
   * 'clientDoor' = the pack's own GAP 62 client-door onboarding (the
   * `clientDoor` declaration below), started and resumed by the SESSION
   * rather than a mailed token. Omitted = straight into the product. */
  onboarding?: 'clientDoor';
}

export interface EntryPackConfig {
  /** Wordmark over the door (e.g. "Rexx"); the web's neutral default is
   * "Vowric". */
  productName?: string;
  /** The small line under the wordmark (e.g. "by Ether"). */
  byline?: string;
  /** The `<meta name="description">` a search result or link unfurl shows —
   * a plain sentence about THIS product. Stamped over the build's neutral
   * placeholder by staticWeb.ts, the same one-source-builds-every-deployment
   * mechanism as the title: no product's sentence belongs in the built file. */
  description?: string;
  /** The `<meta name="theme-color">` hex — the browser-chrome colour a
   * deployment wears. Stamped by staticWeb.ts over the build's neutral
   * platform dark; the manifest's theme_color moves with it. */
  themeColor?: string;
  /** The greeting — the copy IS the brand ("Morning. Let’s get you in."). */
  welcome: string;
  /** Label on the one field (e.g. "Work email"); default "Email". */
  emailLabel?: string;
  /** The recovery path, on screen BEFORE anything goes wrong, naming a
   * human ("Ask Wendy in the office to re-send your invite, or call
   * 905 555 0100."). Rendered only when declared. */
  troubleLine?: string;
  /** The trust line ("We will not lock you out for getting it wrong.").
   * Rendered only when declared. */
  reassurance?: string;
  /** The honesty-about-the-record sentence — told, not discovered.
   * Rendered only when declared. */
  recordNotice?: string;
  /** Whether this deployment signs anybody in with a PASSWORD.
   *
   * ⚠ THE DEFAULT IS FALSE, AND FALSE IS THE TRUTH ON EVERY BUILD TODAY. C6a
   * replaced the four-method wall with the email-first door: magic link, then
   * a passkey. Nothing in the web mints a password any more, so an account
   * created since then HAS none — and a "Change password" form offered to
   * such a person can only ever answer "Current password is incorrect". A
   * deployment that really does sign people in with a password says so here
   * and gets the form back. */
  passwordSignIn?: boolean;
  /** The pack's voice on the PREFERENCES screen. */
  settings?: SettingsPackConfig;
  /** ⚑ THE FEATURE-SURFACE GUARD (2026-09-02 review, "every feature has a
   * surface to explore in the app"). One entry per FeatureKey this pack
   * sells (`packages/core/src/entitlements/catalog.ts`): the RouteScreen a
   * family actually touches for that feature, and the literal control
   * label they see there. A key with no entry is a feature with no
   * surface — the pattern seven shipped features repeated before this
   * existed. `pending: true` marks a surface whose screen is not built on
   * this deployment yet.
   *
   * Typed as plain strings rather than FeatureKey/RouteScreen: @vowric/
   * shared carries no dependency on @vowric/core or @vowric/web, and this
   * field must not create one. `FeatureSurfaces.test.ts` (web) is what
   * checks the strings against both live registries. */
  featureSurfaces?: Record<string, { screen: string; control: string; pending?: true }>;
}

/* ------------------------------------------------------------------ *
 * Settings copy — the pack-declared voice of the PREFERENCES screen.
 *
 * It rides `EntryPackConfig` because it is the same kind of fact (copy the
 * deployment's own pack declares, served by GET /auth/entry) and because the
 * screen already reads that payload for `recordNotice` — a second endpoint
 * would be a second moment in time for one pack's words.
 *
 * ⚑ WHY IT EXISTS AT ALL: the shell's own strings were a TRADE company's.
 * "The shop group in Telegram still buzzes", "Job threads and the office",
 * "Techs are invited from the Techs screen", a whole column headed "Tickets &
 * licences" — every one of them rendered unconditionally, so a CallToWrap
 * parent read Rexx's vocabulary on their own settings screen. The engine
 * renders what the pack declares and neutral wording otherwise; no pack's
 * nouns are compiled into the web again.
 * ------------------------------------------------------------------ */

/** One notification category's two lines. The category KEYS are core-owned
 * ('chat' | 'work' | 'attention' — what CV6a's dispatchers actually write):
 * a pack RENAMES them, it never invents one. */
export interface SettingsNotifyCopy {
  label: string;
  sub: string;
}

/** One seat this pack's owner may invite somebody into — see
 * `SettingsPackConfig.invite.seats`.
 *
 * ⚠ NOT EVERY JOB ROLE BELONGS HERE. `actor_assist` declares `child` and
 * omits it deliberately: enrolling a performer is four calls (the seat, the
 * birth date, the performer record, the binding between them) and it has its
 * own screen. A row on a preferences panel that sent only the first would
 * build half a household and email a child about it. */
export interface SettingsInviteSeat {
  /** The pack's own job-role key, as `seating` spells it. */
  jobRole: string;
  /** The row's own words — "Another adult", "A helper". */
  label: string;
  /** What that seat sees, when the pack has something to say. Rendered only
   * where declared; never composed by the shell. */
  sub?: string;
}

export interface SettingsPackConfig {
  /** Said for as long as push is ON, about whatever OTHER channel goes on
   * buzzing beside it (decision 0d25040b ruling 3: the double-ping is handled
   * by SAYING it). Rendered only when declared — a deployment with no second
   * channel has nothing to say and says nothing. */
  pushFallbackNote?: string;
  /** What the three notification categories are called here. Any subset; an
   * undeclared category keeps the web's neutral wording. */
  notify?: {
    chat?: SettingsNotifyCopy;
    work?: SettingsNotifyCopy;
    attention?: SettingsNotifyCopy;
  };
  /** The invite door, in the pack's own words. `section` heads it; `mini` is
   * the quiet line beside the heading; `sub` is rendered only when declared,
   * and must never name a screen another pack has.
   *
   * ⚑ `seats` (2026-09-11, G-18) — AND WHY ONE LABEL WAS NOT ENOUGH.
   *
   * The door used to be one row for one seat, and the web derived WHICH seat
   * from the tier: whoever the pack seats above its members. That question is
   * right for a trade deployment and wrong for a household. `actor_assist`
   * declares four job roles and two of them are people an owner invites — the
   * second adult (seated `admin`) and the HELPER, the sibling who reads
   * off-camera, seated `member` because reading is not a capability in this
   * engine and her write surface is deliberately empty. The tier filter
   * dropped her, so a declared job role with its own rail and its own read
   * scope appeared NOWHERE in the product.
   *
   * A seat a person may be invited into is not derivable from its rank, so
   * the pack names them. Each entry carries the pack's own `jobRoles` key —
   * the same vocabulary `manifest.seating` declares and POST /auth/invites
   * derives the tier FROM, never a second list — with the words a person
   * reads on the row.
   *
   * OPTIONAL, and `label` stays optional beside it: a pack that declares
   * neither keeps the neutral wording, and a pack that declares `label`
   * alone keeps the single tier-derived row it always had, byte for byte
   * (Rexx does exactly that). */
  invite?: {
    section: string;
    mini?: string;
    label?: string;
    sub?: string;
    seats?: SettingsInviteSeat[];
    /** ⚑ THE MANIFEST ACTION THIS DOOR SPENDS (2026-09-15, the Day 1 walk).
     *
     * The web used to draw the door for any ADMIN-ranked seat. `actor_assist`
     * seats its second adult at `admin` and reserves `member.invite` to
     * `parent` — so a co-parent was shown "Your people" and the invite door,
     * which the manifest says is not hers. Rank is not authority: the rank
     * decides what the records ENGINE permits, the manifest decides what the
     * PACK permits, and where they disagree the manifest is the truth.
     *
     * Naming the action here lets the server answer "does this member hold
     * it" (GET /api/team/me's `mayInvite`) instead of the client re-deriving
     * an authority rule it holds no copy of. OPTIONAL: a pack that declares
     * none keeps the rank check it always had, byte-for-byte. */
    action?: string;
  };
  /** Whether this deployment keeps a FILE ON A PERSON — the second column of
   * the settings screen ("Your record — the company's file", tickets and
   * licences, time off). It is an employer's shape: a household has no
   * office, verifies no licences and approves nobody's leave, so the column
   * is absent unless a pack declares it. */
  memberRecord?: boolean;
}

/* ------------------------------------------------------------------ *
 * First run (C6b) — the pack-declared FIRST-RUN SCRIPT: wf05 §02's "four
 * screens, and the last one is doing the job". Same "dumb engine executes
 * declarative pack config" pattern as every sibling config here: the step
 * VOCABULARY is core-owned (exactly four ids — a pack orders a subset, it
 * never invents a step), the server registers the first-run routes only
 * for a pack that declares this block (the knotIntent/comms convention),
 * and a pack that declares nothing keeps its existing onboarding
 * byte-unchanged (Actor, Personal). Completion state is SERVER-SIDE per
 * membership (migration 0043) — the 08-04 SW incident is why client-held
 * first-run state is banned.
 * ------------------------------------------------------------------ */

/** The core-owned step vocabulary. Every step except 'identity' is
 * skippable ("Do this later") — the C6a invariant, inherited structurally
 * by the web (no disabled= on skips, single-flight in handlers).
 *
 * CV2 adds 'install_app' — Add-to-Home-Screen (decision 8a36f2de: the Knot
 * is the tech's primary entry, so the app earns the home screen). It is the
 * one step with NO server-side payload at all: no config block, no boot
 * guard, nothing in GET /api/first-run beyond its presence in `steps` —
 * the whole step is a client-side ceremony (auto-passed under standalone
 * display mode, never a hard block), and the server only records passage.
 *
 * CV6b adds 'push_pings' — turning the phone's notifications on — and it is
 * the SECOND step of that shape: no config block, no boot guard, nothing in
 * GET /api/first-run beyond its presence in `steps`. It has to be a step in
 * the SCRIPT rather than a prompt anywhere else because iOS only grants push
 * permission from a user gesture, and the walk is the one place a tech is
 * already tapping through a sequence of them. It sits AFTER 'install_app'
 * for a hard technical reason, not a stylistic one: on iOS web push does not
 * exist at all until the app is on the home screen, so asking first would be
 * asking for something the phone cannot give. */
export type FirstRunStepId =
  | 'identity'
  | 'install_app'
  | 'push_pings'
  | 'chat_lanes'
  | 'tickets'
  | 'first_capture';

export const FIRST_RUN_STEP_IDS: readonly FirstRunStepId[] = [
  'identity',
  'install_app',
  'push_pings',
  'chat_lanes',
  'tickets',
  'first_capture',
];

/** Where the 'tickets' step's photographs land: a 'json'-typed field on
 * the record type the member's OWN membership_parties binding names (the
 * same binding R1c's "@assigned" walks terminate at). Each entry the
 * write path appends: { id, name, expires_on?, file_ref?, added_at } —
 * verified_by/verified_at stay null here by design (the office verifies;
 * the verify ACTION and the expiry-ramp attention item are register debt,
 * not this config's). */
export interface FirstRunTicketsConfig {
  recordType: string;
  field: string;
  /** C7 — THE FOUNDER HAS NO OFFICE BEHIND HIM. A tech's binding is made
   * at INVITE time (auth/invites.ts's membership_parties insert), so the
   * tickets step always has somewhere to land. The person who CREATES the
   * tenant is preceded by nobody: no invite, no binding, and therefore a
   * tickets step that can only be skipped. Declaring `selfProfile` says
   * this pack's tickets record type is a PERSON's own file, so the caller
   * may create their own — one record, their own name, bound to their own
   * membership (POST /api/me/record). Absent/false: the door does not
   * exist on the build at all, and an unbound caller can only skip. */
  selfProfile?: boolean;
}

/** The 'first_capture' step's target: which knotIntent target the first
 * spoken/typed utterance files as. The Knot cannot be explained, only
 * used — the step drives the PRODUCTION create door
 * (POST /api/knot/intent/create) at this target, deterministically (no
 * classification round trip: "tell me what you did today" IS the target's
 * own shape, and a first run must never dead-end on a model's mood). */
export interface FirstRunCaptureConfig {
  /** A key of the pack's own KnotIntentPackConfig.targets. */
  targetKey: string;
}

export interface FirstRunPackConfig {
  /** Ordered subset of the core-owned vocabulary; the order is the walk. */
  steps: FirstRunStepId[];
  /** Required iff steps includes 'tickets' (enforced at server build). */
  tickets?: FirstRunTicketsConfig;
  /** Required iff steps includes 'first_capture' (enforced at server
   * build, against the pack's own declared knotIntent targets). */
  firstCapture?: FirstRunCaptureConfig;
}

/* ------------------------------------------------------------------ *
 * Comms (C5f) — the pack-declared Telegram comms core: two DM lanes
 * (office/dispatch, one bot each) plus one shared supergroup whose
 * per-job topics C5g builds. Decisions 817a0489 (topics-in-one-
 * supergroup, two bots, role-owned history, forward-only) and 43268389
 * (four-layer roster gate). Same "dumb engine executes declarative pack
 * config" pattern as every sibling config here: declaring `comms` on a
 * VerticalPack is what makes packages/server register the webhook +
 * enrolment routes at all (and what makes boot REFUSE without the
 * VOWRIC_TELEGRAM_* env set — a comms surface with no credentials would
 * be a silent dead bot). Bot tokens, the webhook secret and the
 * supergroup chat id are ENV, never config: config says what the product
 * IS, env says which credentials this box holds.
 * ------------------------------------------------------------------ */

/** The two DM lanes plus the per-job topic lane C5g adds. CHECK-
 * constrained in db/migrations/0041_chat_comms.sql — the two halves must
 * move together. */
export type ChatLane = 'office' | 'dispatch' | 'job';

/** Who spoke on a lane. CHECK-constrained in 0041 likewise. */
export type ChatSenderKind = 'tech' | 'office' | 'system';

/** C5g: what a chat_messages.metadata JSONB holds (migration 0042) —
 * a Telegram media REFERENCE (the bytes are a later chunk; the row is
 * honest about what it holds, spec §6c) and/or system-line provenance. */
export interface ChatMessageMetadata {
  mediaKind?: 'photo' | 'document' | 'voice' | 'audio' | 'video' | 'video_note';
  fileId?: string;
  /** Which lifecycle moment a sender_kind:'system' row records. */
  event?: 'topic_header' | 'transition' | 'assignment';
}

export interface CommsLaneDef {
  /** Human label for the lane's thread header ("Office", "Dispatch"). */
  label: string;
}

export interface CommsPackConfig {
  /** The only channel the conversation layer speaks today. */
  channel: 'telegram';
  /** The manifest action the roster gate cites for enrol AND offboard —
   * the pack names it (e.g. 'rexx.roster.enrol_telegram') so neither Core
   * nor the server plugin hardcodes a pack's namespace. */
  enrolActionId: string;
  /** The two DM lanes, one bot each. */
  lanes: { office: CommsLaneDef; dispatch: CommsLaneDef };
  /** The one shared supergroup (its chat id comes from env; C5g's
   * chat_topics table holds the per-work-order topic map). */
  supergroup: { label: string };
  /** C5g: record types that claim a forum topic the moment a record of
   * that type is created — the claim is written INSIDE the creating
   * transaction (the mapping row is the outbox, spec §6a). Absent/empty
   * means the pack's records claim no topics. */
  topicRecordTypes?: string[];
  /** C5g: the field whose write IS assignment for this pack (Rexx:
   * `assigned_handler` — plain text by the pack's own design). An update
   * carrying it posts a best-effort system line into the record's topic. */
  topicAssignmentField?: string;
  /** R1 (decision 8b92d3df): record types a Telegram GROUP may be bound to
   * via the binding handshake — for Rexx, `hvac_site` (one supergroup per
   * client site; membership of the group is the visibility boundary,
   * because it is the only one Telegram enforces). Absent/empty means the
   * pack binds no groups and the handshake refuses everything. */
  groupRecordTypes?: string[];
  /** R5 (decision 8b92d3df): the tenant's ONE admin-side CLIENTS group —
   * its topics are records of `topicRecordTypes` (Rexx: hvac_client), one
   * level above the site groups. Absent means the pack has no clients
   * group and the handshake never offers one. */
  clientsGroup?: { label: string; topicRecordTypes: string[] };
  /** R4 — KEEP (decision c6a7e57a): a chat line or media item becomes a
   * note at a chosen level. The pack declares the vocabulary — which
   * record type IS a note, which field carries its reading, and which
   * relationship reaches each level — so the server plugin hardcodes
   * nothing. Absent means the pack has no Keep. */
  keep?: KeepPackConfig;
}

export interface KeepLevelDef {
  /** The note-side relationship that attaches a kept note at this level
   * (Rexx: note_work_order / note_site / note_client / note_equipment). */
  relationshipKey: string;
  /** What the level's target must BE — a keep to 'site' aimed at a work
   * order is a 422, not a surprise link. */
  targetRecordType: string;
  /** Human name for cards and summaries. */
  label: string;
}

export interface KeepPackConfig {
  /** The record type a keep CREATES (Rexx: hvac_note). */
  noteRecordType: string;
  /** The field holding the kept text — CV5's "our reading", editable;
   * the SOURCE stays the truth (decision c6a7e57a: link, never copy). */
  noteBodyField: string;
  /** Optional field holding the source chat message's id — provenance,
   * durable and queryable. */
  sourceMessageField?: string;
  /** For MEDIA keeps: the relationship (declared on the source's own
   * type) that ties a retained source record to the note — Rexx:
   * hvac_source's `source_note_record` (CV5's captureSourceRecordLink). */
  sourceLinkKey?: string;
  /** What a media source must BE (Rexx: hvac_source). */
  sourceRecordType?: string;
  /** The levels, keyed by the API's level names. */
  levels: Record<string, KeepLevelDef>;
}

/* ------------------------------------------------------------------ *
 * Authority manifest (C3) — the pack-declared answer, per action, to:
 * whose authority does this spend, can it be taken back, does it leave
 * the building, and what happens at the boundary (it converts to an ask,
 * never a dead end). Spec: wiki page
 * the-authority-manifest-c3-specification-draft-for-the-walk-2026-08-06;
 * decisions 08547e14 (boundary is authority), f8ff0b50 (reversibility not
 * confidence), b9d8164b (W1–W7 dispositions). Declarative data consumed
 * by the pure resolver in @vowric/core's authority/resolve.ts — same
 * "dumb engine executes declarative pack config" pattern as
 * KnotIntentPackConfig / NavRolesPackConfig / ClientPortalPackConfig.
 * ------------------------------------------------------------------ */

export type AuthorityReversibilityClass = 'reversible' | 'delayed' | 'irreversible';
export type AuthorityExternality = 'internal' | 'external';
/** bc0bd4a8's three classes of dependent work: mechanically recomputable /
 * human work built on top (notify + block) / escaped the building
 * (correction, not return). */
export type AuthorityDependencyClass = 'pure_derivation' | 'human_work' | 'escaped';

export interface AuthorityActionAuthority {
  /** Pack role slugs, e.g. ["dispatcher", "admin"] or the scoped form
   * "tech:assigned" / "tech:own". W6 disposition: these are the DECLARATION
   * vocabulary; enforcement binds to capabilities (migration 0030) minted
   * from `roleCapabilities` at invite time — memberships.job_roles (0028)
   * stays descriptive. Ownership scope lives ON THE ROLE (and on the minted
   * capability as an "@assigned"/"@own" suffix), never on the action —
   * "dispatcher, admin, tech:assigned" means admins act un-scoped while a
   * tech acts only on their assigned record, which one action-level scope
   * could not express. Validation cross-checks that every role listed here
   * actually mints a matching capability, so declaration and minting cannot
   * drift. */
  roles: string[];
}

export interface AuthorityActionReversibility {
  class: AuthorityReversibilityClass;
  /** Return window, ISO 8601 duration (e.g. "P3D"). Entanglement may close
   * it earlier (bc0bd4a8), never later. Omit for irreversible. */
  window?: string | null;
  /** For class "delayed": the undo-send hold, ISO 8601 (e.g. "PT60S"). */
  delay?: string | null;
}

export interface AuthorityOnExceed {
  /** "authority" routes to any holder of the missing capability (W2
   * disposition: any-holder default); a role slug names a specific owner. */
  route_to?: 'authority' | string;
  /** The ask carries the prepared cascade itself — approval executes it,
   * re-gated as the approver. */
  carry?: 'full_cascade';
}

export interface AuthorityFieldRule {
  /** Role slugs that may read this field, or "*" — a field absent from an
   * actor's read scope must stay silent everywhere, including the resolver
   * (spec §5: no existence oracle). */
  read: string[] | '*';
  write: string[] | '*';
  /** "visible_locked": value renders, control locks, the affordance is
   * "Request a change" — on_exceed at field level (wf02's masked pay). */
  mask?: 'visible_locked';
}

export interface AuthorityActionDeclaration {
  /** Pack-namespaced action id, e.g. "rexx.work_order.close". */
  action: string;
  authority: AuthorityActionAuthority;
  externality: AuthorityExternality;
  reversibility: AuthorityActionReversibility;
  on_exceed?: AuthorityOnExceed;
  dependency_class: AuthorityDependencyClass;
  /** Field-ownership marks source (decision 2d089cf9). */
  fields?: Record<string, AuthorityFieldRule>;
}

/* ------------------------------------------------------------------ *
 * Derivation (R1a) — THE MANIFEST GATES THE OPERATION CLASS; THE
 * LIFECYCLE GATES THE EDGE.
 *
 * The pack already encodes per-edge authority in its transition graph
 * (LifecycleTransitionDefinition.min_role, and the propose->approve
 * primitive where an awaiting_*_approval state is CLEARED by an
 * admin-only edge). Duplicating that graph into per-state manifest
 * actions would be two sources of truth for one fact. So a manifest
 * action is per (record type x operation class), carrying exactly what
 * the lifecycle cannot express: externality, reversibility class +
 * window, dependency class, field rules, on_exceed routing.
 *
 * THE CLIENT NEVER NAMES AN ACTION. A caller that declares its own
 * action id is self-assessing its own authority. The server derives the
 * id from what the ROUTE itself knows — the record's type, the
 * operation the route performs, and (for a transition) the destination
 * state — via @vowric/core's deriveActionId.
 *
 * Resolution order: specific override (actionAliases) -> operation-class
 * action -> the manifest's `unlisted` policy.
 * ------------------------------------------------------------------ */

/** The five operation classes the generic /records/* surface performs.
 * `link` covers both linking and unlinking (one relationship edge, one
 * authority question); `archive` covers the soft status flip AND ITS
 * SIBLING — G32 (gap 32) arms `deleteRecord` exactly like `archiveRecord`,
 * reusing this same operation class rather than minting a new one, because
 * a fresh 'delete' class would derive to an action id no existing manifest
 * declares (Rexx, actor-assist) and turn every already-working delete into
 * an unlisted deny-and-ask the moment a pack has a manifest at all. Both
 * routes ask the identical authority question the manifest already
 * answers for archiving that type; that is "armed exactly like
 * archiveRecord" read literally. Same reasoning closes gap 32's OTHER
 * half: `unlinkRecords` (previously ungated entirely — the DELETE
 * /records/:id/links/:linkId route never threaded authority at all) now
 * asks under `link`, the operation class this comment already named as
 * covering it. */
export type AuthorityOperation = 'create' | 'update' | 'link' | 'transition' | 'archive';

/** How an operation-class action id is BUILT from a record type key.
 * Declared as data so a pack whose record-type keys carry a legacy
 * prefix (Rexx's `hvac_`, kept verbatim for the V6 importer's migration
 * path) still reads as `<namespace>.<short type>.<operation>` —
 * `hvac_work_order` + `create` -> `rexx.work_order.create`, the same
 * shape the hand-written v1 ids already had. */
export interface AuthorityDerivationConfig {
  /** Action-id namespace, e.g. "rexx". Normally the pack key. */
  namespace: string;
  /** Stripped off the record type key when present, e.g. "hvac_". */
  recordTypePrefix?: string;
}

/** A SPECIFIC OVERRIDE: the (record type, operation[, destination
 * state]) situation whose semantics genuinely differ from its operation
 * class, mapped onto the hand-written action id that expresses it.
 * Overrides are DATA — the derivation function reads this table, it does
 * not branch on pack knowledge. */
export interface AuthorityActionAlias {
  recordType: string;
  operation: AuthorityOperation;
  /** Only meaningful for `transition`: the destination state this
   * override covers. Omitted = every occurrence of the operation on this
   * type, whatever the destination. A toState-specific entry wins over a
   * toState-less one for the same (recordType, operation). */
  toState?: string;
  /** The action id this situation resolves to. */
  action: string;
}

/* ------------------------------------------------------------------ *
 * R1c — HOW A RELATION IS COMPUTED. The pack DECLARES it; Core executes
 * it.
 *
 * A scoped capability ("rexx.visit.transition@assigned") grants only when
 * the actor's relation to the TARGET matches — and until R1c nothing in
 * the system ever computed that relation, so every `@assigned` capability
 * granted nothing and the Rexx tech role was unusable the moment R1b
 * armed the gate on the production write routes.
 *
 * The relation is a WALK over the pack's own `links` graph, from the
 * target record to the record that IS this person (their
 * membership_parties binding). WHICH hops that walk takes is pack
 * knowledge, so it is declared here as data — exactly like
 * `roleCapabilities` and `actionAliases` — and the engine
 * (@vowric/core's resolveTargetRelation) knows only how to follow hops.
 * ------------------------------------------------------------------ */

/** ONE hop of the walk, over the `links` table.
 *   'out' — the CURRENT record is the link's SOURCE and declares
 *           `relationship` on its own type (hvac_visit -> 'tech_profile').
 *   'in'  — the current record is the link's TARGET, and some other type
 *           declares `relationship` pointing at it (hvac_work_order <-
 *           hvac_visit's 'work_order'). */
export interface AuthorityRelationHop {
  direction: 'out' | 'in';
  /** A relationship KEY off the pack's own record-type declarations —
   * never an invented name. Seed-time validation walks each path against
   * the real relationship graph and refuses the boot on a key that does
   * not exist, or that is ambiguous at an 'in' hop. */
  relationship: string;
}

/** An ordered walk. It resolves when the record it lands on is one of the
 * actor's OWN party records (membership_parties). An empty path is
 * meaningless and refused at seed time. */
export type AuthorityRelationPath = AuthorityRelationHop[];

/** How `assigned` is computed for ONE record type. */
export interface AuthorityRelationDeclaration {
  /** ALTERNATIVE walks — the relation holds if ANY of them resolves. More
   * than one is the honest shape wherever a pack carries assignment by
   * two routes (an hvac_receipt names its tech directly OR inherits one
   * through its work order). Absent = `assigned` is never true for this
   * type, and every "@assigned" capability over it converts to an ask. */
  assigned?: AuthorityRelationPath[];
}

/** Record type key → how its relations are computed. A type ABSENT from
 * this map FAILS CLOSED: the resolver returns {} and every scoped
 * capability over it converts into an ask. */
export type AuthorityRelationsConfig = Record<string, AuthorityRelationDeclaration>;

/* ------------------------------------------------------------------ *
 * C5a — ROW-LEVEL READ SCOPE (decision ea4063aa).
 *
 * Read scope in Core was TYPE-level only: a tech who may read
 * `hvac_work_order` could list EVERY job in the shop. The write side
 * already knew better ("@assigned" capabilities are measured against the
 * actor's relation to the target). This closes the asymmetry.
 *
 * NO NEW PACK VOCABULARY. The reachable set is `relations` above, run
 * OUTWARD: R1c asks "given this record, is it mine?"; row scope asks
 * "given me, which records are mine?". One declaration answers both, so
 * the two answers cannot drift apart. A pack author declares only WHICH
 * types are scoped and FOR WHOM — never a second walk.
 * ------------------------------------------------------------------ */

/** How ONE record type is row-scoped on read. */
export interface AuthorityReadScopeDeclaration {
  /** The JOB ROLES (the `roleCapabilities` vocabulary, not the membership
   * tier) whose reads of this type are narrowed to their own reachable
   * set. A role NOT listed here reads the type exactly as it does today —
   * which is how "dispatch sees unassigned work" is expressed: by leaving
   * `dispatcher` off the list, never by a special case in the engine. */
  scopedRoles: string[];
  /** C5c (decision 965e4266) — MAY A SCOPED ROLE STILL *NAME* ONE OF THESE
   * RECORDS? Enumeration and addressability are different permissions.
   * `scopedRoles` governs LISTING AND SEARCH; this flag governs whether a
   * record the actor cannot list still resolves far enough, BY ID, for the
   * authority gate to answer "that is not your job, ask the office" instead
   * of "not found". What it exposes is exactly two facts — the record's
   * TYPE and its CREATOR (see Core's resolveAddressableTarget and migration
   * 0040) — never its fields.
   *
   * DEFAULT FALSE, and the default is the point. A pack that says nothing
   * keeps C5a's strict 404. Turn it on only where there is no real trust
   * boundary between the scoped role's members and the record is one they
   * can legitimately be standing in front of: Rexx's `hvac_work_order`, and
   * nothing else. Where the boundary IS real — an ask, which carries who
   * asked for what and why — leaving this false is what keeps a denial an
   * absence. Granting it where it is not needed is the opt-in-gate mistake
   * in reverse. */
  addressable?: boolean;
}

/** Record type key → its read-scope declaration. A type ABSENT from this
 * map is NOT row-scoped for anyone — the default is OFF, per type. */
export type AuthorityReadScopeConfig = Record<string, AuthorityReadScopeDeclaration>;

export interface AuthorityManifestPackConfig {
  /** Manifest version — receipts cite the version that governed them. */
  version: number;
  /** Absent = operation-class ids are not derivable for this pack, and
   * every derived id falls to `unlisted`. Present = the naming rule. */
  derivation?: AuthorityDerivationConfig;
  /** Specific overrides, consulted BEFORE the operation-class id. */
  actionAliases?: AuthorityActionAlias[];
  /** Role slug → capability patterns minted at invite time. A capability
   * pattern is an action id or a trailing-wildcard form
   * ("rexx.work_order.*"), optionally scope-suffixed "@assigned" / "@own"
   * (e.g. "rexx.work_order.close@assigned"): a scoped capability grants
   * only when the actor's relation to the target matches. The gate checks
   * the actor's minted capabilities against the action id — W6
   * disposition (b). */
  roleCapabilities: Record<string, string[]>;
  /** Field rules about a MEMBER rather than about a record.
   *
   * `AuthorityFieldRule` otherwise hangs off an AuthorityActionDeclaration —
   * per (record type x operation class). That has no home for a fact ABOUT A
   * PERSON: a member's age is derived from `user_ages`, not read off a record,
   * so a pack had no way to say who may see it. This is that home.
   *
   * ⚠ WHY IT MATTERS THAT THIS EXISTS AT ALL. resolveFieldAccess treats an
   * UNDECLARED field as readable — "field rules narrow, they never widen".
   * That default is right for record fields and dangerous for personal ones:
   * a pack whose domain contains minors, silent on age, would show every
   * member's date of birth to every other member. The absence of a stanza,
   * not a bug, would be the leak. `validateMemberFieldRules` refuses that
   * pack at BOOT rather than trusting anyone to remember. */
  memberFields?: Record<string, AuthorityFieldRule>;
  /** R2 2026-08-07 (decision 6ae2703d) — THE SEATING. Job role → the
   * MEMBERSHIP TIER that job role sits at.
   *
   * Adding a rung to the ladder makes the right seat AVAILABLE; it does not
   * make anyone sit in it. Until this field existed, POST /auth/invites took
   * the tier and the job roles as two independent free inputs, so nothing
   * stopped a Rexx dispatcher being invited at 'staff' (below every
   * management gate) or at 'admin' (above routes the manifest does not gate
   * at all) — the same mis-seating the manager rung was added to end, still
   * reachable one field later. Declaring it here closes that: the pack SAYS
   * where each job role sits, and the invite path DERIVES the tier from the
   * job roles rather than accepting both (a supplied tier that disagrees is
   * refused, never silently honoured).
   *
   * GOVERNANCE, not vocabulary — which tier a job role holds is half of why
   * a gate let someone through, so it lives inside the manifest, is cited by
   * (pack_key, version) on every receipt, and editing it is a VERSION BUMP
   * exactly like editing an action row (seed.ts's immutability refusal
   * enforces this).
   *
   * Absent = today's behavior everywhere (Actor, Personal, the test
   * fixture): no derivation, no seating check, the invite's `role` stands
   * on its own. Present = validateAuthorityManifest requires EVERY role in
   * `roleCapabilities` to have a seat, so a job role can never be added to
   * the manifest without someone deciding where it sits. */
  seating?: Record<string, Role>;
  actions: AuthorityActionDeclaration[];
  /** R1c: how "@assigned" is COMPUTED, per record type. Lives inside the
   * manifest — and not beside it — because it is governance, not
   * vocabulary: whether a tech was "assigned" is half of why the gate
   * allowed them, so a receipt citing (pack_key, version) must be able to
   * resolve the rule that actually governed it. That also means editing
   * it is a VERSION BUMP, exactly like editing an action row (seed.ts's
   * immutability refusal enforces this). Absent = no type has a computed
   * relation and every scoped capability in the pack converts. */
  relations?: AuthorityRelationsConfig;
  /** C5a (decision ea4063aa) — WHICH TYPES ARE ROW-SCOPED ON READ, and for
   * which job roles. Lives inside the manifest for the same reason
   * `relations` and `seating` do, and it is the sharpest case of the three:
   * whether a tech could SEE a record at all is half of why a gate answered
   * as it did, so a receipt citing (pack_key, version) must be able to
   * resolve it. Editing it is therefore a VERSION BUMP.
   *
   * Absent (Actor, Personal, the test fixture) = today's behaviour exactly:
   * no GUC is set, no reachable set is computed, and the RESTRICTIVE policy
   * migration 0039 adds passes every row. Per-type, DEFAULT OFF. */
  readScope?: AuthorityReadScopeConfig;
  /** v21 (Rexx 173-tech-screens, 2026-09-26; brought to trunk 2026-09-27) — WHICH SOURCE RECORD TYPES'
   * RENDERED DOCUMENTS a job role may not open, EVEN THOUGH that job role may read the source record
   * itself. Keyed by JOB ROLE, each naming the record types that role alone may not open documents from:
   * `{ tech: ['hvac_invoice'] }` refuses only when EVERY job role the actor holds is named for that type
   * (a second seat rescues); an actor with no job roles is not narrowed. Enforced in
   * documents/render.ts `canReadSourceRecord`; validated at seed by authority/rowScope.ts
   * `validateDocumentAccess`. Absent = a document is exactly as readable as its source record.
   * Editing it is a VERSION BUMP, same as `readScope`. */
  documentAccess?: AuthorityDocumentAccessConfig;
  /** W5 disposition: unlisted actions are deny-and-ask. This is the only
   * supported value; declared so the choice is visible in the manifest. */
  unlisted?: 'deny_and_ask';
  /** G28 (engine-gaps §28/§24) — WHAT A NAMED SYSTEM ACTOR MAY DO.
   *
   * `actorType: 'system'` resolves to `role: null` (enforcement.ts's
   * resolveScope), and every pack record type declares a `min_role_write`
   * — so a background job, hook, obligation producer, document deliverer or
   * sweep was either refused outright by the general engine or reached
   * around it with raw SQL (the two things this gap register entry names
   * paid for already: a mail bridge that ran under a household's owner
   * membership so the audit trail named a person for a machine's act, and a
   * catch-up job whose marker write silently no-op'd). A system actor is a
   * first-class principal with a NAME and a GRANT, never a role — this is
   * that grant.
   *
   * `actor` is a stable machine name a job/hook/sweep names itself as
   * (`EnforcementContext.systemActor`, carried onto `EnforcementScope` by
   * resolveScope) — `mail_bridge`, `capture_catchup`, `obligation_producer`,
   * `document_delivery`, `calendar_sweep`. `reason` is a sentence a person
   * reads in the audit log, not a code comment — REQUIRED (validateSystemGrants
   * refuses a grant with an empty one).
   *
   * Consulted exactly where the role floor would have decided (records.ts's
   * assertRoleAtLeast and the per-transition min_role check): a write
   * outside the grant refuses with the sentence "system actor '<name>' is
   * not granted '<operation>' on '<type>' — add it to the pack's
   * systemGrants with a reason". Never silent — the same "an unnamed field
   * rule is a leak, not a bug" posture memberFields/readScope already take
   * for a missing declaration, applied to WRITE authority itself.
   *
   * Absent (every pack before this chunk) = an unnamed OR named system call
   * gets exactly today's behaviour: `role: null` refuses on every gated
   * type. A pack opts a job INTO the front door by declaring the grant, not
   * by the job reaching around the door. */
  systemGrants?: AuthoritySystemGrant[];
}

/** G28 — the vocabulary a `systemGrants` entry's `operations` may name.
 * Deliberately NOT `archive`/`delete`/`restore`: those are treated as a
 * `'update'` against the record type for grant purposes (the same "a write
 * against its own record type" framing records.ts's applyStatusChange
 * comment already uses for the human-role gate) — a separate vocabulary
 * word for the identical write would just be two names a pack author has
 * to remember mean the same grant. */
export type AuthoritySystemGrantOperation = 'create' | 'update' | 'transition' | 'read' | 'link';

/** One thing a named system actor may do (see `systemGrants` above).
 * `recordTypes: '*'` names every type in the pack; an array names some.
 * `fields`, read-grants only: narrows WHICH fields the read actually
 * discloses — absent means the grant discloses nothing past what G6's own
 * role-based field rules already would (empty `actorRoles`, i.e. usually
 * nothing beyond a field ruled `read: '*'`); naming a field (or `'*'` for
 * all of them) is what lets a system actor see one G6 rules otherwise hide
 * from every seat. Never a way to see MORE than the pack's own field rules
 * would show any role — it is additive over a role-based `hidden`, never a
 * blanket bypass. */
export interface AuthoritySystemGrant {
  actor: string;
  recordTypes: string[] | '*';
  operations: AuthoritySystemGrantOperation[];
  fields?: string[] | '*';
  reason: string;
}

/* ------------------------------------------------------------------ *
 * Cascade (C2b) — the durable consequence object. A cascade row records
 * ONE authorized action's full consequence: the replayable INTENT that
 * described it, the ordered EFFECTS that executed it (each carrying the
 * inverse captured at execution time), and the receipt citation
 * (pack_key, manifest_version, action_id) naming the manifest that
 * governed it. Migration 0034_cascades.sql is the table; the ONE shared
 * executor is packages/core/src/cascade/execute.ts. These types live
 * here beside the pack-config interfaces because every door (routes,
 * capture, asks, exchanges) speaks this vocabulary.
 * ------------------------------------------------------------------ */

/** Where a cascade entered the system. 'route' = a gated production/test
 * route; 'capture' = an approved Knot-capture proposal; 'ask' = an
 * approved ask-as-commitment (C2d); 'exchange' = a follow-on cascade
 * derived from a prior one (C2c); 'knot' = SPOKEN — the Knot's own create
 * door (C4a), which is mechanically a route but is kept distinct because
 * the receipt surface must be able to say "you said this" rather than
 * "you tapped this". CHECK-constrained in db/migrations/0034_cascades.sql,
 * widened by 0038_cascade_origin_knot.sql — the two halves must move
 * together. */
export type CascadeOriginKind = 'route' | 'capture' | 'ask' | 'exchange' | 'knot';

/** Cascade lifecycle. 'open' = executed, window state (if any) is
 * computed from window_ceiling_at at read time (C2c); the closed states
 * are C2c's return/exchange/correction outcomes. */
export type CascadeStatus = 'open' | 'settled' | 'returned' | 'exchanged' | 'corrected';

/** A reference inside an intent list — an existing record id, or the
 * result of an earlier createRecord step in the SAME list (0-based step
 * index). Same shape as commitments/proposals.ts's CaptureRef, so a
 * capture proposal's prepared actions ARE a valid intent prefix. */
export type CascadeStepRef = { id: string } | { newIndex: number };

/** The actor's relation to a step's target, for scoped capabilities
 * ("…@assigned"/"…@own") — resolved by the CALLER (it needs the links
 * table); the executor's per-step gate consumes it as booleans, same as
 * authority/resolve.ts's AuthorityTargetRelation. */
export interface CascadeStepRelation {
  own?: boolean;
  assigned?: boolean;
}

/** A step may declare what it IS in the manifest's vocabulary; the
 * executor re-gates it mid-cascade (an approved ask's remainder executes
 * re-gated as the approver — C2d). Steps without a declaration ride the
 * cascade's own top-level action. */
export interface CascadeStepAuthority {
  actionId: string;
  relation?: CascadeStepRelation;
}

/** The replayable vocabulary — a SUPERSET of the capture proposal's
 * CaptureAction ('createRecord' | 'link' are shape-identical, so a
 * capture actions list is assignable as-is), plus 'updateRecord' and
 * 'transition'. Everything executes through the records engine's own
 * functions, never raw SQL. */
export type CascadeIntentStep =
  | {
      op: 'createRecord';
      recordType: string;
      fields?: Record<string, unknown>;
      /** R1b: the two OTHER create-time inputs CreateRecordArgs has always
       * carried. They joined the intent vocabulary when the generic
       * POST /records became a cascade door — that route accepts both, and
       * an intent that cannot express them would silently drop a caller's
       * explicit initial lifecycle state or `restricted` sensitivity on the
       * way through the executor. Optional and additive: every intent
       * written before R1b means exactly what it meant (absent =
       * createRecordMechanics' own defaults — the type's first lifecycle
       * state, 'normal'). */
      sensitivity?: 'normal' | 'restricted';
      lifecycleState?: string | null;
      authority?: CascadeStepAuthority;
    }
  | {
      op: 'link';
      src: CascadeStepRef;
      relationshipKey: string;
      dst: CascadeStepRef;
      label?: string;
      authority?: CascadeStepAuthority;
    }
  | {
      op: 'updateRecord';
      ref: CascadeStepRef;
      fields: Record<string, unknown>;
      editSummary?: string;
      authority?: CascadeStepAuthority;
    }
  | {
      op: 'transition';
      ref: CascadeStepRef;
      to: string;
      fields?: Record<string, unknown>;
      authority?: CascadeStepAuthority;
    }
  | {
      /** G45: a step that is not a records-engine mutation at all, but a
       * call through the DOOR REGISTRY (cascade/doors.ts) — the vocabulary
       * gap gaps 4+5 named ("no CHANNEL-ACT step", "no reversal
       * vocabulary for asks"). `door` names a REGISTERED door (never a
       * pack-declared one — G45's own fence); `args` is whatever that
       * door's own schema accepts, opaque to the executor. Today the only
       * caller that ever composes one is approveAsk, synthesizing a
       * single-step intent for a door-kind ask — a door step inside a
       * larger, client-composed cascade is explicitly Not In G45 ("a door
       * that asks another door"). */
      op: 'door_act';
      door: string;
      args: unknown;
      authority?: CascadeStepAuthority;
    };

/** The links row an executed 'link' step inserted — recorded WHOLE in the
 * inverse so undo is a literal DELETE-by-content, never a reconstruction. */
export interface CascadeLinkRowSnapshot {
  id: string;
  tenant_id: string;
  src_type: string;
  src_id: string;
  dst_type: string;
  dst_id: string;
  link_type: string;
  label: string | null;
  created_at: string;
}

/** The recorded undo for one executed step.
 *
 * RULE: inverses are captured at execution time from the rows being
 * changed — never reconstructed later. `restoreFields.prior` holds the
 * written fields' pre-write values read from the row inside the same
 * transaction, BEFORE the write (a field absent before the write is
 * recorded as null — JSON carries no undefined); `restoreState.prior_state`
 * is the pre-transition lifecycle state; `unlink.linkRow` is the full
 * inserted row. `null` marks a step that is not invertible (emits,
 * external sends — not in C2b's vocabulary, but the type allows it so the
 * effects list stays honest when they arrive). */
// G45 additions: `door_act` records that undoing this effect means calling
// the REGISTERED REVERSE door with its own args (never guessed — captured
// at PERFORM time from what the forward door itself returned, the same
// "never reconstruct later" rule every other inverse already keeps);
// `irreversible` is the sentence a door hands back INSTEAD of a reverse
// door when the forward act cannot be undone (an email already sent). It is
// treated exactly like `null` by window.ts's escaped-effect check — the
// return path is gone, correction is what remains — but unlike `null` it
// carries the human sentence the ask card showed before anyone approved,
// so a blocked return still names why, in words, rather than in silence.
export type CascadeEffectInverse =
  | { op: 'archive'; id: string }
  | { op: 'restoreFields'; id: string; prior: Record<string, unknown> }
  | { op: 'unlink'; linkRow: CascadeLinkRowSnapshot }
  | { op: 'restoreState'; id: string; prior_state: string | null }
  | { op: 'door_act'; door: string; args: unknown }
  | { op: 'irreversible'; sentence: string }
  | null;

/** One executed step, in execution order. `ordinal` is the index into the
 * cascade's INTENT list (so a skipped link leaves a visible gap rather
 * than silently renumbering). `door`/`doorArgs`/`receipt` are carried ONLY
 * on a `door_act` effect (G45) — the door that ran, the args it ran with,
 * and the receipt string its `perform` returned; every other op leaves
 * them undefined. */
export interface CascadeEffect {
  ordinal: number;
  op: CascadeIntentStep['op'];
  object_type: string;
  object_id: string;
  inverse: CascadeEffectInverse;
  door?: string;
  doorArgs?: unknown;
  receipt?: string;
}

/* ------------------------------------------------------------------ *
 * Money flags (S13) — the pack-declared MONEY MAP the approval-flag
 * sweep reads. Alan reviews work orders BY EXCEPTION: he does not read
 * every job, he reads the ones whose money is out of band. Which record
 * type is "the job", which rows carry what it was billed and what it
 * cost, and where a worker's pay rate lives are all VERTICAL facts —
 * Core hardcodes none of them, exactly as ops/ticketExpiry.ts hardcodes
 * neither 'hvac_tech_profile' nor 'tickets'. A pack that declares no
 * `moneyFlags` is swept over in silence.
 *
 * THE BAND, NOT THE NUMBER, IS THE POINT: every threshold is a lever
 * (commitments/levers.ts, `hvac.flag.*`), never anything declared here.
 * This block says WHERE the numbers are; the levers say WHICH numbers
 * are wrong.
 * ------------------------------------------------------------------ */

/** The labour half: the visit-shaped child that carries what the tech
 * BILLED, plus the walk from that child to the worker's PAY RATE. Every
 * field is read as a plain number; absent/unparseable is UNKNOWN, never
 * zero (S13's own silence rule). */
export interface MoneyFlagsLabourConfig {
  /** The child record type holding one session of work. */
  recordType: string;
  /** Its relationship key back at the subject (the rollup source key). */
  relationshipKey: string;
  /** What the customer was billed for this session. */
  billedField: string;
  /** Hours the worker actually put in — the hourly payout's multiplicand. */
  hoursField: string;
  /** Optional: the field naming which basis this session is paid on.
   * Values are matched against `hourlyTreatment`/`commissionTreatment`
   * below; anything else (or an absent field) falls through to the pay
   * record's own shape. */
  treatmentField?: string;
  hourlyTreatment?: string;
  commissionTreatment?: string;
  /** Optional: a boolean field meaning "overtime was approved on this
   * session". A session flying it is NOT COMPUTABLE — no pack models the
   * overtime HOUR SPLIT, so a base-rate payout would understate the cost
   * and under-flag. Silence beats a wrong number. */
  overtimeApprovedField?: string;
  /** The worker the session was performed by. */
  workerRelationshipKey: string;
  workerRecordType: string;
  /** The pay record, which points AT the worker (src = pay, dst =
   * worker), and the two rates it may carry. Reading pay is what makes
   * the labour-derived flags OFFICE-ONLY — see MoneyFlagsPackConfig. */
  payRecordType: string;
  payRelationshipKey: string;
  /** Currency per hour. */
  hourlyRateField: string;
  /** A FRACTION of the billed amount, 0..1. A stored value outside that
   * range is not silently re-scaled — the session is NOT COMPUTABLE. */
  commissionRateField: string;
}

/** The materials half: cost and sell per unit on a quantity line. Both
 * prices must be present on a line for it to count; a line missing
 * either is UNKNOWN and poisons the ratio rather than defaulting to 0. */
export interface MoneyFlagsMaterialConfig {
  recordType: string;
  relationshipKey: string;
  qtyField: string;
  unitCostField: string;
  unitSellField: string;
}

export interface MoneyFlagsPackConfig {
  /** The record the owner approves — the flag's subject and the doorway
   * an attention item opens. */
  subjectRecordType: string;
  /** Lifecycle states whose money is moot. A cancelled job's ratios are
   * arithmetic about work that never happened; flagging it is noise, and
   * a noisy list is the one failure mode worse than no list. */
  excludeStates?: string[];
  labour: MoneyFlagsLabourConfig;
  material: MoneyFlagsMaterialConfig;
}

/** G-S13-1 — one tunable a PACK declares, in the same shape Core's own
 * registry (`LEVER_DEFINITIONS`, packages/core/src/commitments/levers.ts)
 * uses, so a pack lever is read and written through Core's one lever path
 * (`getLever`/`listLevers`/`setLever`/`getLeverValueWithClient`, the
 * `/api/levers` routes, `rt.lever.get`) — never a second lever store.
 *
 * ⚑ THE KEY MUST START WITH `<pack key>.` and may not collide with a Core
 * key or another pack's: `seedVerticalPacks` refuses the boot otherwise
 * (`PackLeverDeclarationError`, the sentence names the key). A pack's levers
 * are listed and settable ONLY for tenants of that pack's vertical — every
 * other tenant reads them as unknown. */
export interface PackLeverDefinition {
  key: string;
  category: string;
  description: string;
  valueType: 'integer' | 'number' | 'boolean' | 'string' | 'string-list';
  defaultValue: unknown;
  /** A 'string' lever whose value must be one of these. */
  allowedValues?: readonly string[];
  /** Inclusive bounds for an 'integer'/'number' lever; setLever refuses a
   * value outside them, and the default must sit inside them. */
  min?: number;
  max?: number;
  /** Absent = 'tenant' (a tenant's own dial). 'platform' = the deployment's
   * number, moved by env var (`VOWRIC_LEVER_<KEY>`), never a tenant row. */
  scope?: 'platform' | 'tenant';
}

/** A vertical definition pack (spec §11's `VerticalPack` shape). Only
 * `recordTypes` is meaningful to the P1.4 definitions loader; the rest
 * (watcherChecks, sideEffects, navigation, theme, seeds, engines) are
 * later phases' concerns and are typed loosely here — the loader does not
 * consume them yet, it only must not choke on their presence. `knotIntent`,
 * `navRoles`, and `clientPortal` are the exception: typed and consumed
 * (packages/server/src/plugins/{knotIntent,team,clientPortal}.ts),
 * optional/additive like the rest. */
/** One line of a pack's opening chart of accounts (B8b). */
export interface LedgerChartEntry {
  type: 'assets' | 'liabilities' | 'equity' | 'income' | 'expenses';
  /** Colon-separated, Beancount's convention. Its root must equal `type`. */
  name: string;
  currency?: string;
}

/** The five roots of double-entry — as a NAMESPACE, which is all they ever
 * were.
 *
 * ⚑ THEY ARE NOT ACCOUNTS, and B8b shipped them as five rows that were. The
 * export (B8b′) is what surfaced it: an account name is at minimum
 * `Root:Something`, so a bare `Assets` cannot be written down, and it could
 * never be posted to either — a root is a category, not a place money sits.
 * Every tenant created between 2026-08-22 and this change opened with five
 * accounts that no entry could reference and no export could carry.
 *
 * The restraint that produced them was right and is unchanged: seed from
 * ESTABLISHED PRACTICE, never authorship, and no published chart is on file to
 * copy. What was wrong was the shape of the thing chosen to express it. */
export const LEDGER_ROOT_NAMES: readonly LedgerChartEntry['type'][] = [
  'assets', 'liabilities', 'equity', 'income', 'expenses',
];

/** The one account a book genuinely cannot start without.
 *
 * Not a judgement about anybody's business — it is the counterpart every book
 * that begins mid-life needs, and the canonical contra for `padToBalance`. A
 * person arriving in August has a bank balance and eight months they are not
 * going to re-key; the difference has to land somewhere, and every double-entry
 * tool in this family calls that somewhere `Equity:Opening-Balances`.
 *
 * Everything past this is still a judgement about a particular business, and
 * the honest way to learn one is to ask its owner. */
export const LEDGER_OPENING_CHART: readonly LedgerChartEntry[] = [
  { type: 'equity', name: 'Equity:Opening-Balances' },
  // D3 (2026-09-12): the FX conversion root every tenant has, so a
  // cross-border transaction always has somewhere to post its conversion
  // legs without waiting on a pack to think of it first. Bare here —
  // `Equity:Conversions:<CCY>` per-currency sub-accounts are opened lazily,
  // one at a time, by `recordFxTransaction` the first time that currency's
  // legs need one.
  { type: 'equity', name: 'Equity:Conversions' },
];

/** The liability/equity namespaces a pack's OWN chart may declare, beyond the
 * two Core reserves seeded into every tenant above.
 *
 * D3 (2026-09-12): Liabilities and Equity carry the ledger's liability-
 * boundary positioning — "Vowric keeps a real set of records, Vowric is
 * NEVER the customer's book of record" — in a way Assets/Income/Expenses do
 * not, so only these two roots are held to a controlled vocabulary; a pack
 * may still open any Assets/Income/Expenses account it likes, subject only
 * to the existing root/type-agreement and Beancount-writability checks
 * (`signup.ts`'s chart seed already runs both).
 *
 * A namespace here means the exact name OR anything nested under it —
 * `Liabilities:Pass-Through` covers `Liabilities:Pass-Through:Regionals-2026`
 * as well as the bare name itself. `Equity:Opening-Balances` is deliberately
 * ABSENT: it is Core's own reserved leaf, seeded once above, never a
 * namespace a pack extends with children of its own (the seed refuses any
 * pack-declared account nested under it for exactly that reason). */
export const LEDGER_PACK_NAMESPACES: readonly string[] = [
  'Liabilities:Held-For-Clients',
  'Liabilities:Deferred-Revenue',
  'Liabilities:Pass-Through',
  'Equity:Conversions',
];

/** c3-lifecycle-kernel.md's own words on its lifecycle tables: "any state
 * may follow any state with a note — the order is the default path, not a
 * wall; guards come per transition in later chunks." So each kind's
 * `states` array is the default path (what the strip shows, in order), but
 * the graph is fully connected — every ordered pair within one kind's own
 * states — because THIS chunk declares no guard that would narrow it; a
 * later chunk narrows specific edges by editing this table, not by this
 * helper changing shape. A terminal state's OWN outgoing edges are real
 * data (the graph does not special-case them) — engagement/lifecycle.ts's
 * transitionEngagement is what actually refuses leaving one, per c3's
 * "a bounded engagement does not reopen" sentence; the data stays honest
 * about what the KIND's shape allows, the engine owns the business rule. */
function fullyConnected(states: readonly string[]): LifecycleTransitionDefinition[] {
  const transitions: LifecycleTransitionDefinition[] = [];
  for (const from of states) {
    for (const to of states) {
      // V11 (the command boundary) — every engagement transition gains
      // `min_role: 'admin'`, matching the dedicated operation
      // (engagement/lifecycle.ts's transitionEngagement, which already
      // gates at admin via its own withEnforcement floor). `fullyConnected`
      // is used ONLY by ENGAGEMENT_RECORD_TYPE below, so this is not a
      // generic helper's opinion leaking onto an unrelated type.
      if (from !== to) transitions.push({ from, to, min_role: 'admin' });
    }
  }
  return transitions;
}

/** V11 — the union of every variant's own (fully-connected) edges,
 * deduplicated by (from, to), each attributed to ONE command. Used to build
 * ENGAGEMENT_RECORD_TYPE's `command_owned.transitions`: "transitions ->
 * engagement.transition" (the spec's own words) means EVERY edge, in EVERY
 * kind's template, is reachable only through `transitionEngagement` — the
 * generic engine's own transition door refuses all of them, which is what
 * guarantees the dated `engagement_transitions` row is never skipped. Two
 * different kinds sharing a (from, to) pair (e.g. 'proposed' -> 'approved'
 * appears only in one kind's own template here, but the shape is generic)
 * collapse to one entry — they all resolve to the same command regardless
 * of which kind actually owns that edge, so there is nothing to lose by
 * deduplicating. */
function commandOwnedEdgesAcrossVariants(
  variants: Record<string, LifecycleDefinition>,
  command: string
): Array<{ from: string; to: string; command: string }> {
  const seen = new Set<string>();
  const edges: Array<{ from: string; to: string; command: string }> = [];
  for (const variant of Object.values(variants)) {
    for (const t of variant.transitions) {
      const key = `${t.from} ${t.to}`;
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push({ from: t.from, to: t.to, command });
    }
  }
  return edges;
}

/** K2's consumer (c3-lifecycle-kernel.md) — the bounded engagement: a
 * dated undertaking under an Account (`account_id`), five `kind`s each with
 * their own lifecycle, declared once here and included by any pack that
 * needs bounded engagements (today: personal) — same "Core-declared,
 * pack-included" shape LEDGER_OPENING_CHART already established.
 *
 * ⚑ NAME COLLISION, DELIBERATE AND NAMED: B9 already calls the enduring
 * client relationship `engagements` in storage (aliased to "Account" on
 * every surface — c2-account-kernel.md). This is a DIFFERENT, later object
 * — a bounded piece of work UNDER an Account — riding the generic records
 * engine under the SAME English word because that is what Huan's design
 * calls it (c3's own title). The two never share a table: this one is
 * `records` rows with `record_type = 'engagement'`; B9's is the dedicated
 * `engagements` table. Every screen says "Account" for the relationship and
 * "Engagement" only for these bounded ones — never both at once.
 *
 * Lifecycles are `lifecycle_variants` keyed by `kind` (K2) — one bounded
 * engagement type, five shapes, chosen at creation and fixed after
 * (`effectiveLifecycle`/`assertKeyFieldUnchanged`, records/lifecycle.ts).
 * Terminal states (`lost`, `converted`, `completed`, `complete`, `renewed`,
 * `exited`) are globally unique across all five templates — nothing here
 * depends on `kind` to know a state is terminal. */
/** V11 — pulled out of ENGAGEMENT_RECORD_TYPE's own `lifecycle_variants`
 * literal (unchanged in shape/values) so `command_owned.transitions` below
 * can be COMPUTED from the same object `lifecycle_variants.variants` uses,
 * rather than a second, hand-maintained copy of five kinds' worth of
 * states drifting from the first. */
const ENGAGEMENT_LIFECYCLE_VARIANTS: Record<string, LifecycleDefinition> = {
  acquisition: {
    states: ['arrived', 'qualified', 'diagnostic', 'offered', 'won', 'lost'],
    get transitions() { return fullyConnected(this.states); },
  },
  design_partner: {
    states: ['proposed', 'accepted', 'learning', 'building', 'validating', 'converted', 'completed'],
    get transitions() { return fullyConnected(this.states); },
  },
  // PRG-S (the cockpit's Delivery tab, phase 1 plan of record
  // 2026-10-09 §PRG-S item 1): a programme — a plan Huan hands Atlas that
  // comes back as ONE proposal and, on his yes, runs as standing steps.
  // NOT fully connected (unlike the five templates above): `proposed ->
  // running -> paused <-> running -> completed | withdrawn`, plus a
  // separate `proposed -> declined` (terminal, the "Not now" decline).
  // Hand-written rather than `fullyConnected(this.states)` because the
  // graph genuinely isn't — a declined programme never runs, a running
  // one never re-declines, and `completed`/`withdrawn` are reachable only
  // from `running`, never from `proposed` or `declined` directly.
  // `min_role: 'admin'` on every edge matches fullyConnected's own choice
  // above (every engagement transition is engagement.transition, admin
  // floor already enforced there).
  programme: {
    states: ['proposed', 'running', 'paused', 'completed', 'withdrawn', 'declined'],
    transitions: [
      { from: 'proposed', to: 'running', min_role: 'admin' },
      { from: 'proposed', to: 'declined', min_role: 'admin' },
      { from: 'running', to: 'paused', min_role: 'admin' },
      { from: 'paused', to: 'running', min_role: 'admin' },
      { from: 'running', to: 'completed', min_role: 'admin' },
      { from: 'running', to: 'withdrawn', min_role: 'admin' },
      { from: 'paused', to: 'withdrawn', min_role: 'admin' },
    ],
  },
  implementation: {
    states: ['discover', 'design', 'build', 'validate', 'cutover', 'stabilize', 'complete'],
    get transitions() { return fullyConnected(this.states); },
  },
  change: {
    states: ['proposed', 'approved', 'build', 'validate', 'release', 'complete'],
    get transitions() { return fullyConnected(this.states); },
  },
  managed_service: {
    states: ['active', 'at_risk', 'renewing', 'renewed', 'exited'],
    get transitions() { return fullyConnected(this.states); },
  },
};

export const ENGAGEMENT_RECORD_TYPE: RecordTypeDefinition = {
  type_key: 'engagement',
  vertical: 'personal',
  label_singular: 'Engagement',
  label_plural: 'Engagements',
  name_template: '{title}',
  fields: [
    { name: 'account_id', type: 'text', required: true, references: { table: 'engagements' } },
    {
      name: 'kind',
      type: 'picklist',
      required: true,
      options: ['acquisition', 'design_partner', 'implementation', 'change', 'managed_service', 'programme'],
    },
    { name: 'title', type: 'text', required: true },
    { name: 'summary', type: 'text', required: false },
    { name: 'started_on', type: 'date', required: true },
    { name: 'ended_on', type: 'date', required: false },
    { name: 'closed_reason', type: 'text', required: false },
  ],
  // No `relationships` entry for the Account: B9's Account is a DEDICATED
  // table (`engagements`, aliased — c2-account-kernel.md), not a generic
  // `records` row, so the generic `links` mechanism (which only ever
  // connects two `records` rows) cannot name it as a target_type. The plain
  // `account_id` field above IS the association — read directly, never
  // through listLinks.
  relationships: [],
  lifecycle_variants: {
    key_field: 'kind',
    variants: ENGAGEMENT_LIFECYCLE_VARIANTS,
  },
  converts_to: null,
  rollups: null,
  min_role_read: 'viewer',
  min_role_write: 'admin',
  // V11 (the command boundary, INV-CMD-1/3) — c3's own six terminal names
  // (this file's TERMINAL_STATES const, right above `fullyConnected`, no
  // longer exists as a separate literal here: this IS the one list now;
  // engagement/lifecycle.ts reads it off THIS definition instead of
  // keeping its own copy). Every edge in every kind's template is owned by
  // `engagement.transition` (transitionEngagement) — see
  // commandOwnedEdgesAcrossVariants's own header for why "transitions ->
  // engagement.transition" means the WHOLE graph, not a named subset.
  // PRG-S adds 'withdrawn' and 'declined' — the programme kind's own two
  // new terminal names, globally unique like every name already here
  // (checked by this file's own header comment at line 931).
  terminal_states: ['lost', 'converted', 'completed', 'complete', 'renewed', 'exited', 'withdrawn', 'declined'],
  command_owned: {
    transitions: commandOwnedEdgesAcrossVariants(ENGAGEMENT_LIFECYCLE_VARIANTS, 'engagement.transition'),
  },
};

/** K4 (console kernel, plan v3) — the Outcome: a measurable thing the work
 * is meant to change, for an Account or one of its bounded engagements.
 * Spec: docs/console/c5-outcome.md. Core-declared (like
 * ENGAGEMENT_RECORD_TYPE above) so any pack can include it; the personal
 * pack does, now.
 *
 * `account_id`/`engagement_id` are bare text fields, not `relationships` —
 * same reasoning as ENGAGEMENT_RECORD_TYPE's own account_id: the Account is
 * B9's dedicated `engagements` table, not a generic `records` row, and the
 * generic `links` mechanism only ever connects two `records` rows.
 * `engagement_id` (also a bare id, this time INTO the generic substrate —
 * the bounded `engagement` record type K2 declares) is nullable: null means
 * an account-level outcome.
 *
 * Lifecycle is the single, simple chain the spec names — NOT
 * `lifecycle_variants` (there is only one outcome shape, unlike the
 * engagement's five kinds): `defined -> measuring -> {realised, missed,
 * withdrawn}`. The transition into `realised` declares `required_fields:
 * ['current_value', 'measured_on']` — gap 23's declarative guard, already
 * enforced generically by transitionRecordMechanics (records.ts); no new
 * engine code for K4's guard. */
export const OUTCOME_RECORD_TYPE: RecordTypeDefinition = {
  type_key: 'outcome',
  vertical: 'personal',
  label_singular: 'Outcome',
  label_plural: 'Outcomes',
  name_template: '{measure}',
  fields: [
    { name: 'account_id', type: 'text', required: true, references: { table: 'engagements' } },
    { name: 'engagement_id', type: 'text', required: false, references: { record_type: 'engagement' } },
    { name: 'measure', type: 'text', required: true },
    { name: 'unit', type: 'text', required: false },
    { name: 'baseline_value', type: 'number', required: false },
    { name: 'baseline_as_of', type: 'date', required: false },
    { name: 'baseline_evidence', type: 'text', required: false },
    { name: 'target_value', type: 'number', required: false },
    { name: 'target_by', type: 'date', required: false },
    { name: 'method', type: 'text', required: false },
    { name: 'responsible', type: 'text', required: false },
    { name: 'review_on', type: 'date', required: false },
    { name: 'current_value', type: 'number', required: false },
    { name: 'measured_on', type: 'date', required: false },
    {
      name: 'realised',
      type: 'picklist',
      required: false,
      options: ['not_yet', 'partly', 'yes', 'no'],
      default: 'not_yet',
    },
    { name: 'client_confirmed_on', type: 'date', required: false },
    { name: 'client_confirmed_by', type: 'text', required: false },
  ],
  relationships: [],
  lifecycle: {
    states: ['defined', 'measuring', 'realised', 'missed', 'withdrawn'],
    transitions: [
      { from: 'defined', to: 'measuring' },
      { from: 'measuring', to: 'realised', required_fields: ['current_value', 'measured_on'] },
      { from: 'measuring', to: 'missed' },
      { from: 'measuring', to: 'withdrawn' },
    ],
  },
  converts_to: null,
  rollups: null,
  // V14 (INV-REF-1): the outcome's engagement, if it names one, must
  // belong to the SAME account the outcome itself names — an outcome
  // whose engagement_id points at a different account's engagement is
  // mis-parented (see references/runtime.ts + records/references.ts's
  // assertReferences, which reads this off the installed pack, never off
  // record_type_definitions — no column exists for it).
  consistency: [{ field: 'engagement_id', must_belong_to: 'account_id' }],
  min_role_read: 'viewer',
  min_role_write: 'member',
  // V11 (the command boundary) — the client's confirmation is a fact
  // ONLY `outcome.confirm` (admin, packages/core/src/outcome/confirm.ts)
  // may write, and entering 'realised' is that same command's own act
  // (it drives measuring -> realised itself, under the required_fields
  // guard already declared on that edge above) — a member's ordinary
  // PATCH/generic transition no longer records a confirmation nobody
  // outside this command actually witnessed.
  command_owned: {
    states: [{ state: 'realised', command: 'outcome.confirm' }],
    fields: [
      { field: 'client_confirmed_on', command: 'outcome.confirm' },
      { field: 'client_confirmed_by', command: 'outcome.confirm' },
      { field: 'realised', command: 'outcome.confirm' },
    ],
  },
  // G14 acceptance: "outcome (review_on, all-day)" — the review date on
  // every member's feed. `allDay` is technically redundant here (`date`
  // fields already render all-day) but stated explicitly, matching the
  // acceptance's own words. Non-terminal default excludes realised/missed/
  // withdrawn outcomes — a settled outcome has nothing left to review.
  calendar: [
    { key: 'review', start: 'review_on', allDay: true, title: 'Review: {measure}', audience: 'tenant' },
  ],
};

/** K5 (console kernel, plan v3) — the Plan: proposed dates/scope/price for
 * an engagement, authorised through `packages/core/src/plan/authorize.ts`
 * against tenant capacity/margin policy (migration 0106). Spec:
 * docs/console/c6-plan-authorization.md.
 *
 * ⚑ `status`, AS THE SPEC NAMES IT, IS THIS RECORD'S OWN `lifecycle_state`
 * — not a duplicate field. Every other generic-record-engine type in this
 * codebase (the bounded engagement above included) reports its state
 * through `lifecycle_state`/`lifecycle`, queryable via `GET
 * /records?lifecycle_state=` and renderable from `GET
 * /records/types/plan`'s schema — a second, hand-maintained `status` field
 * would drift from it the first time one write path touched one and not
 * the other. `authorize.ts`'s functions move a plan between states with
 * `transitionRecordMechanics` (the SAME mechanics `transitionRecord`
 * itself calls), inside their own enforced transaction, exactly the "net-
 * zero extraction, composed atomically" pattern records.ts's own mechanics
 * functions document. */
export const PLAN_RECORD_TYPE: RecordTypeDefinition = {
  type_key: 'plan',
  vertical: 'personal',
  label_singular: 'Plan',
  label_plural: 'Plans',
  name_template: '{title}',
  fields: [
    { name: 'engagement_id', type: 'text', required: true, references: { record_type: 'engagement' } },
    { name: 'title', type: 'text', required: true },
    { name: 'scope', type: 'text', required: false },
    { name: 'proposed_start', type: 'date', required: false },
    { name: 'proposed_end', type: 'date', required: false },
    { name: 'estimated_hours', type: 'number', required: false },
    // "null for design_partner" (c6) — never required.
    { name: 'price', type: 'number', required: false },
    { name: 'currency', type: 'text', required: false },
    { name: 'approved_on', type: 'date', required: false },
    { name: 'approved_by', type: 'text', required: false },
    { name: 'override_reason', type: 'text', required: false },
    { name: 'override_consequence', type: 'text', required: false },
    // V15 (v15-capacity-and-governing-agreement.md, INV-CAP-4) — a plan
    // that is a client promise is approved only under an applicable
    // accepted Agreement; the ONE exception is internal speculative work
    // Huan himself authorises, never a client promise (ruling 2).
    // Command-owned by `plan.mark_internal` (admin) — see this type's own
    // command_owned.fields below.
    { name: 'internal', type: 'boolean', required: false, default: false },
    // The ONE agreement this plan considers its governing terms, when the
    // plan names one explicitly rather than relying on whichever accepted
    // agreement `governingAgreements` would otherwise resolve. Not
    // command-owned: an ordinary plan.update (proposed/held only) sets it,
    // same as price/currency/scope — approvePlan is what actually CHECKS
    // it (must be a member of the governing set, or approval refuses).
    { name: 'governing_agreement_id', type: 'text', required: false, references: { table: 'agreements' } },
    // V15 (INV-CAP-5, the three-date rule) — the PROMISE: written ONCE, by
    // plan.approve/plan.override, copied from proposed_start/proposed_end
    // at that moment; frozen thereafter (frozen_in_states) except through
    // plan.accept_revision's own self-loop transition (see
    // command_owned.transitions below), which is the ONLY way a promise
    // ever moves after the first freeze — the OLD promise is preserved in
    // plan_forecasts (reason 'superseded by revision'), never overwritten
    // silently.
    { name: 'promised_start', type: 'date', required: false },
    { name: 'promised_end', type: 'date', required: false },
    // The proposed-revision "ask" (plan.revise, member) — a THIRD date,
    // distinct from both the promise above and the forecast history
    // (plan_forecasts, a separate table: "member may append" needs no
    // record field at all). `revision_status` starts 'none'; plan.revise
    // sets 'proposed'; plan.accept_revision (admin) sets 'approved' on a
    // successful re-evaluation. No 'declined' path is built this chunk
    // (not exercised by DATES-01) — the picklist value exists for a later
    // command to use.
    { name: 'revision_proposed_start', type: 'date', required: false },
    { name: 'revision_proposed_end', type: 'date', required: false },
    { name: 'revision_reason', type: 'text', required: false },
    {
      name: 'revision_status',
      type: 'picklist',
      required: false,
      options: ['none', 'proposed', 'approved', 'declined'],
      default: 'none',
    },
  ],
  relationships: [],
  lifecycle: {
    states: ['proposed', 'held', 'approved', 'overridden', 'withdrawn'],
    transitions: [
      // holdPlan (member) — a soft hold refuses nothing, so proposing to
      // hold is ordinary work, same bar as time.hold.create itself.
      { from: 'proposed', to: 'held', min_role: 'member' },
      // approvePlan / overridePlan (admin — the authority to spend
      // capacity and margin against policy).
      { from: 'held', to: 'approved', min_role: 'admin' },
      { from: 'held', to: 'overridden', min_role: 'admin' },
      // displaceHold (admin) reverts the DISPLACED plan's hold — the same
      // "withdraws the other plan's hold (status proposed)" c6 names.
      { from: 'held', to: 'proposed', min_role: 'admin' },
      // Withdrawing a plan that never got approved — reachable through the
      // generic /records/:id/transition door; no dedicated K5 endpoint.
      { from: 'proposed', to: 'withdrawn', min_role: 'admin' },
      { from: 'held', to: 'withdrawn', min_role: 'admin' },
    ],
  },
  converts_to: null,
  rollups: null,
  min_role_read: 'viewer',
  min_role_write: 'member',
  // V11 (the command boundary) — entering 'approved'/'overridden' (by
  // create OR transition) is owned by plan.approve/plan.override;
  // 'held' -> 'proposed' (displaceHold reverting the OTHER plan's own
  // hold) is owned by plan.displace. The fields ONLY those two commands
  // ever write are owned the same way.
  //
  // V15 — `promised_start`/`promised_end` are owned by `plan.accept_revision`
  // ALONE, even though `plan.approve`/`plan.override` write them FIRST: the
  // INITIAL write happens via approvePlan/overridePlan's own
  // transitionRecordMechanics call (held -> approved/overridden), which
  // never consults command_owned.fields at all (only command_owned.states/
  // .transitions gate a transition — see records.ts's own header on why
  // fields ride along unchecked on that door), so naming 'plan.approve'
  // here would be read by nobody. The field IS read here for the generic
  // PATCH door's frozen-state exemption AND for a plain (pre-freeze) PATCH
  // attempt — a caller naming promised_start while the plan is still
  // proposed/held (where it is null) is refused citing this command, which
  // is honest: `plan.accept_revision` is the ONLY command that EVER writes
  // this field through the generic mechanics, on a plan already frozen by
  // approve/override.
  //
  // `revision_status` is owned by BOTH `plan.revise` (sets 'proposed') and
  // `plan.accept_revision` (sets 'approved') — the array form
  // (RecordTypeDefinition.command_owned.fields's own `command: string |
  // string[]`, see its doc comment above) exists for exactly this: one
  // field, two commands that may legitimately write it. Both go through
  // updateRecordMechanics (never a transition — accept_revision changes no
  // lifecycle_state), which is why `frozen_in_states` needs the "every
  // field in THIS patch is owned by THIS opts.command" exemption
  // (records.ts) rather than a blanket state-level carve-out: `plan.approve`
  // still owns entering 'approved' itself (command_owned.states below),
  // undisturbed by accept_revision's later, same-state field writes.
  // `internal` is owned by `plan.mark_internal`, checked at update time
  // same as any other command-owned field (mark_internal only ever runs on
  // a proposed/held plan — see authorize.ts — so it never needs the frozen
  // exemption).
  //
  // Once approved/overridden, NO field may move through the generic door
  // EXCEPT plan.revise's own four fields and plan.accept_revision's own
  // three (promised_start/promised_end/revision_status) — `frozen_in_states`.
  command_owned: {
    states: [
      { state: 'approved', command: 'plan.approve' },
      { state: 'overridden', command: 'plan.override' },
    ],
    transitions: [
      { from: 'held', to: 'approved', command: 'plan.approve' },
      { from: 'held', to: 'overridden', command: 'plan.override' },
      { from: 'held', to: 'proposed', command: 'plan.displace' },
    ],
    fields: [
      { field: 'approved_on', command: 'plan.approve' },
      { field: 'approved_by', command: 'plan.approve' },
      { field: 'override_reason', command: 'plan.override' },
      { field: 'override_consequence', command: 'plan.override' },
      // INV-CAP-5 — the promise is written ONCE by plan.approve/plan.override
      // (their own transitions carry it) and moved after that only by
      // plan.accept_revision. GAP 89: a transition's `fields` are now held to
      // this list, so all three writers are named.
      { field: 'promised_start', command: ['plan.approve', 'plan.override', 'plan.accept_revision'] },
      { field: 'promised_end', command: ['plan.approve', 'plan.override', 'plan.accept_revision'] },
      { field: 'revision_proposed_start', command: 'plan.revise' },
      { field: 'revision_proposed_end', command: 'plan.revise' },
      { field: 'revision_reason', command: 'plan.revise' },
      { field: 'revision_status', command: ['plan.revise', 'plan.accept_revision'] },
      { field: 'internal', command: 'plan.mark_internal' },
    ],
    frozen_in_states: ['approved', 'overridden'],
  },
  // V11 (INV-CMD-3) — a withdrawn plan is done; nothing here declares an
  // outgoing edge FROM 'withdrawn' (see the transitions list above), so
  // G31's "an outgoing transition with no command" check has nothing to
  // flag — the state is simply unreachable-from, same as it always was,
  // now DECLARED as such rather than merely true by omission.
  terminal_states: ['withdrawn'],
  // G14 acceptance: "plan (proposed_start -> proposed_end, audience
  // tenant)" — the K5 plan's window, on every member's feed the moment it
  // is proposed. Non-terminal default (proposed/held/approved) drops it off
  // the feed once overridden/withdrawn.
  calendar: [
    { key: 'window', start: 'proposed_start', end: 'proposed_end', title: '{title}', audience: 'tenant' },
  ],
};

/** D1 (console kernel) — the fixture kind the consent primitive's own
 * acceptance walk is built to exercise: the cockpit's `case_study` consent
 * on an account's party (the NINE agreement's advocacy/reuse terms are
 * consents too — d1-consent-on-a-person.md's own header). NOT a gate on
 * the shared `outcome` type's `-> realised` transition, which the kernel
 * leaves alone; a small record type of its own instead, Core-declared
 * (like ENGAGEMENT_RECORD_TYPE/OUTCOME_RECORD_TYPE/PLAN_RECORD_TYPE above)
 * so any pack can include it.
 *
 * `subject_party_id` is a bare text field, not a `relationships` entry —
 * same reasoning as every other Core-declared personal type above: it
 * names a party, and a party is not a `records` row the generic `links`
 * mechanism can target. */
export const CASE_STUDY_MENTION_RECORD_TYPE: RecordTypeDefinition = {
  type_key: 'case_study_mention',
  vertical: 'personal',
  label_singular: 'Case study mention',
  label_plural: 'Case study mentions',
  name_template: '{title}',
  fields: [
    { name: 'subject_party_id', type: 'text', required: true },
    { name: 'title', type: 'text', required: true },
  ],
  relationships: [],
  // States named after the ACT, not the resulting adjective ('publish',
  // not 'published') — matching the consentGates.transitions entry below
  // verbatim, per the spec's own literal shape
  // (d1-consent-on-a-person.md's declared fixture).
  lifecycle: {
    states: ['draft', 'publish'],
    transitions: [{ from: 'draft', to: 'publish' }],
  },
  converts_to: null,
  rollups: null,
  min_role_read: 'viewer',
  min_role_write: 'member',
  // D1's own chokepoint: publishing names a real person, so it gates on
  // the `case_study` consent for whoever `subject_party_id` names.
  consentGates: [
    { kind: 'case_study', on: 'transition', transitions: ['publish'], subjectField: 'subject_party_id' },
  ],
};

/** ⚑ WHAT A PLAN'S COUNTABLE LIMIT ACTUALLY COUNTS, declared BY THE PACK.
 *
 * The subscriptions framework (§14.2) requires that "entitlements resolve
 * per-VERTICAL meters, not one global limit key" — `storage_bytes` for one
 * vertical and `seats` for another, with neither a special case. This is the
 * half of that Core cannot supply: the plan model says CallToWrap the product
 * caps a free household at three PERFORMERS, and only the pack knows that a
 * performer is an `aa_subject`.
 *
 * Without it, enforcing that cap means Core hardcoding a pack's record type —
 * the exact conflation that has bitten this codebase repeatedly (the July
 * `actor` vocabulary surviving in `actor_assist` code; `moneyFlags` existing
 * so Core hardcodes no `hvac_*` anything). So the pack names its own noun and
 * Core counts it.
 *
 * ⚠ A pack that omits this has NO countable meter, and that is a legitimate
 * state rather than an oversight — Rexx and the cockpit have no tier model at
 * all. Absence means "nothing to count", never "count zero".
 */
export interface PackMeterConfig {
  /** The record type one unit of the limit corresponds to — `aa_subject` for
   * actor_assist. */
  countedRecordType: string;
  /** What to call one of them in a sentence a CUSTOMER reads, singular. The
   * limit message is shown to a parent, so it must say "performer", never
   * "aa_subject". */
  unitLabel: string;
  /** The plural, because English. */
  unitLabelPlural: string;
}

/** GAP 62 — see `VerticalPack.clientDoor`'s own header. `profileTypes`
 * names which record types a SELF-SERVE capture may create at all;
 * `requiredFields` is, per type, the CLOSED set of field names that
 * capture may write — every name in the set must be present on a given
 * create, and nothing outside it is accepted (an undeclared type, or an
 * undeclared/missing field, is refused). `consentKinds` names the
 * contact-consent kinds (consent/contact.ts's own vocabulary, e.g.
 * 'contact.email' / 'casl.commercial') the door's consent-texts/consent
 * routes may offer during a self-serve session. */
export interface VerticalPackClientDoorConfig {
  profileTypes: string[];
  requiredFields: Record<string, string[]>;
  consentKinds: string[];
  /** The party/engagement a door activation creates has NO name yet — a
   * typed address is never used as one (atlas's review, GAP 62: "an email
   * as a name"). This names the FIRST capture that gets to rename them: a
   * record of this `type`, the first time `field` is written, renames the
   * party and the engagement from the neutral placeholder to that value —
   * and only from the placeholder, so a second, different value (a second
   * dancer's name, say) never re-renames a family already named by the
   * first). Absent = the placeholder stands until some OTHER act renames
   * it (a staff edit, a later chunk) — never silently patched in from an
   * email. */
  displayNameField?: { type: string; field: string };
  /** GET RID OF /door/:slug (2026-10-11, Huan). The door's own mailed
   *  `/start` link points at `/door/:tenantSlug` by default (the page
   *  `packages/server/src/plugins/clientDoor.ts`'s GET route itself
   *  still serves). A pack whose own app has grown a BETTER landing for
   *  that link — Studio Stays' `/signup`, which already runs the exact
   *  same activate -> profile -> consent -> complete sequence as its own
   *  screen — names that path here instead, and the mailed link points
   *  there: `${origin}${activatePath}#token=…&email=…`, never
   *  `${origin}/door/${slug}#…`. Absent (every pack but Stays today,
   *  including Eden proper's own personal pack) is BYTE-IDENTICAL to
   *  before this field existed.
   *
   *  Validated ONCE, at boot (registerClientDoorPlugin) — never
   *  per-request: must start with '/', hold no '#', '?' or '..', and
   *  name no host (an absolute or protocol-relative URL here would send
   *  every family's activation link off this deployment's own origin). */
  activatePath?: string;
}

/** CallToWrap native round 1 — the pack's native app(s), for the
 * `/.well-known/apple-app-site-association` the server answers with
 * (server plugins/nativeApp.ts). Each id is `<TeamID>.<bundle id>`. Absent =
 * this deployment has no native app and the AASA path answers 404. */
export interface NativeAppPackConfig {
  appleAppIds: readonly string[];
}

/** K1 — one name-link declaration (VerticalPack.captureModelNameLinks). */
export interface CaptureNameLinkDeclaration {
  /** The field on the SOURCE record holding the name (text) or names (json string[]). */
  field: string;
  /** A relationship the source type declares; its target type is what the name resolves against. */
  relationshipKey: string;
  /** K4 (2026-10-30) — when the name matches nothing in the capture, look for
   * it among the household's existing records of the target type (the dedup
   * scan, `captureModelDedupBy`'s field), link a match, and otherwise CREATE
   * the target with that one field — at most ONE create per capture; a second
   * distinct name is linked to nothing (one log line). Only for a target type
   * the capture lane may create. The junk guard runs first: a name shorter
   * than 2 characters, or equal (by `dedupKeyFor`) to any value the capture
   * carries in `createGuardFields`, is dropped from the source, never created. */
  createIfMissing?: boolean;
  /** K4 — `{ recordType: [field, …] }`: values in this capture that are never
   *  a name to create (a casting office, a company, an agency). */
  createGuardFields?: Record<string, string[]>;
  /** K4 — the line kept when THE INVARIANT drops this source because its
   * master could not be resolved (see `captureModelSingletonLinks`): the
   * source's `field` verbatim, then ` — ` and `reason`, filed under the
   * capture's unreadable-lines note on the first document. */
  orphanNote?: { field: string; reason: string };
}

/** L1 (2026-10-30) — where an install's inbound messages are kept, so
 * `GET /api/mailbox` can say when the last one arrived
 * (VerticalPack.inboundMessageSource). Every name is the pack's own. */
export interface InboundMessageSourceDeclaration {
  /** The record type every inbound message is retained as. */
  recordType: string;
  /** The field saying which kind of retained thing a row is, and the value
   *  that means "the message itself" (never one of its attachments). */
  kindField: string;
  kindValue: string;
  /** The datetime field holding when it was received (falls back to the
   *  row's created_at), and the text field holding its subject. */
  atField: string;
  subjectField: string;
}

export interface VerticalPack {
  key: string;
  /** GAP 108 — does this product take files from people at all? 'off' = the
   * upload doors are NOT REGISTERED (404): the blob-upload door
   * (`/records/blobs/presign|commit`), the records multipart door
   * (`POST /records/blobs`), the write routes of `files` (`POST /api/files`,
   * `POST /api/capture/url`), and every other multipart route refuses a file
   * part. An install may override it with `VOWRIC_UPLOADS=on|off`. Absent =
   * 'on', every existing pack unchanged. In production the stub scanner may
   * only boot with uploads off. */
  uploads?: 'on' | 'off';
  /** Agreed by atlas-core and atlas-review, 2026-10-31 — this pack's OWN
   * surfaces render documents on request (server-side, through weasyprint),
   * so the boot gate requires weasyprint exactly as a `deliver_document` side
   * effect does (core system/requiredBinaries.ts). Absent = the pack renders
   * nothing on request. No pack declares it today: CallToWrap's résumé PDF is
   * made on the phone. A pack that starts rendering on request declares this,
   * and the boot gate follows in the same diff. */
  documentRendering?: 'on';
  /** CallToWrap native round 1 — see NativeAppPackConfig. */
  nativeApp?: NativeAppPackConfig;
  /** CallToWrap R4c — the account holder deletes the whole account (the tenant, and their login when it was the
   * last one) from the app: `GET|POST /api/account/deletion`, the keep link, the day-7 runner. Absent = every one of
   * those paths answers 404 on this deployment (server plugins/accountDeletion.ts). */
  accountDeletion?: true;
  name: string;
  /** V1: the data-domain string this pack's record types are filed under
   * (`RecordTypeDefinition.vertical`, and the value a tenant row is stamped
   * with). A pack's KEY is its identity — the `VOWRIC_PACK` env value, the
   * workspace directory name — and that is a DIFFERENT namespace from the
   * data domain. They coincide for most packs, so this **defaults to `key`**
   * and must be declared ONLY where they legitimately differ (today: the
   * actor pack, `key: 'actor'` / `vertical: 'actors'`). Conflating the two
   * is what broke every Actor tenant created after 2026-08-10: the tenant
   * was stamped 'actor', no record type matched, and GET /records/types
   * returned []. */
  vertical?: string;
  recordTypes: RecordTypeDefinition[];
  /** G-S13-1 — the pack's own tunables (see PackLeverDefinition). Joined to
   * Core's lever registry at boot by `seedVerticalPacks`, the same path the
   * record types take. Absent = the pack declares none. */
  levers?: PackLeverDefinition[];
  /** GAP 60: the pack's server module (a VerticalServerModule, @vowric/core)
   * may register routes under a public `/<key>/` prefix as well as
   * `/api/<key>/`. Omitted (every pack today) means `/api/<key>/` only. */
  publicRoutePrefix?: boolean;
  /** The plan's countable meter for this vertical — see PackMeterConfig.
   * Omitted by every pack without a tier model, which is most of them. */
  meter?: PackMeterConfig;
  watcherChecks?: unknown[];
  sideEffects?: Record<string, unknown>;
  navigation?: unknown[];
  theme?: unknown;
  seeds?: unknown[];
  engines?: unknown[];
  knotIntent?: KnotIntentPackConfig;
  navRoles?: NavRolesPackConfig;
  clientPortal?: ClientPortalPackConfig;
  authority?: AuthorityManifestPackConfig;
  /** GAP 62 — the self-serve CLIENT FRONT DOOR's own declared capture
   * surface. The `client_door.enabled` lever is a tenant's yes/no; this is
   * what the vertical knows how to take in once that yes has been given —
   * two independent switches on purpose (a pack with no clientDoor config
   * leaves the door with nothing it may legally capture, even if a tenant
   * flips the lever on).
   *
   * During a SELF-SERVE (door-issued) onboarding session, the existing
   * capture route accepts ONLY a `profileTypes` member, and only the exact
   * field set `requiredFields` names for it — a staff-issued session is
   * unaffected by this config entirely. */
  clientDoor?: VerticalPackClientDoorConfig;
  /** The account front door (2026-10-12) — see AccountDoorPackConfig. */
  accountDoor?: AccountDoorPackConfig;
  /** B8b — the chart of accounts a NEW TENANT of this pack opens with.
   *
   * ⚑ THE SEED IS PER VERTICAL; THE ROWS ARE PER TENANT; IT IS EDITABLE
   * AFTERWARDS. Huan's three-part question answered in one field
   * (2026-08-22): a pack knows what a dance studio or an actor needs and Core
   * does not, the accounts are ordinary tenant data, and nothing stops a
   * tenant renaming, closing or adding to them once seeded.
   *
   * OPT-IN, deliberately. A pack that declares nothing gets NO accounts — the
   * ledger is not a capability every tenant uses, and seeding a chart into a
   * tenant that will never post to it is presumption dressed as helpfulness.
   *
   * ⚑ NOTHING HERE MAY BE INVENTED. The rule is "seeded from ESTABLISHED
   * PRACTICE, not invented": the accounting hat is synthesis from published
   * practice, never authorship. Core ships `LEDGER_OPENING_CHART` — the single
   * account a book cannot begin mid-life without — and nothing else, because NO
   * PUBLISHED CHART IS ON FILE TO COPY. A plausible-looking list of expense
   * categories presented as standard practice is exactly the fabrication that
   * rule exists to forbid. (It shipped as the five ROOTS first; those are a
   * namespace, not accounts — see LEDGER_ROOT_NAMES.) */
  ledgerChart?: LedgerChartEntry[];
  /** THE PACK'S DOMAIN CONTAINS MINORS.
   *
   * Declared by the pack, consumed by Core in two places: the age model's
   * member-creation guard refuses a membership with no recorded age, and
   * `validateMemberFieldRules` (authority/resolve.ts) refuses the pack at BOOT
   * unless the manifest also says who may READ an age. Collecting the datum
   * and governing who sees it are two obligations, and a pack that takes on
   * the first without the second is exactly the shape that leaks.
   *
   * DEFAULTS TO FALSE, and must. Rexx technicians and every existing member on
   * the platform were never asked for a birth date; a flag that defaulted true
   * would lock them out of their own tenants the day it shipped.
   *
   * Origin note: the flag is Lane A's (the age model, D47/D47b) and was
   * declared here by Lane B so the boot guard could read it. Both lanes landed
   * it, as predicted, and the merge kept THIS one (2026-08-19) — same name,
   * same type, same intent. The defaults-false paragraph above came across
   * from Lane A's copy, because it is the reason the field is optional and
   * nothing else said so. */
  requiresMemberAge?: boolean;
  /** D1 — the pack's own consent vocabulary: kind -> { label, scopeSchema?,
   * grantors, expiresAfterMonths? }. A `consentGates` entry on any of this
   * pack's record types (RecordTypeDefinition.consentGates) must name a
   * kind declared here — validated at seed (consent/gates.ts), boot
   * refuses otherwise, same "declare it or the gate cannot exist"
   * discipline as `requiresMemberAge`'s own `memberFields.age` guard.
   * Absent/empty = this pack declares no consent kinds; any `consentGates`
   * entry anywhere in its record types then refuses boot outright, since
   * nothing could possibly cover it. */
  consentKinds?: Record<string, ConsentKindDeclaration>;
  /** GAP 67: how THIS pack's record types are treated when one client
   * party is erased — see ErasurePackConfig's own header. Absent = every
   * Core default in docs/specs/core-gap-67-68-erasure.md §2 applies and
   * `selfServe` is false (a pack that declares nothing gets no self-serve
   * erasure route at all, same "declare it or it doesn't exist" posture
   * `clientPortal`/`clientDoor` already take). Validated at seed
   * (erasure/validate.ts): every named type/field must exist on this
   * pack's own `recordTypes`, and a `keepFields` entry may never name a
   * field flagged `sensitive` — an anonymised row keeping a sensitive
   * field defeats the point of anonymising it. */
  erasure?: ErasurePackConfig;
  /** C5f: declaring this is what makes the Telegram comms plugin exist on
   * a build at all — see CommsPackConfig's own header. */
  comms?: CommsPackConfig;
  /** C6a: the front door's pack-declared copy — see EntryPackConfig's own
   * header. Absent = the web's neutral defaults (Actor, Personal, the
   * test fixture keep their declarations byte-unchanged). */
  entry?: EntryPackConfig;
  /** C6b: the first-run script — see FirstRunPackConfig's own header.
   * Absent = no first-run framework on this build at all (Actor, Personal
   * keep their existing onboarding byte-unchanged). */
  firstRun?: FirstRunPackConfig;
  /** S13: where the money is, so the approval-flag sweep can find it —
   * see MoneyFlagsPackConfig's own header. Absent = no money flags on
   * this build; the sweep skips the vertical in silence. */
  moneyFlags?: MoneyFlagsPackConfig;
  /** W3-1: how a document addressed to a reach ROLE finds a real address —
   * see ReachPackConfig's own header. Absent = only the two memberships
   * roles Core resolves for itself (`technician`, `owner`) can be reached.
   *
   * Ported 2026-09-26 (decision 2da5b9e8) — required by notify/reach.ts's
   * `resolveReach`/`registerReachRoutes` (both ported verbatim) and by
   * deliverEffect.ts's `fallbackTo` mechanism, which resolves through this
   * same registry. Without this field a pack has nowhere to DECLARE a
   * route, so the ported reach machinery could never be configured. */
  reach?: ReachPackConfig;
  /** W1-7: what THIS trade calls the fields of the company profile — see
   * CompanyProfilePackConfig's own header. Absent = Core's neutral field
   * names (every existing build is byte-identical).
   *
   * Ported 2026-09-26 (decision 2da5b9e8) — required by
   * `registerTenantProfilePlugin`'s own `companyProfile` param (server
   * plugins/tenantProfile.ts) and proven by the ported tenantProfile.test.ts
   * ("THE WORDS COME FROM THE PACK. Core does not know what an HST is."). */
  companyProfile?: CompanyProfilePackConfig;
  /** U1: the record type this pack retains KNOT CAPTURES as.
   *
   * The Knot is CORE (decision 1663db4d), but what a captured thing is STORED
   * as is vocabulary, and vocabulary belongs to the pack. Before this, the
   * capture path hard-coded `actor_source` — a type the Actor pack owns — so a
   * build without Actor broke outright ("unknown record type actor_source")
   * the first time a real file was handed over. The fix after that broke it
   * the other way: it asked the DATABASE which types were seeded, so on a
   * shared database where both are present an ACTOR build chose `source` and
   * the link then failed against the Actor relationship's `actor_source`
   * target. A deployment's identity comes from the pack it runs, never from
   * ambient database state.
   *
   * Absent = `'actor_source'` (see app.ts's call site), so every existing
   * Actor deployment is byte-identical. */
  captureSourceType?: string;
  /** CV3: which record types a Knot capture may pre-associate WITH — the
   * capture engine's subject vocabulary, per subject record type. Follows
   * `captureSourceType`'s exact precedent (a pack declaration threaded from
   * app.ts as a parameter, never ambient state): before this, the engine's
   * `SOURCE_BEARING_SUBJECT_TYPES` set was 100% Actor hardcodes, so a
   * perfect Rexx context envelope was silently ignored and every capture
   * fell to the generic inbox. See CaptureSubjectDeclaration for the shape.
   *
   * Absent = the Actor pack's six subject types (the engine's own default,
   * server/plugins/actorCapture.ts), so a pack that declares nothing — and
   * every packless test server — is byte-identical. */
  captureSubjects?: Record<string, CaptureSubjectDeclaration>;
  /** CV3: the record types the capture engine's MODEL-extraction lane may
   * CREATE on this build. A closed set is the first validation gate — the
   * model is told these are the only legal types and anything else it emits
   * is dropped before staging. Was the actor-only `MODEL_CREATE_TYPES`
   * hardcode, which left the lane structurally dead on every other pack.
   *
   * Absent = the Actor pack's four (the engine's own default), so existing
   * Actor deployments and packless test servers are byte-identical. */
  captureModelTypes?: string[];
  /** Which of `captureModelTypes` a household must be ENTITLED to before the
   * model may create it — `{ recordType: featureKey }`.
   *
   * ⚑ THE GATE BELONGS PER-TYPE, NOT PER-LANE. CallToWrap sells "audition
   * paperwork read for you" on FREE and "call sheets become the morning read"
   * on WORKING — the same capture, the same model call, two different tiers.
   * Gating the whole lane would take the free half away; gating nothing makes
   * the paid half free. So the pack names the requirement per type and the
   * engine narrows the model's legal set to what this household actually has.
   *
   * ⚠ A type ABSENT from this map is ungated — available to every tier. That
   * is the right default (most captures are free) and the reason
   * FeatureGates.test.ts exists to catch a paid feature that never acquired a
   * requirement here. Keys are feature strings rather than the FeatureKey
   * union because shared must not depend on core. */
  captureModelTypeFeatures?: Record<string, string>;
  /** W3: which record types a household must be ENTITLED to before it may
   * WRITE one through the generic record routes — `{ recordType: featureKey }`.
   *
   * ⚑ A DIFFERENT QUESTION FROM `captureModelTypeFeatures` ABOVE, and the
   * difference is who is acting. That map gates what the MODEL may create out
   * of a capture; this gates what a PERSON may create, edit, link or retire by
   * hand. A pack can need one, the other, or both: CallToWrap's saved resume
   * versions (`aa_resume_version`) are never extracted from anything and are
   * sold on Working+, so they appear here and not there.
   *
   * ⚠ WRITES ONLY. Reads are deliberately outside this map's reach, and that
   * is the entitlements framework's §5 lapse posture rather than an
   * oversight: a household that drops to a tier without the feature keeps
   * every record it already made, readable and printable. The gate refuses
   * the next EDIT, never the existing document. A map that also narrowed
   * reads would make a lapse look like data loss.
   *
   * ⚠ A type ABSENT from this map is ungated, which is the right default and
   * the pre-change behaviour for every pack. Keys are feature strings rather
   * than core's FeatureKey union for the same reason
   * `captureModelTypeFeatures` is: shared must not depend on core. */
  recordWriteFeatures?: Record<string, string>;

  /** W2 (`9753e5ed`): how the classify prompt's MENU describes one of
   * `captureModelTypes` — `{ recordType: hint }`.
   *
   * ⚑ THE CLASSIFIER PICKS FROM A MENU, AND A MENU ITEM IS A SENTENCE, NOT A
   * TYPE KEY. Stage 1 asks a cheap model "which record type(s) should this
   * capture become", listing each allowed type with a short description of the
   * THING ON THE PAGE. The engine carries hand-tuned hints for the frozen
   * Actor pack's four; every other type falls back to its own
   * `label_singular`, lowercased.
   *
   * That fallback is right while the label IS the description ("shoot day",
   * "project") and silently wrong when it is not: `aa_crew_credit` presented
   * as "crew credit" is a noun from our schema rather than anything a person
   * would say about a call sheet, so the classifier never names it and the
   * type — declared, gated, migrated, rendered — is never once produced. A
   * capture lane that is structurally dead while everything around it is
   * green is this file's most-repeated failure (`captureSourceType`,
   * `captureSubjects`, `captureModelTypes` were each the same shape).
   *
   * A hint here WINS over the engine's own default for the same key, so a
   * pack can also correct a legacy hint without the engine knowing its
   * vocabulary. Absent = the engine's existing behaviour, byte-identical for
   * every pack that declares nothing. */
  captureModelTypeHints?: Record<string, string>;
  /** WHICH MODEL-CREATED TYPES ARE THE SAME THING TWICE, and by which field —
   * `{ recordType: fieldName }`.
   *
   * ⚑ WHY THE ENGINE CANNOT DECIDE THIS. Most captured records SHOULD be
   * created afresh every time: two auditions for the same part on the same
   * day are two auditions. A few are registry entries instead — a casting
   * director named on Tuesday's breakdown and again on Friday's is ONE
   * person (CallToWrap, decision `deeb6266`) — and only the pack knows which
   * of its nouns behave that way, exactly as only the pack knows its capture
   * source type or its subject vocabulary.
   *
   * The engine's half is generic and small: before staging a `createRecord`
   * for a declared type, look for a record of that type IN THIS TENANT whose
   * named field matches case- and whitespace-insensitively, and stage a link
   * to the existing one instead of a second create.
   *
   * ⚠ TENANT-SCOPED, ALWAYS. The lookup runs through the ordinary enforced
   * read path, so it can only ever see the capturing household's own records.
   * A cross-tenant "this casting director across the product" match is
   * refused by ruling, not merely unimplemented.
   *
   * Absent = no dedup, which is the pre-change behaviour for every pack. */
  captureModelDedupBy?: Record<string, string>;
  /** K1 (2026-10-30) — LINKS BY NAME, declared by the pack: `{ sourceType:
   * { field, relationshipKey } }` or a list of them. The source record
   * carries the NAME of the record it points at (a crew credit's
   * `person_name`, an audition's `people_names`), and the engine links it to
   * the record of the relationship's target type whose dedup key
   * (`captureModelDedupBy`'s field for that type, compared through
   * `dedupKeyFor`) equals it.
   *
   * Candidates are ONLY the capture's own records of the target type, new or
   * deduped to an existing one — a matched existing person is a legal link end.
   * Zero matches or two → no link and one log line. A `json` field is read as
   * a list of names (one link per name, for a `many` relationship).
   *
   * ⚑ THE MODEL'S OWN LINKS ALWAYS WIN: this fills only what is missing, and
   * runs before the engine's adjacency repair. Tenant-scoped by construction,
   * because the dedup read it rides is. Absent = nothing, for every pack. */
  captureModelNameLinks?: Record<string, CaptureNameLinkDeclaration | CaptureNameLinkDeclaration[]>;
  /** K1 — SINGLETON LINKS: `{ sourceType: relationshipKey }`. When a capture
   * holds EXACTLY ONE record of the relationship's target type (new or
   * deduped), every newly created source record without that link is linked
   * to it. Zero, or two or more, and nothing is inferred. Runs after the
   * model's links and the name links, before the adjacency repair. A source
   * whose declared name was read and matched nothing is decided, and skipped.
   *
   * ⚑ K4 — THE INVARIANT. A `master_detail` / cardinality-`one` relationship
   * the pack declares a rule for here or in `captureModelNameLinks` is one the
   * engine was told how to resolve, so a capture never stages its child
   * without its master: a source still unlinked after every rule is not
   * staged (its links go with it), and the name link's `orphanNote` keeps the
   * line. A master_detail key with no rule keeps today's behaviour. */
  captureModelSingletonLinks?: Record<string, string>;
  /** L1 (2026-10-30) — the install's inbound messages, so `GET /api/mailbox`
   * carries `lastReceived: { at, subject } | null` beside the address. Read
   * through `listRecords` (newest first, one row, active only), so the type's
   * read floor and read scope apply exactly as on `GET /records`; a seat that
   * cannot read them gets `lastReceived: null`, never a refusal, because the
   * address itself stays readable. Absent = the key is absent from the
   * response, which stays byte-identical. */
  inboundMessageSource?: InboundMessageSourceDeclaration;
  /** K3 (2026-10-30) — whether this install's ingest runs the GENERIC
   * commitment extractor (core memory/extraction.ts). Default true, today's
   * behaviour. A pack whose product has no surface for generic commitments
   * declares `false`, so every ingest on that install (forwarded mail, its
   * attachments, uploads) skips the step instead of staging proposals nobody
   * will ever see. Install-wide by the packs[0] convention. */
  commitmentExtraction?: boolean;
  /** The pack's OWN below-tier sentence per gated model type (`{ recordType:
   * sentence }`) — what a family reads when their plan keeps a document but
   * does not read it into that type ("On Working, a call sheet becomes your
   * morning read"). Absent = the engine's neutral sentence, which names no
   * plan and no product. The document-kind lane has its own `notIncludedTeaser`;
   * this is the ordinary lane's. */
  captureModelTypeTeasers?: Record<string, string>;
  /** W4: DOCUMENT KINDS this pack's classifier may name, each with the
   * extraction it dispatches — see CaptureDocumentKindDeclaration for the
   * shape and for why an old resume could not ride the ordinary lane.
   *
   * Absent = the classifier is never asked about a kind at all and its prompt
   * is byte-identical to what it was, so Actor, Rexx, Personal and every
   * packless test server keep their exact behaviour. */
  captureDocumentKinds?: Record<string, CaptureDocumentKindDeclaration>;
  /** W4: which of `captureDocumentKinds` a household must be ENTITLED to
   * before the MID-tier extraction for that kind runs — `{ kindKey:
   * featureKey }`.
   *
   * ⚑ SEPARATE FROM THE KIND ITSELF, exactly as `captureModelTypeFeatures` is
   * separate from `captureModelTypes`, and for the same two reasons: shared
   * must not depend on core's FeatureKey union, and FeatureGates.test.ts
   * recognises a gate DECLARED as the value of a `*Features` map.
   *
   * ⚠ FILING IS NEVER GATED. A kind whose feature the household lacks still
   * classifies and still files — with the marker that says so — because the
   * parse ladder governs which documents get READ, never which are kept. */
  captureDocumentKindFeatures?: Record<string, string>;
  /** CV5 §4: the relationship, DECLARED ON `captureSourceType`, that joins a
   * retained RECORDING to the record its transcript became.
   *
   * The Knot's hold already stores the audio (`POST /api/knot/transcribe`
   * returns the stored `ref`) and then writes the transcript as an ordinary
   * record through `POST /api/knot/intent/create`. Without this key the two
   * halves of one utterance have nothing joining them, which is how a tech's
   * recording came to be stored, referenced by nothing, and unreachable from
   * every surface.
   *
   * On the SOURCE's side, not the record's, for the same reason
   * `CaptureSubjectDeclaration.direction` exists: `linkRecords` resolves the
   * key against the SRC's definition and link authority derives from the
   * SRC's type, so a recording's link cites the pack's SOURCE link action —
   * the one a field seat holds — rather than the created record's.
   *
   * Absent = this build does not join recordings to records, and the intent
   * route answers `recording: null` (never silently: the client says the
   * recording could not be attached). */
  captureSourceRecordLink?: string;
  /** CV6a: WHICH MOMENTS IN A TECH'S DAY EARN A PING — see
   * NotificationsPackConfig's own header. Absent = this build fires no
   * `work` notifications at all (the `chat` category is engine-level and
   * needs no declaration), so Actor, Personal and every packless test
   * server are byte-identical. */
  notifications?: NotificationsPackConfig;
  /** Stage 4: WHAT A CHARGE ON THE COMPANY CARD IS MEANT TO BE BACKED BY,
   * and what a deposit confirms — see CardsPackConfig's own header. Absent =
   * this build has no statement import, no matcher and no reconciliation
   * screens, and the card chase never opens.
   *
   * Ported 2026-09-27 (bank-to-ledger matching spec) verbatim from the
   * rexx-tier1 fork (origin/main) — required by
   * `packages/core/src/money/statements.ts`, ported in the same change. */
  cards?: CardsPackConfig;
}

/* ------------------------------------------------------------------ *
 * CV6a — NOTIFICATIONS (2026-08-16). Which moments in a tech's day are
 * worth a buzz in their pocket.
 *
 * THE ENGINE MUST STAY IGNORANT OF REXX NOUNS. Core knows how to resolve an
 * audience, write an inbox row and send a push; it does not know that a
 * work order is a thing, that a visit carries a tech, or that
 * `scheduled_start` means "when someone is expected". The pack DECLARES and
 * core EXECUTES — the same seam as `captureSubjects`/`captureModelTypes`
 * (CV3), `readScope` (C5a) and `topicRecordTypes` (C5g).
 *
 * AND IT DECLARES NO SECOND WALK. `assignmentSubjectTypes` names TYPES, not
 * paths: the walk that decides who is on a record is already declared once,
 * in the manifest's `relations`, and both the authority gate and row scope
 * already run it (in opposite directions). Assignment detection runs that
 * same declaration a third way — matching a freshly created link against a
 * hop of it — so a pack author never writes "who is on this" twice.
 *
 * ON VerticalPack RATHER THAN INSIDE THE AUTHORITY MANIFEST, deliberately.
 * `relations`, `readScope` and `seating` live in the manifest because they
 * are GOVERNANCE — half of why a gate answered as it did, and therefore
 * something a receipt citing (pack_key, version) must be able to resolve.
 * Who gets a buzz decides nothing and authorizes nothing; putting it in the
 * manifest would force a version bump (and re-project every seat's offer
 * tokens — seedAuthorityManifests, S5e) to change a notification title.
 * ------------------------------------------------------------------ */

/** ONE declared field whose change is worth telling the people on the job. */
export interface NotificationFieldChangeEvent {
  /** The record type whose field is watched. */
  recordType: string;
  /** The field name, as declared in the type's own `fields`. */
  field: string;
  /** What to call it on the phone ("Rescheduled"). */
  label: string;
  /**
   * Optional: the record the notification should ADDRESS, when it is not the
   * record that changed.
   *
   * Rexx needs this and the need is instructive: the field that actually
   * carries a job's timing is `hvac_visit.scheduled_start` (the work order
   * carries no scheduling field at all — verified against recordTypes.ts,
   * not assumed), but the screen a tech must land on is the JOB. So the
   * change is watched on the visit while the audience, the title and the
   * link are all resolved on the work order this hop reaches.
   *
   * `relationship` is an OUTBOUND relationship key declared on the watched
   * `recordType`; the nested `recordType` is what the far end must be for
   * the hop to count.
   */
  addressVia?: { relationship: string; recordType: string };
}

export interface NotificationsPackConfig {
  /**
   * Record types whose ASSIGNMENT is worth a ping: when a newly created link
   * puts a record of this type inside someone's derived scope, that person
   * hears "you're on WO-4007".
   *
   * The type must declare a `relations`.`assigned` walk in the manifest —
   * that walk IS the assignment definition, and this list only says which of
   * the declared types are worth announcing.
   */
  assignmentSubjectTypes?: string[];
  /** Declared field changes worth announcing to a record's current audience. */
  fieldChangeEvents?: NotificationFieldChangeEvent[];
  /** Ping a capture proposal's AUTHOR when someone else approves or declines
   * it — the other side of CV3/CV5's loop: the tech who spoke into the Knot
   * and got an ask learns the answer without hunting for it. */
  captureDecided?: boolean;
  /**
   * Record type → the in-app route a notification about it opens, with
   * `{id}` substituted. Hash-form paths, because the app is a hash router
   * mounted at the origin root: "/#/jobs/detail/{id}".
   *
   * Core builds CHAT links itself (a lane and a thread are core concepts
   * with a core address); a work event's screen is pack vocabulary and comes
   * from here. A type absent from this map gets a notification with NO link
   * rather than a guessed one — a push that opens the wrong screen is worse
   * than a push that opens the app.
   */
  recordRoutes?: Record<string, string>;
}

/** CV3: how a capture pressed on one subject record type lands on it.
 *
 * `relationshipKey` — the declared relationship the staged link runs
 * through. `direction` — WHICH SIDE OF THE LINK OWNS THAT KEY, because
 * `linkRecords` resolves the key against the SRC's definition and the
 * staged-link authority derives from the SRC's type
 * (core/commitments/proposals.ts): `subject_to_source` stages
 * src:subject -> dst:source (Actor's shape — the subject's own 'source'
 * slot); `source_to_subject` stages src:source -> dst:subject (Rexx's —
 * `hvac_source.source_work_order`, so the replay cites rexx.source.link).
 * `slotLimited` — true when the relationship is cardinality-one FROM the
 * subject's side, so a second capture must downgrade to the generic inbox
 * rather than stage a doomed link; false means N captures per subject and
 * no pre-check at all. `modelSubjectLinkKey` — optional: when the model
 * lane creates records while this subject is on screen, link each created
 * record that declares this relationship key (targeting the subject's own
 * type) to the subject; the generalization of the engine's old
 * `subject.type === 'actor_project'` hardcode.
 *
 * `nameFields` — optional: the fields on THIS subject type that hold a
 * person's own name (`aa_subject`'s `professional_name`/`legal_name`).
 * Declared ONLY on a subject type that IS a named person — a project or a
 * shoot day has no name a sender would print about themselves. Mail carries
 * no page a person was viewing, so it has no `context.subjectId` to hand the
 * engine; this is what lets the inbound-mail bridge resolve one anyway, by
 * matching the sender's own name (or a name printed in the subject line)
 * against the household's own roster — see actorCapture.ts's
 * `resolveMailCaptureSubject`. Absent = this type is never scanned for a
 * name match, which is every subject type that predates this and every pack
 * that never declares it. */
export interface CaptureSubjectDeclaration {
  relationshipKey: string;
  direction: 'subject_to_source' | 'source_to_subject';
  slotLimited: boolean;
  modelSubjectLinkKey?: string;
  nameFields?: string[];
}

/**
 * W4 (decision `7c04876f`) — A DOCUMENT KIND THE CLASSIFIER CAN NAME, and the
 * extraction that kind gets instead of the ordinary one.
 *
 * ⚑ WHY THE ORDINARY LANE COULD NOT DO THIS. The classify/extract pair in
 * server/plugins/actorCapture.ts asks ONE question — "which record type(s)
 * should this become" — and then shows the model those types' schemas. That
 * shape fits a breakdown (one document, one audition) and fits a call sheet
 * (one document, one shoot day). It does NOT fit an old resume: one document
 * is twenty credits, each of which is a project AND a character AND an
 * engagement, plus training, awards and skills — a shape the generic prompt
 * has no way to describe and the classifier's flat `candidateTypes` list no
 * way to express. So the pack DECLARES the kind and what a page of it means,
 * and the engine dispatches on the classifier's answer.
 *
 * ⚠ DECLARED, NEVER KNOWN. Every field below is the pack's vocabulary; the
 * engine reads `documentKind` off the classifier and looks it up here. An
 * engine that knew what a "resume" is would be the same hardcode
 * `captureSourceType`, `captureSubjects` and `captureModelTypes` each had to
 * be dug back out of that file.
 *
 * ⚠ THE TIER LIVES IN `captureDocumentKindFeatures`, NOT HERE — the same
 * split `captureModelTypes` / `captureModelTypeFeatures` already makes, and
 * for the same two reasons: shared must not depend on core's FeatureKey
 * union, and FeatureGates.test.ts's guard reads requirement MAPS.
 */
export interface CaptureDocumentKindDeclaration {
  /** What this document LOOKS LIKE, in the words a person would use. Rendered
   * into the LOW-tier classify prompt's kind menu, so it must describe the
   * page rather than name the feature. */
  hint: string;
  /** What a line of THIS document MEANS, and how it maps onto the types
   * below. Appended to the MID-tier extract system prompt.
   *
   * ⚠ THIS IS WHERE THE REFUSALS GO. Field descriptions (rendered from the
   * DATABASE — 0088/0089/0092's lesson) say what each field wants; this says
   * what the DOCUMENT is, which is the only place a rule about the page as a
   * whole can live ("the name at the top of a resume is the performer whose
   * resume it is — never a person record"). */
  guidance: string;
  /** The CLOSED set of record types this kind's extraction may create.
   * Deliberately independent of `captureModelTypes`: a resume produces types
   * (training, awards, skills) that no other captured document ever does, and
   * widening the ordinary lane to reach them would let a breakdown mint a
   * skill. */
  modelTypes: string[];
  /** Optional initial lifecycle state per created type — an ENGINE fact about
   * the document kind, never a fact the model is asked for.
   *
   * ⚑ WHY IT EXISTS AT ALL: a credit on an old resume is work that HAPPENED,
   * and CallToWrap's automatic resume only prints engagements in a credit
   * state. Created at the type's first state instead, every imported credit
   * would be an "offer" and the whole import would produce a resume with
   * nothing on it. */
  createLifecycleStates?: Record<string, string>;
  /** Created record type -> a relationship key DECLARED ON THAT TYPE and
   * targeting `captureSourceType`, linking what was extracted back to the
   * retained original. "Where did this credit come from?" with an answer on
   * the record itself rather than only in the proposal that made it. */
  provenanceLinks?: Record<string, string>;
  /** The record type that means "a performer" for this kind, and, per created
   * type, the relationship key that attaches one to them. Applied when the
   * capture names a subject of that type, or — following the ruling the
   * `captured-audition-performer` reaction already carries — when the
   * household has EXACTLY ONE performer, so there is only one thing it can
   * mean. Two or more and nothing is attached: putting one child's history on
   * another child's resume is worse than leaving it unattached. */
  subjectType?: string;
  subjectLinks?: Record<string, string>;
  /** The `captureSourceType` `kind` value to stamp on the filed original once
   * the classifier has named it, so the household's captures list says what
   * arrived. Absent = leave whatever retention inferred from the filename. */
  sourceKind?: string;
  /**
   * P1 — THE SENTENCE A HOUSEHOLD BELOW THIS KIND'S TIER READS, in the pack's
   * own words.
   *
   * ⚑ WHY THIS IS DECLARED AND NOT WRITTEN IN THE ENGINE. W4 shipped the
   * below-tier posture with the teaser hardcoded in actorCapture.ts — "Reading
   * an existing resume into past credits is part of Working" — which is prose
   * about a resume and a rung, in a file whose whole design rule is that it
   * knows neither (`⚠ DECLARED, NEVER KNOWN`, this interface's own header).
   * The second kind to arrive proved it: a Working household that files a
   * CONTRACT would have been told its resume import was not included. One kind
   * made the hardcode invisible; two make it a wrong sentence on a screen.
   *
   * ⚠ HONEST AND SHORT, never a degraded silent copy (house rule 9). It says
   * what was KEPT, what was not done, and what would change it — and it may
   * claim nothing about the document's contents, because on this path nothing
   * has read them. Absent = the engine's own domain-neutral sentence, which is
   * true but says less than a pack can.
   */
  notIncludedTeaser?: string;
  /**
   * GAP 111 — THE RECEIPT FOR A DECLARED DOCUMENT WHOSE TEXT COULD NOT BE READ.
   * A person pressed a door that names this kind ("Bring an old resume") and
   * the file's text extraction ran and found nothing (a scan, an image-only
   * PDF, a broken file). The capture's summary becomes this sentence, with
   * `{filename}` replaced by the file's own name — a receipt about what was
   * kept and what was not done, never an instruction. Absent = the engine's
   * own domain-neutral sentence. (When the extractor itself is missing, the
   * capture is DEFERRED instead and this sentence is not used.)
   */
  notReadTeaser?: string;
  /**
   * J2 (2026-10-30, the live walk) — A FIELD THAT MUST NEVER CARRY ONLY A
   * BILLING. `{ createdType: { field, sizeField, synonyms } }`.
   *
   * A résumé line that prints no character ("Northwater · Co-Star · 2023")
   * came back with the billing in the name field (`character_name: "Co-Star"`
   * beside `size: co_star`), and a print then reads "Co-Star — Co-Star".
   * Enforced deterministically AFTER the model, never by the prompt alone: a
   * `field` value that equals, case- and punctuation-insensitively, one of
   * `sizeField`'s own picklist options (`co_star` ≡ "Co-Star") or one of the
   * pack's `synonyms` ("Featured", "Principal") is cleared to null. When
   * `sizeField` is empty and the word IS one of its options, the option is
   * filled in, because the billing belongs there and only there. The field
   * must therefore be optional on the type. Absent = nothing is touched.
   */
  billingWordFields?: Record<string, { field: string; sizeField: string; synonyms: readonly string[] }>;
  /**
   * P1 — OPT IN TO ASKING THE MODEL FOR THIS DOCUMENT'S TERMS, VERBATIM, and
   * the heading they are filed under on the retained original.
   *
   * ⚑ WHAT A "TERM" IS AND WHY IT IS NOT A RECORD. A contract prints lines
   * that are STATEMENTS ABOUT the money rather than money themselves: "agency
   * commission 10% of gross", "ACTRA dues deducted at source", "usage limited
   * to Canada, one year". Each is load-bearing and none of them is a payment.
   * Staged as `aa_payment` rows they would be invented debts on a child's
   * ledger; folded into an amount as a deduction they would be an arithmetic
   * NOBODY PRINTED — the sharpest form of the fabrication this whole lane
   * exists to refuse. So they are copied out word for word and filed on the
   * document, where a person reads them and decides.
   *
   * ⚠ A RECEIPT, LIKE `unreadable`, AND WRITTEN THE SAME WAY: onto the filed
   * original, appended, immediately — never staged. There is nothing to
   * confirm about "the contract says this", it is true whether or not the
   * family accepts the payments, and it must survive a DECLINE.
   *
   * ⚠ OPT-IN PER KIND, so a kind that declares nothing here gets the
   * byte-identical extraction prompt it had before and is never asked for a
   * key it would have to invent an answer for — the same additive discipline
   * `captureDocumentKinds` itself applies to the classify prompt.
   */
  termsHeading?: string;
  /**
   * P1 — CREATED TYPE -> FIELDS THIS KIND OF DOCUMENT MAY NEVER FILL. Dropped
   * before anything is staged, and said out loud when one arrives.
   *
   * ⚑ WHY A DECLARATION AND NOT JUST A SENTENCE IN `guidance`. Everything
   * else this lane refuses, it refuses in the prompt — and for a resume that
   * is enough, because the worst outcome of a model ignoring it is a credit
   * a family can see is wrong on the page in front of them. A CONTRACT has
   * one field where that is not true: `aa_payment.received_amount` means
   * MONEY HAS ARRIVED, and the money surface's own settled rule takes any row
   * carrying one straight OUT of the chase. A model that helpfully filled it
   * from "session fee $850" would stage a payment that reads as PAID, the
   * review card would still say "3 payments to add", and a family would
   * approve their own money out of the list it exists to be in. Nothing red,
   * nowhere.
   *
   * ⚠ A PROMPT IS A REQUEST; THIS IS A REFUSAL. The guidance still carries
   * the sentence — the model should not try — but a field the DOCUMENT is
   * structurally incapable of knowing is an engine fact about the document
   * kind, exactly as `createLifecycleStates` is, and it belongs where a model
   * cannot reach it.
   *
   * ⚠ DROPPED, NOT REJECTED. The rest of the record still stages: refusing a
   * whole payment because one field was overreached would lose a true amount
   * over an untrue one.
   */
  refusedFields?: Record<string, string[]>;
  /**
   * ⚑ THE FIGURE THAT MAKES A ROW A ROW (2026-09-16, the Day 3–4 walk, item 1).
   *
   * Created type -> the fields at least ONE of which must carry a real number,
   * and where the document's own words for that line live when none does.
   *
   * ⚑ WHY THIS EXISTS. `guidance` already tells the model, in capitals, that a
   * line it cannot place goes VERBATIM into `unreadable` and that guessing is
   * the only wrong answer. The Day 3–4 walk forwarded a deal memo carrying
   * "Additional shoot days .... per scale, to be confirmed" and the model did
   * neither: it staged an `aa_payment` with `kind: other`, no amount, and the
   * line itself sitting in `notes`. Approved, that became a row on a child's
   * money screen reading "Other $0.00" — a precise, confident figure about a
   * child's money that no document printed, produced entirely by a missing
   * field. The exact thing the rule in the prompt exists to prevent, arriving
   * because a prompt is a request.
   *
   * ⚠ A PROMPT IS A REQUEST; THIS IS A REFUSAL — `refusedFields`' own sentence,
   * and the argument is the same one class further in. `refusedFields` drops a
   * field the document cannot know. This drops a RECORD the document did not
   * contain: a payment with no amount anywhere in it is not a payment, it is a
   * line nothing could place, and the pack already has a place for those.
   *
   * ⚠ RESCUED, NOT DISCARDED. The row does not vanish — it is copied into
   * `unreadable` in the document's own words and filed under the unreadable
   * heading on the retained original, which is where the family reads it and
   * adds it by hand. `line` names the field holding those words (for a payment,
   * `notes`, which is where the pack asks for a basis a document states without
   * a date); if it is empty, every non-empty text the model returned for that
   * row is joined instead, so nothing is ever silently lost.
   *
   * ⚠ OPT-IN PER KIND AND PER TYPE. A kind that declares nothing here stages
   * byte-for-byte what it staged before. And it is deliberately NOT a general
   * "required field" — `expected_amount` is legitimately optional on a payment
   * a HOUSEHOLD types by hand. It is only the DOCUMENT that cannot mean one.
   */
  figureFields?: Record<string, { anyOf: string[]; line: string }>;
  /**
   * ⚑ WHICH BINDER THE FILED ORIGINAL GOES IN (2026-09-16, the Day 3–4 walk,
   * item 4).
   *
   * Created type -> a relationship key declared ON `captureSourceType` and
   * targeting that type. The retained original is linked to the record this
   * capture created, so the document is FILED where a person goes looking for
   * it.
   *
   * ⚑ THIS IS NOT `provenanceLinks`, AND THE DIFFERENCE IS THE WHOLE POINT.
   * `provenanceLinks` runs the other way — created record -> the original —
   * and answers "where did this figure come from". The comment beside this
   * pack's own `provenanceLinks` refuses to stamp a PRODUCTION that way, and
   * is right to: a production is a registry noun a household reaches from
   * twenty directions and it did not "come from" one contract.
   *
   * Filing is a different claim. `aa_source.source_project` is the key the
   * project's own Documents binder reads, and it means "this paper belongs
   * under this production" — which a contract that names the production
   * plainly does. The walk found the consequence of conflating the two: a
   * forwarded contract created "Northwind Crossing", created its payments, and
   * the production's Documents section still read its empty teaser — "a call
   * sheet or a contract forwarded to your address files itself here, under this
   * production" — about the very contract that had made it. The sentence was a
   * promise the product was not keeping, on the screen that made it.
   *
   * ⚠ ONLY WHEN THIS CAPTURE CREATED IT. A deduped record the household already
   * had is not filed under — the same conservatism every other link here takes.
   * And the key is checked against the source type's declared relationships
   * rather than trusted, so a pack typo stages nothing instead of a link the
   * replay would refuse.
   */
  fileUnder?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// TENANT EXPORT VOCABULARY (2026-08-13)
//
// Lives in shared, not core, because a pack must be able to declare its export
// FORMAT without depending on the engine. The split: shared owns the vocabulary
// (these types and the pure link helpers), core owns the machinery (reading the
// database, writing the workbook, storing and mailing it), and each pack owns
// the mapping — because "a format the customer recognises" is by definition
// vertical-specific. Making @vowric/vertical-rexx depend on @vowric/core to
// name a column would have pulled the whole engine into a declarative pack.
// ---------------------------------------------------------------------------

export interface ExportRecord {
  id: string;
  /** `records.record_type`, aliased so specs read naturally. */
  type_key: string;
  /** `records.display_name`. */
  title: string | null;
  fields: Record<string, unknown>;
  lifecycle_state: string | null;
  created_at: Date;
  updated_at: Date;
}

export interface ExportContext {
  /** Every exported record by id, so a column can resolve a linked record. */
  byId: Map<string, ExportRecord>;
  /** Records linked to this one, in EITHER direction. */
  linksOf: (recordId: string) => ExportRecord[];
}

export interface ColumnSpec<T extends ExportRecord = ExportRecord> {
  /** EXACT header text from the customer's own workbook, oddities included. */
  header: string;
  value: (record: T, ctx: ExportContext) => unknown;
}

export interface SheetSpec<T extends ExportRecord = ExportRecord> {
  /** EXACT sheet name from the customer's workbook. */
  sheet: string;
  /** Record type the rows are drawn from. */
  typeKey: string;
  columns: ColumnSpec<T>[];
}

/** First linked record of a type — the common case ("its client"). */
export function linked(
  ctx: ExportContext,
  record: ExportRecord,
  typeKey: string
): ExportRecord | undefined {
  return ctx.linksOf(record.id).find((r) => r.type_key === typeKey);
}

export function linkedAll(
  ctx: ExportContext,
  record: ExportRecord,
  typeKey: string
): ExportRecord[] {
  return ctx.linksOf(record.id).filter((r) => r.type_key === typeKey);
}

// ---------------------------------------------------------------------------
// K3 — the Agreement's vocabulary (c4-agreement.md). Shared, not core-only,
// so a vertical pack and the client console read the same kind/status/side
// words the server and Core enforce — the same "shared enum, one vocabulary"
// shape as LEDGER_OPENING_CHART above.
// ---------------------------------------------------------------------------

export type AgreementKind = 'design_partner' | 'master' | 'project' | 'change' | 'managed_service';
export const AGREEMENT_KINDS: readonly AgreementKind[] = [
  'design_partner', 'master', 'project', 'change', 'managed_service',
];

export type AgreementStatus = 'draft' | 'sent' | 'accepted' | 'superseded' | 'ended';
export const AGREEMENT_STATUSES: readonly AgreementStatus[] = [
  'draft', 'sent', 'accepted', 'superseded', 'ended',
];

export type AgreementAcceptedVia = 'paper' | 'console_click' | 'esign' | 'verbal';
export const AGREEMENT_ACCEPTED_VIA: readonly AgreementAcceptedVia[] = [
  'paper', 'console_click', 'esign', 'verbal',
];

export type AgreementSignatureMode = 'console_click' | 'esign_request';
export const AGREEMENT_SIGNATURE_MODES: readonly AgreementSignatureMode[] = [
  'console_click', 'esign_request',
];

/** Which side of an agreement a term belongs to — Vowric's own consideration
 * table column, or the client's. Renders as the two-column consideration
 * table on the agreement spread. */
export type AgreementTermSide = 'vowric' | 'client';
export const AGREEMENT_TERM_SIDES: readonly AgreementTermSide[] = ['vowric', 'client'];

/** The consideration kinds — what is actually being exchanged. Rendered as
 * plain phrases on screen, never as these keys (c4's voice rule: "access",
 * "own box, managed by us", "tokens at cost", never `own_box_managed`). */
export type AgreementTermKind =
  | 'cash'
  | 'access'
  | 'observation'
  | 'testing'
  | 'advocacy'
  | 'reuse'
  | 'own_box_managed'
  | 'discount_hosting_maintenance'
  | 'tokens_at_cost'
  | 'conversion'
  | 'other';

export const AGREEMENT_TERM_KINDS: readonly AgreementTermKind[] = [
  'cash', 'access', 'observation', 'testing', 'advocacy', 'reuse',
  'own_box_managed', 'discount_hosting_maintenance', 'tokens_at_cost', 'conversion', 'other',
];

/** The machine voice's plain phrase for each term kind — never the enum key
 * itself (c4's copy rule). `other` has no fixed phrase: its own
 * `description` field IS the phrase for that row. */
export const AGREEMENT_TERM_KIND_PHRASES: Record<Exclude<AgreementTermKind, 'other'>, string> = {
  cash: 'cash',
  access: 'access',
  observation: 'observation',
  testing: 'testing',
  advocacy: 'advocacy',
  reuse: 'reuse',
  own_box_managed: 'own box, managed by us',
  discount_hosting_maintenance: 'discount on hosting and maintenance',
  tokens_at_cost: 'tokens at cost',
  conversion: 'conversion',
};

// ---------------------------------------------------------------------------
// G30 — Documents and deliveries' vocabulary (g30-documents-and-deliveries.md).
// Shared, not core-only, for the same reason the Agreement's vocabulary
// above is: a vertical pack, the client console and the server all read the
// same kind/channel/status words Core enforces.
// ---------------------------------------------------------------------------

export type DocumentKind =
  | 'agreement' | 'letter' | 'invoice' | 'receipt' | 'statement' | 'packet'
  | 'itinerary' | 'call_sheet' | 'export' | 'other';
export const DOCUMENT_KINDS: readonly DocumentKind[] = [
  'agreement', 'letter', 'invoice', 'receipt', 'statement', 'packet',
  'itinerary', 'call_sheet', 'export', 'other',
];

// V19c: 'sms' joins the vocabulary (docs/console/v19-consent-otp-and-the-
// client-surface.md) — the OTP primitive's own transport, riding the SAME
// deliveries/delivery_attempts outbox rather than a new mechanism (see
// db/migrations/0123_otp_and_sms.sql's own header). Every OTHER channel
// still requires a document_id (migration 0123's
// deliveries_document_required_unless_sms CHECK); 'sms' is the one
// exception, since an OTP text carries a generated code, never a
// rendered document.
export type DeliveryChannel = 'email' | 'portal' | 'download' | 'telegram' | 'whatsapp' | 'paper' | 'sms';
export const DELIVERY_CHANNELS: readonly DeliveryChannel[] = [
  'email', 'portal', 'download', 'telegram', 'whatsapp', 'paper', 'sms',
];

// V12 (v12-delivery-outbox.md, INV-DEL-2): 'attempted' (the worker has
// claimed the row and is mid-transport) and 'unknown' (the reaper's own
// verdict when a crash landed between "the provider accepted" and "we
// wrote that down" — a real state, never resolved by a blind resend) join
// the original six. The web's delivery chip reads these words verbatim
// (voice canon: state names are act names).
export type DeliveryStatus =
  | 'queued' | 'sent' | 'delivered' | 'opened' | 'failed' | 'withdrawn' | 'attempted' | 'unknown';
export const DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'queued', 'sent', 'delivered', 'opened', 'failed', 'withdrawn', 'attempted', 'unknown',
];

/** V12: one row in `delivery_attempts` — append-only history of what the
 * worker tried, when, and what the provider (or the reaper) said. */
export type DeliveryAttemptStatus = 'attempting' | 'sent' | 'failed' | 'unknown';
export const DELIVERY_ATTEMPT_STATUSES: readonly DeliveryAttemptStatus[] = [
  'attempting', 'sent', 'failed', 'unknown',
];

// ---------------------------------------------------------------------
// Ported from the Rexx fork's documents system (decision 2da5b9e8,
// 2026-09-26: "Use Rex's so we can merge."). Verbatim from
// origin/main:packages/shared/src/index.ts in the rexx-tier1 fork,
// needed by packages/core/src/documents/*, notify/*, reports/statement.ts
// and notifications/vocabulary.ts.
// ---------------------------------------------------------------------
export type AuthorityDocumentAccessConfig = Record<string, string[]>;

export interface ReachCandidateDeclaration {
  /** Relationship keys, walked in order from the subject record. Absent or
   * empty = the subject record itself carries the address. */
  path?: string[];
  /** The field on the record the path ends at that holds the address. */
  emailField: string;
  /** The field holding the person's name. Absent = the record's own display
   * name, which is always something rather than nothing. */
  nameField?: string;
}

/**
 * ISSUE #70 — WHAT A CUSTOMER'S COPY OF A DOCUMENT IS ALLOWED TO CONTAIN.
 *
 * The quote that went to a customer on the walk of 2026-09-12 printed the
 * shop's credit hold on them and the reason for it, the roof-access route
 * and where the loading-dock key is kept, and the shop's own cost prices
 * beside its selling prices. None of it was a template bug: the templates
 * were handed the record WHOLE (`record.rows`, `related_list`) and printed
 * what they were handed, so every field anybody ever adds to a record type
 * is on the customer's paper the day it is added.
 *
 * So a customer-facing template is given an ALLOW-LIST instead of a record.
 * Nothing prints unless it is named here. Core still learns no trade
 * vocabulary — every name in this structure is the PACK'S, declared beside
 * its record types and handed to the renderer at boot on the same seam as
 * every other pack claim.
 *
 * A template with no entry here keeps the old behaviour (it sees everything),
 * which is what an internal document — one nobody outside the shop reads —
 * wants.
 */

export interface DocumentAudience {
  /** The record's OWN fields that may print, in the order they should print.
   * A field not named here is withheld — from `record.rows`, from
   * `record.money_rows`, from `record.values` and from `record.fields`, so a
   * template cannot reach it by any path. */
  recordFields: readonly string[];
  /** Relationship PATHS (`quote_work_order`, `quote_work_order.wo_client`)
   * that may print, and for each, the fields of that related record that may
   * print. A path not named here does not reach the template at all; a path
   * named with an empty list reaches it as a name with no rows under it. */
  related?: Readonly<Record<string, readonly string[]>>;
  /** Whether the record's lifecycle state may print. Default FALSE: a
   * customer's copy stating the shop's own internal workflow state is issue
   * #73, and the honest states ("With the office") are the ones that read
   * worst on a document the customer is holding. */
  showState?: boolean;
}

/** Per-template allow-lists, keyed by the TEMPLATE key the pack's own
 * `deliver_document` effects name (`quote`, `invoice`, `statement`,
 * `pay_stub`). */

export interface ReachPackConfig {
  /** reach role -> candidates in preference order. The role vocabulary is
   * open: a pack names the roles its own documents address. */
  routes: Record<string, ReachCandidateDeclaration[]>;
}

/* ------------------------------------------------------------------ *
 * STAGE 1 — THE CUSTOMER IS TOLD, WITHOUT ANYBODY RELAYING IT.
 *
 * The highest-frequency manual task in the whole record is a person on the
 * phone answering "when is somebody coming?". This declaration is what
 * lets the engine answer it instead.
 *
 * THE FOUR MOMENTS ARE CORE'S, THE WORDS ARE THE PACK'S. "On the way",
 * "arrived", "done" and "delayed" are not a trade's vocabulary — they are
 * the shape of any visit to anywhere. What IS a trade's vocabulary is which
 * record is a visit, which field is the time it was promised for, which
 * link means somebody is coming, who the customer is, and every sentence
 * the customer reads. All of that is declared here and none of it is in
 * core/src/notify/customerNotices.ts.
 *
 * Absent = this build sends the customer nothing, which is what every build
 * did before this stage.
 * ------------------------------------------------------------------ */

/** The four moments, closed. A fifth would be a new sentence in somebody's
 * life, not a config value — it goes through a spec. */

export type CustomerNoticeMoment = 'on_the_way' | 'arrived' | 'done' | 'delayed';

export const CUSTOMER_NOTICE_MOMENTS: readonly CustomerNoticeMoment[] = [
  'on_the_way',
  'arrived',
  'done',
  'delayed',
];

/** One moment's words. `subject` and `text` are substituted with the same
 * tiny mustache the document templates use — `{{company}}`, `{{job}}`,
 * `{{site}}`, `{{window}}` — and NOTHING else is available, so a template
 * cannot come to depend on a field nobody declared. `{{window}}` is a
 * WINDOW and never a minute: the office widens it when a job runs long, and
 * a system that promised 2:15 would make the office a liar at 2:20. */
export interface CustomerNoticeMessageDeclaration {
  /** What the office calls this moment on the notice log. */
  label: string;
  subject: string;
  text: string;
}

/* ------------------------------------------------------------------ *
 * STAGE 1 §6 — A PACK MAY FINALLY DECLARE A WATCHER.
 *
 * `VerticalPack.watcherChecks` has existed as `unknown[]` since the records
 * layer was written, and NOTHING has ever read it: the watcher registry
 * (core/src/attention/watchers.ts) held exactly one built-in demo check and
 * its own header said packs "declare theirs in the VerticalPack
 * .watcherChecks block per §11, wired when packs land". Packs landed; the
 * wiring did not. This is the wiring, and the shape a pack declares.
 *
 * THE CLIENT IS STRUCTURAL, exactly as `ReportClient` is and for the same
 * reason: a pack depends on @vowric/shared and never on @vowric/core, and a
 * `pg.PoolClient` satisfies this shape. The client handed to a check is
 * ALREADY inside the tenant's RLS-scoped transaction, so a check writes
 * plain SQL and gets isolation for free. A CHECK MUST NOT WRITE — it
 * reports, and the runner alone turns findings into attention items.
 * ------------------------------------------------------------------ */

export interface CustomerNoticesPackConfig {
  /** The dated record a visit to a customer IS. */
  visitRecordType: string;
  /** The relationship from that record to the job it belongs to — the job
   * is what a notice is keyed on, because two visits to one job are one
   * conversation with the customer. */
  visitToSubjectKey: string;
  subjectRecordType: string;
  /** The link whose presence means SOMEBODY IS COMING. Without it a
   * scheduled visit is a slot in a diary, and telling a customer about it
   * would be promising a person nobody has assigned. */
  assignmentLinkKey: string;
  fields: {
    /** The datetime the customer was promised. */
    scheduledStart: string;
    /** Set when the technician reached the site. */
    arrived: string;
    /** Set when the work was finished (a signature, a stamp). */
    done: string;
  };
  /** Subject lifecycle states that mean the job is over — the other half of
   * "done", for a job that closes without anybody signing. */
  subjectDoneStates: string[];
  /** The customer record, from the visit: the path to it, and the flag on
   * it that means "stop sending me these". */
  customer: {
    path: string[];
    recordType: string;
    optOutField: string;
  };
  /** The path from the visit to the PREMISES, for `{{site}}`. Absent = the
   * substitution resolves to the job's own name, never to an empty gap in a
   * sentence somebody reads. */
  sitePath?: string[];
  /** Reach roles (ReachPackConfig.routes' keys), tried in order. Stage 1's
   * own order: the person waiting at the site, then the account's principal
   * contact. */
  reachRoles: string[];
  /** The words, per moment. A moment with no entry is not sent at all —
   * which is how a pack ships three of the four deliberately. */
  messages: Partial<Record<CustomerNoticeMoment, CustomerNoticeMessageDeclaration>>;
  /** The line every message carries saying how to stop receiving them. The
   * canon requires it on every message and the pack owns its wording. */
  stopLine: string;
}

/* ------------------------------------------------------------------ *
 * W3-2 — THE REPORT LAYER (review G9/G3).
 *
 * THE DEFECT CLASS THIS CLOSES: every number the office reads was summed
 * BY THE BROWSER, over whatever rows the sweep happened to bring back.
 * RexxHome swept six lenses and added them up in a `reduce`; ClientDetail
 * added up a page of work orders; TechDetail added up a page of visits;
 * WorkOrders counted its own piles out of the list it had just paged. Each
 * of those pages stops at the row cap, so every one of those numbers is a
 * FUNCTION OF THE CAP rather than of the business — and a short total is
 * indistinguishable from a small one.
 *
 * THE MECHANISM: a report is a named, parameterised aggregate that runs ON
 * THE SERVER, over ALL the rows, inside the same RLS-scoped transaction
 * every route already uses. It never SELECTs rows into the browser to add
 * them up there, and it never truncates: `coverage` states how many rows it
 * scanned and carries `capped: false` because the type has no other value.
 *
 * WHY THE SHAPES LIVE IN @vowric/shared AND NOT IN @vowric/core: exactly
 * the reason RecordTypeDefinition/VerticalPack do (see this package's own
 * header) — a pack declares its own reports, and a pack may depend on
 * @vowric/shared and never on @vowric/core.
 * ------------------------------------------------------------------ */

/** The database handle a report is handed. Structural on purpose: this
 * package has no `pg` dependency and must not grow one, and a `pg.PoolClient`
 * satisfies this shape exactly. It is always a client ALREADY inside
 * `withEnforcement`'s transaction — i.e. `app.tenant_id` is set, row scope is
 * applied, and every query below is answered through RLS. A report therefore
 * writes plain SQL and gets tenant isolation for free; it must never open its
 * own pool. */
export interface ReportClient {
  query(text: string, values?: unknown[]): Promise<{ rows: Record<string, unknown>[] }>;
}

/** How much of the truth this answer covers. `capped` is typed as the
 * literal `false` and not as `boolean` BECAUSE A REPORT NEVER TRUNCATES —
 * it is the server, it scans what there is. The field exists so a consumer
 * that has learned to distrust a list (see `ListAllResult.capped` in the
 * web) can read the same word here and see that this one is whole. */
export interface ReportCoverage {
  /** How many underlying rows the aggregate actually scanned. */
  rows: number;
  capped: false;
}

/** A named rollup within a report — an aged band, a pile, a state group.
 * Separate from `rows` because several reports legitimately answer BOTH
 * questions at once: `receivables.aged` returns one row per open invoice
 * (the chase list, with a contact on it) AND one group per band (what the
 * money column draws). Folding them into one array would make the consumer
 * re-derive which is which. */
export interface ReportGroup {
  key: string;
  label: string;
  /** A CSS custom property, when the grouping declares a severity ramp.
   * Presentation, carried beside the grouping so a screen never picks its
   * own colour for someone else's band. */
  color?: string;
  count: number;
  total: number;
}

/** What every report answers. */
export interface ReportResult {
  /** The answer rows, already aggregated. Never raw records dumped for the
   * caller to sum. */
  rows: Record<string, unknown>[];
  /** The report's own totals, by name. */
  totals: Record<string, number>;
  /** Named rollups, when the report has them (aged bands, state piles). */
  groups?: ReportGroup[];
  /** WHICH OF THESE NUMBERS ARE ESTIMATES — the keys, in `totals` or in a
   * row, that are derived from a rate rather than counted from a record.
   * Declared by the report so a screen CANNOT render one without knowing it
   * is an estimate: the owner's "net" is invoiced less an estimated payout,
   * and printing it beside a counted number without the word is how a guess
   * becomes a fact. */
  estimates?: string[];
  /** THE PERIOD THIS ANSWER COVERS, when it covers one — 'YYYY-MM-DD' at
   * both ends, inclusive. A report over a window must say WHICH window, or
   * the screen labels it from its own assumption and the two drift the first
   * time a lever moves the boundary. */
  window?: { from: string; to: string };
  /** When the aggregate actually ran. Present on every answer, including a
   * cached one — that is the whole point of it. */
  computedAt: string;
  coverage: ReportCoverage;
}

/** What a report is handed BESIDES its client and its parameters — the
 * small services it would otherwise have to reach into @vowric/core for,
 * which a pack may not do. Today that is one: the levers, already resolved
 * against their code-level defaults, so an owner-facing number inside a
 * report is a lever like every other one (standing rule 4) without the pack
 * carrying a second copy of the default. Additive by construction — a report
 * that ignores the fourth argument is unaffected by anything added here. */
export interface ReportRunContext {
  lever(key: string): Promise<unknown>;
}

/** Whatever validates a report's parameters. Structurally exactly a Zod
 * schema's `.parse`, so `params: z.object({...})` is a legal value wherever
 * zod is already a dependency (the server) — and a hand-written parser is a
 * legal value where it is not (@vowric/core and every pack carry no zod;
 * see W3-2's report for why that is a deliberate substitution and not a
 * shortcut). Throwing is how it refuses. */
export interface ReportParamsSchema<P> {
  parse(raw: unknown): P;
}

/** One report. */
export interface ReportDefinition<P = unknown> {
  /** Dotted and stable — it is in a URL and in a screen's own code. */
  key: string;
  params: ReportParamsSchema<P>;
  /** The rung a caller must reach to run it AT ALL. A caller below it is
   * REFUSED, never answered with zeros: a dispatcher who asks for the
   * owner's numbers and receives 0 has been told the shop is empty. */
  minRole: Role;
  /** One sentence: what this report answers. Shown nowhere yet; read by
   * whoever opens the registry. */
  description: string;
  run(client: ReportClient, tenantId: string, params: P, context: ReportRunContext): Promise<ReportResult>;
}

/* ------------------------------------------------------------------ *
 * REPORT PARAMETERS, PARSED.
 *
 * The W3-2 brief specifies `params: zod schema`, and `ReportParamsSchema` is
 * structurally exactly a Zod schema's `.parse` — so wherever zod IS a
 * dependency (packages/server) a `z.object({…})` is a legal value with no
 * adapter. It is NOT a dependency of @vowric/core, of this package, or of any
 * vertical pack (a pack depends on @vowric/shared alone, deliberately), and
 * the executor brief forbids adding one. So the parsers a Core report and a
 * pack report reach for live here: small, total, and LOUD — every one throws
 * on a value it will not read rather than coercing it, because a report that
 * quietly reads `type=` as an empty string answers "nothing found" about a
 * question nobody asked.
 * ------------------------------------------------------------------ */

export class ReportParamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReportParamError';
  }
}

function paramBag(raw: unknown): Record<string, unknown> {
  if (raw === null || raw === undefined) return {};
  if (typeof raw !== 'object') throw new ReportParamError('parameters must be an object');
  return raw as Record<string, unknown>;
}

export function requiredParam(raw: unknown, name: string): string {
  const value = paramBag(raw)[name];
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new ReportParamError(`'${name}' is required`);
  }
  return value.trim();
}

export function optionalParam(raw: unknown, name: string): string | undefined {
  const value = paramBag(raw)[name];
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string') throw new ReportParamError(`'${name}' must be text`);
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

/** A whole number. Query strings arrive as text, so a numeric string is
 * accepted — anything else is refused rather than becoming NaN, which would
 * silently widen or empty a window. */
export function optionalIntegerParam(
  raw: unknown,
  name: string,
  min: number,
  max: number
): number | undefined {
  const value = paramBag(raw)[name];
  if (value === undefined || value === null || value === '') return undefined;
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n) || !Number.isInteger(n)) throw new ReportParamError(`'${name}' must be a whole number`);
  if (n < min || n > max) throw new ReportParamError(`'${name}' must be between ${min} and ${max}`);
  return n;
}

/** Wraps a plain parse function as a schema, over a CLOSED set of parameter
 * names.
 *
 * The closed set is the point, not decoration. A caller who mistypes
 * `client=` as `clientId=` on the aged-receivables report would otherwise be
 * handed the WHOLE workspace's outstanding balance and read it as one
 * account's — a wrong number with a decimal point on it, which is the exact
 * failure this layer exists to prevent. An unknown parameter is refused. */
export function paramsOf<P>(allowed: readonly string[], parse: (raw: unknown) => P): ReportParamsSchema<P> {
  return {
    parse(raw: unknown) {
      const stray = Object.keys(paramBag(raw)).filter((k) => !allowed.includes(k));
      if (stray.length > 0) {
        throw new ReportParamError(
          `this report does not take ${stray.join(', ')}${allowed.length > 0 ? ` (it takes ${allowed.join(', ')})` : ''}`
        );
      }
      return parse(raw);
    },
  };
}

/** For a report that takes nothing at all. */
export function noParams(): ReportParamsSchema<Record<string, never>> {
  return paramsOf([], () => ({}) as Record<string, never>);
}

/** JSONB gives back text; a currency field may hold a number, a numeric
 * string, or nothing at all. Anything unreadable is ZERO in a sum and is
 * still COUNTED — the row existed, its money did not parse, and dropping the
 * row would quietly shrink the denominator too. */
export function reportNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Every report ends the same way: what it scanned, and that it was whole. */
export function reportCoverage(rowsScanned: number): ReportCoverage {
  return { rows: rowsScanned, capped: false };
}

/* ------------------------------------------------------------------ *
 * FIX (review, stage 2's front-door suite) — REPORT WINDOWS, IN THE
 * TENANT'S ZONE, AS INSTANTS.
 *
 * THE DEFECT: `technician.week` built its window from the SERVER PROCESS'S
 * local date parts (`getFullYear`/`getMonth`/`getDate`) and then compared
 * against the UTC day PREFIX of a stored instant (`left(iso, 10)`). After
 * ~20:00 America/Toronto the UTC calendar day has already rolled to
 * tomorrow, so a visit that happened this evening carries a UTC date the
 * server's "today" does not recognise, and it silently falls out of "this
 * week" every single evening. A day-prefix comparison is ALWAYS wrong for a
 * timestamp: the tenant's calendar day and a stored instant's UTC calendar
 * day are two different days for roughly a third of every day, in either
 * direction, depending on the zone.
 *
 * THE FIX: every report window is computed in the TENANT's zone
 * (`tenant_profiles.timezone`, default `America/Toronto` — see migration
 * 0061), never the server process's zone (`process.env.TZ`, or whatever a
 * container happens to ship) and never UTC by accident. It is handed back
 * as INSTANTS — `startUtc` inclusive, `endUtc` exclusive — so a caller
 * compares `col >= $start AND col < $end` against a real timestamp column,
 * never a string prefix, which cannot silently agree with the wrong day.
 *
 * NO DEPENDENCY ADDED. Node's `Intl.DateTimeFormat` already carries the
 * full IANA database; `formatToParts` reads a UTC instant's wall-clock
 * components in an arbitrary zone, which is the one primitive every
 * function below is built from. The two-pass "guess as UTC, read the
 * zone's offset AT the guess, correct once, re-check in case the
 * correction itself crossed a DST boundary" algorithm is the same one
 * `packages/core/src/ops/timezone.ts` already uses for G1/G2 scheduling —
 * written again here, standalone, because a REPORT may be a PACK'S
 * (`packages/verticals/*`), and a pack depends on `@vowric/shared` and
 * NEVER on `@vowric/core` (this file's own header states that boundary for
 * `ReportClient`/`ReportDefinition`, and it is exactly why the window
 * layer lives here and not beside `core/src/ops/timezone.ts`).
 *
 * WHY IT LIVES HERE AND NOT IN `packages/core/src/reports/window.ts`
 * ALONE: a Core report (`core/src/reports/statement.ts`) and a PACK's
 * report (`verticals/rexx/src/reports/technician.ts`) both need the exact
 * same window, and only one of the two may import from `@vowric/core`.
 * `core/src/reports/window.ts` re-exports everything below so a Core
 * report can `import { reportWindow } from '../reports/window.js'` exactly
 * as the fix brief names it; a pack imports the same functions straight
 * from `@vowric/shared`, which it already depends on for `defineReport`
 * and friends. One implementation, reachable from both sides of the
 * boundary, is what stops the two from drifting the way `left(...,10)`
 * and a browser-side week ever did.
 * ------------------------------------------------------------------ */

/** A report's day-grained window, resolved to real instants. */
export interface ReportWindow {
  /** Inclusive instant, UTC ISO. */
  startUtc: string;
  /** EXCLUSIVE instant, UTC ISO — the instant the window ends AT, not the
   * last one it includes. A caller compares `col >= startUtc AND col <
   * endUtc`; never a string-prefix match, which is exactly what let a
   * tenant's evening fall out of "this week". */
  endUtc: string;
  /** The IANA zone actually used — `tenant_profiles.timezone`, or the
   * shipped default when the row is missing or the value will not parse. */
  zone: string;
  /** The local calendar days the window covers, 'YYYY-MM-DD' at both
   * ends, inclusive — what `ReportResult.window` carries and a screen
   * renders, so it labels the window it was actually given rather than
   * the one it assumed. */
  label: { from: string; to: string };
}

/** The company always has a timezone (same rule `tenant/profile.ts`
 * states); this is the one other module reads when it cannot afford that
 * module's dependency (a pack report cannot import `@vowric/core`). */
export const REPORT_DEFAULT_TIMEZONE = 'America/Toronto';

function reportZoneParts(instant: Date, timeZone: string): {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
} {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) if (part.type !== 'literal') map[part.type] = part.value;
  return {
    y: Number(map['year']),
    mo: Number(map['month']),
    d: Number(map['day']),
    h: Number(map['hour']),
    mi: Number(map['minute']),
    s: Number(map['second']),
  };
}

/** `instant`'s wall-clock calendar day in `timeZone`, 'YYYY-MM-DD'. Throws
 * `ReportParamError` when `timeZone` is not a name `Intl` recognises —
 * loud, because a silently-ignored bad zone is exactly the kind of
 * silence this fix exists to end.
 *
 * WALK 2, #147 — AND IT IS THE ONLY WAY THIS PRODUCT TURNS AN INSTANT INTO A
 * DAY. It was built for report windows; the same question is asked every time
 * something stamps a BUSINESS DAY (`hvac_invoice.issued_date`, a statement's
 * "As at", a sweep comparing today with a due date), and those were answering
 * it with `new Date().toISOString().slice(0, 10)` — the UTC day.
 *
 * Toronto is UTC-4 in September and UTC-5 in the winter, so every invoice
 * drafted from 20:00 onward — 19:00 once the clocks go back — was dated
 * TOMORROW. A shop that bills at the end of the day, which is when a service
 * shop bills, hit it every single time. It was never only a wrong date on a
 * document: the due date derives from it (so the terms clock and the overdue
 * chase both started a day late), Money's age bands are computed from it (so
 * an invoice sat a day younger than it was, and near a band edge in the wrong
 * band), the money-flag engine correctly called every evening invoice "dated
 * ahead" — a standing false positive printed beside every line of a statement
 * sent to a customer — and on September 30 at 20:30 an invoice for September
 * work was dated October 1, in the wrong accounting period.
 *
 * `dayLint.test.ts` is what keeps this the only one. */
export function reportZonedYmd(instant: Date, timeZone: string): string {
  let p: ReturnType<typeof reportZoneParts>;
  try {
    p = reportZoneParts(instant, timeZone);
  } catch {
    throw new ReportParamError(`'${timeZone}' is not a recognized IANA timezone name`);
  }
  return `${String(p.y).padStart(4, '0')}-${String(p.mo).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

function offsetMinutesAt(instant: Date, timeZone: string): number {
  const p = reportZoneParts(instant, timeZone);
  const asIfUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  return (asIfUtc - instant.getTime()) / 60_000;
}

function parseYmd(ymd: string): [number, number, number] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new ReportParamError(`'${ymd}' is not a YYYY-MM-DD day`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** Local midnight of `ymd` in `timeZone`, as the real UTC instant — the
 * standard two-pass `Intl` algorithm: guess (treat the components as if
 * UTC), read the zone's offset AT that guess, correct once, then re-check
 * the offset at the corrected instant in case the correction itself
 * crossed a DST boundary. This is what keeps the boundary exact on the
 * March and November Sunday a naive fixed-offset shift would miss by an
 * hour. */
export function reportZonedStartOfDayUtc(timeZone: string, ymd: string): string {
  const [y, mo, d] = parseYmd(ymd);
  let guess: Date;
  try {
    guess = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0));
    // Touch the zone once here so an invalid name throws the same error
    // whether the caller passed a bad ymd or a bad zone.
    reportZoneParts(guess, timeZone);
  } catch {
    throw new ReportParamError(`'${timeZone}' is not a recognized IANA timezone name`);
  }
  const offset1 = offsetMinutesAt(guess, timeZone);
  const candidate1 = new Date(guess.getTime() - offset1 * 60_000);
  const offset2 = offsetMinutesAt(candidate1, timeZone);
  const result = offset2 === offset1 ? candidate1 : new Date(guess.getTime() - offset2 * 60_000);
  return result.toISOString();
}

/** `ymd` plus `days` (may be negative), as a calendar day — pure date
 * arithmetic on an already-extracted local day, which carries no zone
 * ambiguity: once a wall-clock day is known, "three days later" means the
 * same thing everywhere. */
export function addDaysToYmd(ymd: string, days: number): string {
  const [y, mo, d] = parseYmd(ymd);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** The Monday on or before `ymd` — the same Monday-start week every
 * screen in this product has always drawn, computed off the tenant's own
 * calendar day rather than the server's. */
export function mondayOnOrBeforeYmd(ymd: string): string {
  const [y, mo, d] = parseYmd(ymd);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  const back = (dt.getUTCDay() + 6) % 7; // days since Monday, Sunday=6
  dt.setUTCDate(dt.getUTCDate() - back);
  return dt.toISOString().slice(0, 10);
}

/** The two instants a LOCAL calendar-day range `[fromYmd, toYmd]`
 * (inclusive at both ends) resolves to in `timeZone` — `endUtc` is the
 * instant the day AFTER `toYmd` begins, so a caller's comparison is always
 * `>= startUtc AND < endUtc` and never has to reason about "inclusive of
 * the last day" itself. */
export function reportDayRangeUtc(timeZone: string, fromYmd: string, toYmd: string): { startUtc: string; endUtc: string } {
  return {
    startUtc: reportZonedStartOfDayUtc(timeZone, fromYmd),
    endUtc: reportZonedStartOfDayUtc(timeZone, addDaysToYmd(toYmd, 1)),
  };
}

/** The tenant's own zone, read directly (no `@vowric/core` import — see
 * this section's header). Never null: a company always has a timezone,
 * and "we do not know how to render your dates" is a defect, not a fact —
 * the exact rule `tenant/profile.ts` states for the same column, applied
 * here for a caller (a pack report) that cannot reach that module. */
export async function reportTenantZone(client: ReportClient, tenantId: string): Promise<string> {
  const { rows } = await client.query('SELECT timezone FROM tenant_profiles WHERE tenant_id = $1', [tenantId]);
  const z = rows[0]?.['timezone'];
  return typeof z === 'string' && z.trim().length > 0 ? z : REPORT_DEFAULT_TIMEZONE;
}

/** What kind of window: the current Monday-start week, or the trailing
 * `days` days (inclusive of today) — the two shapes `technician.week` and
 * `owner.numbers` already needed. */
export type ReportWindowKind = { kind: 'week' } | { kind: 'days'; days: number };

/**
 * THE WINDOW, resolved for one tenant. Reads the tenant's own zone (one
 * indexed lookup), finds "today" inside it — never the server process's
 * "today" — and returns the window as instants plus the local-day labels a
 * screen renders.
 *
 * `now` is a fixed clock for tests; omitted, it is the real instant. Two
 * calls one line apart with no `now` given can legitimately answer
 * different windows across a real local-midnight rollover; that is
 * correct, not a race — the same as any other "as of now" report.
 */
export async function reportWindow(
  client: ReportClient,
  tenantId: string,
  opts: ReportWindowKind & { now?: Date }
): Promise<ReportWindow> {
  const now = opts.now ?? new Date();
  const zone = await reportTenantZone(client, tenantId);
  const today = reportZonedYmd(now, zone);
  const from =
    opts.kind === 'week'
      ? mondayOnOrBeforeYmd(today)
      : addDaysToYmd(today, -(Number.isFinite(opts.days) && opts.days >= 1 ? Math.floor(opts.days) - 1 : 0));
  const { startUtc, endUtc } = reportDayRangeUtc(zone, from, today);
  return { startUtc, endUtc, zone, label: { from, to: today } };
}

/** W3-2 — WHAT A STATEMENT IS MADE OF, in the pack's own vocabulary.
 *
 * A statement is "everything this customer still owes, oldest first" — a
 * shape every trade has and none of them spells the same way. Core knows how
 * to ASSEMBLE and RENDER one; it does not know what an `hvac_invoice` is, and
 * `packages/core` must stay free of a vertical's words (the same rule that
 * put `MoneyFlagsPackConfig` here rather than a hardcoded `hvac_work_order`
 * inside the approval sweep). So the pack names the four record types and
 * the walk between them, and Core does the arithmetic.
 *
 * A pack that declares nothing produces no statements, and the door that
 * makes one simply does not exist on that build — the same declare-to-exist
 * convention as `comms`, `fieldWork` and `moneyFlags`. */
export interface StatementPackConfig {
  /** Who the statement is ABOUT (Rexx: `hvac_client`). */
  subjectRecordType: string;
  /** Optional field names on the subject, each rendered when present. */
  subjectBillingNameField?: string;
  subjectBillingAddressField?: string;
  subjectContactNameField?: string;
  subjectContactPhoneField?: string;
  subjectContactEmailField?: string;
  /** The money row (Rexx: `hvac_invoice`). */
  ledgerRecordType: string;
  /** The human-facing number on a ledger row. Absent — or absent ON THE ROW,
   * which is the state of every ledger row until stage 3 gives invoices a
   * number — falls back to the record's own display name, never to a blank:
   * a statement line nobody can cite is not a statement line. */
  ledgerNumberField?: string;
  /** The day the row was raised; a `date` field, day-grained. A row with no
   * date is still on the statement and its age reads as unknown — never as
   * new (packages/web/src/lib/aging.ts's founding rule). */
  ledgerDateField: string;
  ledgerTotalField: string;
  ledgerPaidField: string;
  /** The walk from a ledger row to its subject: ledger -> job -> subject.
   * Two hops because that is the shape the money actually has in a field
   * trade — an invoice belongs to a job and the job belongs to the customer
   * — and collapsing it would need a link the packs do not carry. */
  ledgerToJobKey: string;
  jobRecordType: string;
  jobToSubjectKey: string;
  /** The job's own number, cited beside each line when the pack has one. */
  jobNumberField?: string;
}

/** A report of UNKNOWN parameter shape — what a registry and a pack's
 * `reports` array hold. `run`/`parse` are declared with method syntax
 * precisely so a `ReportDefinition<{ type: string }>` is assignable here:
 * the alternative is every collection of reports being `any`. */
export type AnyReportDefinition = ReportDefinition<unknown>;

/** Identity with inference — the same role `defineConfig` plays: it exists
 * so a report's `run` gets its `params` typed from its own schema without
 * the author writing the type twice. Lives here rather than in @vowric/core
 * so a PACK can call it. */
export function defineReport<P>(def: ReportDefinition<P>): ReportDefinition<P> {
  return def;
}

// Ported 2026-09-26 (atlas-2 review of decision 2da5b9e8) — W2-2, migration
// 0063_attention_audience.sql: WHO an attention item is for, out of the
// four the product actually has — owner/person items email+push at their
// own threshold, office/dispatch items push at theirs and arrive by email
// in the daily digest. Severity stays "how loud"; audience is "aimed at
// whom". A failed customer delivery is an 'office' item — proving that is
// the whole point of this type existing.
export type AttentionAudience = 'owner' | 'office' | 'dispatch' | 'person';

export interface StateGroupDef {
  key: string;
  label: string;
  /** State keys, which must all exist in the type's own lifecycle
   * (checked by validateVocabularyCoverage). */
  states: string[];
  /** Optional CSS custom property for a severity ramp, e.g.
   * 'var(--status-warning)'. Presentation, declared beside the grouping
   * so a screen never picks its own colour for someone else's pile. */
  color?: string;
}

export interface CompanyProfilePackConfig {
  /** Label per profile field, keyed by the COLUMN name
   * (`legal_name`, `display_name`, `tax_number`, `default_terms_days`,
   * `invoice_prefix`, `timezone`, `trouble_line`, `address_lines`, `city`,
   * `region`, `postal_code`, `country`, `logo_file_id`). A key the screen
   * does not render is simply unused; a field with no key keeps the neutral
   * default. */
  fieldLabels?: Record<string, string>;
  /** The heading over the section on the settings screen ("Your company").
   * Absent = the neutral default. */
  sectionLabel?: string;
}

export interface RecordTypeLabels {
  key: string;
  label: string;
  labelPlural: string;
  chip?: string;
  /** state key -> label. Empty for a type with no lifecycle. */
  states: Record<string, string>;
  /** field name -> (stored value -> label), for picklist fields. */
  values: Record<string, Record<string, string>>;
  /** field name -> label. */
  fields: Record<string, string>;
  hiddenOnCreate: string[];
  /** grouping key -> its groups, for this type. */
  groupings: Record<string, StateGroupDef[]>;
  /** The `partition: true` grouping's key, when this type declares one —
   * what `groupOf(typeKey, state)` answers from. */
  partitionGrouping?: string;
}

/** The whole vocabulary, as one serialisable payload. This is what
 * GET /auth/entry hands the browser and what the server's own message
 * paths resolve against — ONE dictionary, never two that can drift. */
export interface PackLabels {
  types: Record<string, RecordTypeLabels>;
  actions: Record<string, string>;
  routeTo: Record<string, string>;
  roles: Record<string, string>;
  /** WALK 2, #122 — THE SEAT'S OWN NOUN. A membership carries the ENGINE's
   *  rank ('admin', 'manager', 'member'), and three screens printed it raw
   *  beside a person's name — words this product does not say to anybody and
   *  which nothing else on the screen uses. It is not `roles`: that map is
   *  keyed by the pack's JOB role ('dispatcher'), and the rank is what the
   *  manifest's `seating` derives that job role TO. So this is `seating`
   *  inverted and resolved through `roleLabels` — one declaration, read two
   *  ways, rather than a second table that can disagree with the first.
   *  A rank no job role derives to is simply absent; a screen says nothing
   *  rather than inventing a seat nobody is given. Optional: a pack with no
   *  authority manifest declares no seating, and a hand-built dictionary in
   *  a test is not obliged to invent one. */
  seats?: Record<string, string>;
  acronyms: string[];
}

/** Builds the served dictionary from a pack's own declarations. Pure —
 * no database, no humanising, no defaults invented: what a pack did not
 * declare is simply absent, and `validateVocabularyCoverage` is what makes
 * "absent" impossible for a pack that opted in. */

/* ------------------------------------------------------------------ *
 * STAGE 4 — EVERY DOLLAR TIED TO A JOB.
 *
 * The owner: "Every single purchase made on the company credit card needs to
 * be immediately assigned to a work order… I have no idea what's being
 * bought. I just pray they're not stealing, because I'm not auditing these."
 *
 * WHAT CORE OWNS AND WHAT THE PACK OWNS, drawn the way every seam on this
 * type is drawn. Core owns the tables (`card_statements`,
 * `card_transactions`, `bank_transactions`), the idempotent import, the
 * match/ignore/chase doors and the payment-confirmation walk — none of which
 * knows what a receipt is called. The PACK owns every noun: which record type
 * backs a charge, which fields carry its money and its day, which record type
 * a payment is, where "this was confirmed at the bank" is written down, and
 * which field on a person carries the last four digits of their card.
 *
 * TWO FUNCTIONS RIDE THE CONFIG, for the reason `InvoicingPackConfig.compile`
 * already does: reading a bank's CSV and deciding whether a charge and a
 * receipt are the same purchase are judgements about a trade's own paperwork,
 * not things Core can be taught in a field map.
 * ------------------------------------------------------------------ */

/** One row as a statement file states it, before anything has been decided
 * about it. `postedDate` is a calendar day (`YYYY-MM-DD`) because that is all
 * a statement prints — never an instant, never "today". */
export interface StatementRowInput {
  /** The last four digits of the card or account this row is on. NULL when
   * the file states it once at the top instead of per row. */
  cardLast4: string | null;
  postedDate: string;
  description: string;
  /** Positive is a purchase on a card, and money IN on a bank account. A
   * credit or a reversal is negative. */
  amount: number;
  externalRef?: string | null;
}

export interface StatementParseInput {
  kind: 'card' | 'bank';
  filename: string;
  /** The file's own text. The web reads the file; nothing is uploaded to a
   * third party and no bank credential exists on this deployment. */
  text: string;
}

/**
 * WHAT A PARSER MAY ANSWER, and the refusal is half of it.
 *
 * A column the reader does not recognise ABORTS the whole import with an
 * expected-versus-found diff and imports nothing — the V6 importer's own
 * discipline, which is the right one for money. A silently misread column
 * turns "$4,120.55 of parts" into a number that is wrong in a direction
 * nobody audits. There is no partial import.
 *
 * A DISCRIMINATED UNION RATHER THAN A THROWN ERROR because this type lives in
 * @vowric/shared, which carries no runtime logic — and because the diff is
 * something a SCREEN has to render, not only something a log has to hold.
 */
export type StatementParse =
  | {
      ok: true;
      /** The reader that recognised it ('td_csv', 'headed_csv'), kept on the
       * batch so "which reader read this" is answerable later. */
      format: string;
      /** Stated once for the whole file, when the file states it. */
      cardLast4: string | null;
      rows: StatementRowInput[];
    }
  | {
      ok: false;
      /** The reader that got furthest, or null when none recognised the file
       * at all. */
      format: string | null;
      expected: string[];
      found: string[];
      /** The sentence a person reads. Names the file and the columns. */
      message: string;
    };

/** A record a charge might be backed by, flattened for a matcher that must
 * stay pure. `amount` and `date` are already coerced; an unreadable value is
 * null and null never matches. */
export interface StatementMatchCandidate {
  id: string;
  displayName: string;
  amount: number | null;
  /** `YYYY-MM-DD`, or null when the record states no day. */
  date: string | null;
  /**
   * ISSUE #91 — WHAT TELLS THIS CANDIDATE FROM THE OTHER ONE.
   *
   * The matcher refuses to guess between two receipts at the same amount on
   * the same day, which is right. Then the screen offered two buttons
   * reading "Put supplies — Acklands-Grainger against it" and "Put supplies
   * — Acklands-Grainger against it", because a receipt's display name is its
   * type and its supplier, which for two purchases from one supplier on one
   * day is the same string by construction. Refusing to guess and then
   * making the person guess arrives in the same place — with a wrong match
   * attributed to a job with total confidence.
   *
   * Everything needed already exists on the records: the job, the supplier's
   * own document number, the day, and who filed it. This is that sentence,
   * composed where the records are read. Empty when the pack declared
   * nothing to distinguish them by.
   */
  detail: string;
}

/**
 * AMBIGUITY DOES NOT GUESS (spec §5). Two candidates at the same amount on
 * the same day leave the charge unmatched with both offered to a person. This
 * is the never-assume rule applied where it matters most.
 */
export type StatementMatchVerdict =
  | { kind: 'matched'; candidateId: string }
  | { kind: 'ambiguous'; candidateIds: string[] }
  | { kind: 'none' };

export interface StatementMatcherInput {
  postedDate: string;
  amount: number;
  /** `hvac.cards.match_window_days` — a receipt is photographed on the day
   * and the charge posts later. */
  windowDays: number;
  candidates: StatementMatchCandidate[];
}

export interface CardsReceiptConfig {
  recordType: string;
  amountField: string;
  dateField: string;
  supplierField: string;
  numberField: string;
  imagesField: string;
  /** Lifecycle states in which a receipt may back a charge. A rejected
   * receipt is not evidence of anything. Omitted = every state. */
  matchableStates?: string[];
  /** ISSUE #91: the hop to the JOB a receipt was filed against, so two
   * candidates at one amount on one day can be told apart by the thing that
   * actually differs. Absent = the candidate line carries the fields alone. */
  jobLink?: string;
  /** ISSUE #91: the hop to the person who filed it — and the one that lets
   * the queue put the card-holder's OWN receipts first, which is the
   * narrowing the screen already had the information to do. */
  personLink?: string;
}

export interface CardsPaymentConfig {
  recordType: string;
  amountField: string;
  /** The day the money arrived, as the office recorded it. */
  dateField: string;
  /** Stamped when a deposit for this amount actually landed. Until then a
   * payment is RECORDED, not proven, and the money screens say which. */
  confirmedAtField: string;
  /** Stamped when the deposit came back out again — a returned cheque. */
  nsfAtField: string;
}

export interface CardsPersonConfig {
  recordType: string;
  /** The last four digits of the card this person carries. The office's data
   * to supply; empty until they do, and a charge on an unclaimed card is
   * chased at the office rather than dropped. */
  cardField: string;
}

export interface CardsPackConfig {
  receipt: CardsReceiptConfig;
  payment: CardsPaymentConfig;
  person: CardsPersonConfig;
  /** Reads one statement file, or refuses it with a diff. See StatementParse. */
  parse: (input: StatementParseInput) => StatementParse;
  /** Decides whether a charge is backed by one of these receipts. Pure. */
  match: (input: StatementMatcherInput) => StatementMatchVerdict;
}


/**
 * The cockpit switcher's surfaces (S1c, decision eb659c8d) — the ONE list both the web switcher
 * (`web/src/lib/cockpitSurfaces.ts`, asserted equal by its test) and Core's assistant `navigate` verb
 * validate against. "Atlas can switch screens on request" may name ONLY these keys; Core refuses any
 * other, and the web client refuses any other again (two locks). Keys are stable and never renamed.
 */
export const COCKPIT_SURFACES = [
  // H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1) — the board is the cockpit's
  // front page now; Home is the switcher's first door.
  { key: 'home', label: 'Home' },
  { key: 'today', label: 'Today' },
  { key: 'mail', label: 'Mail' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'personal', label: 'Personal' },
  { key: 'review', label: 'Review' },
  { key: 'audience', label: 'Audience' },
  { key: 'clients', label: 'Clients' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'systems', label: 'Systems' },
  { key: 'radar', label: 'Radar' },
  { key: 'record', label: 'Record' },
  { key: 'settings', label: 'Settings' },
] as const;
export type CockpitSurfaceKey = (typeof COCKPIT_SURFACES)[number]['key'];
export function isCockpitSurfaceKey(v: unknown): v is CockpitSurfaceKey {
  return typeof v === 'string' && COCKPIT_SURFACES.some((s) => s.key === v);
}
